/**
 * Adamiro Editor — TipTap entry. Spec: docs/Reach/PHASE_1C.md.
 *
 * Exposes window.AdamiroEditor.{ mount, destroy } so PHP can render a
 * <div id="..."> and call mount(el, opts) from a tiny inline bootstrap.
 *
 * Phase 1c.1 scope: editor with bold/italic/strike/inline-code/link/lists/
 * blockquote/HR/headings/undo/redo, character count, placeholder text.
 * Per-platform serializers + custom blocks (subscribe/paywall) + image
 * upload land in 1c.2 / 1c.3 / 1c.5. Versioning lands in 1c.4.
 */

import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import Image from '@tiptap/extension-image';
import { ReachSubscribe } from './extensions/reach-subscribe.js';
import { ReachPaywall } from './extensions/reach-paywall.js';
import { serializeFor, SERIALIZERS } from './serializers/index.js';
import { openProductPlacement } from './product-placement.js';

/**
 * REST headers for every call the editor makes to /adamiro/v1/. The nonce
 * comes from AdamiroEditorConfig; the account header rides along only when
 * window.ADAMIRO_ACCT is known (never an empty one), because a person with
 * several memberships is refused (400) on a request that names no account.
 * One helper, so a new fetch cannot forget the second header.
 */
function admRestHeaders(extra) {
    var h = { 'X-WP-Nonce': (window.AdamiroEditorConfig || {}).nonce };
    if (window.ADAMIRO_ACCT) {
        h['X-Adamiro-Account'] = window.ADAMIRO_ACCT;
    }
    return Object.assign(h, extra || {});
}

/** Same two headers, set on an XMLHttpRequest (upload progress needs XHR). */
function admSetRestHeaders(xhr) {
    xhr.setRequestHeader('X-WP-Nonce', (window.AdamiroEditorConfig || {}).nonce);
    if (window.ADAMIRO_ACCT) {
        xhr.setRequestHeader('X-Adamiro-Account', window.ADAMIRO_ACCT);
    }
}

/**
 * Phase 1c.7 mobile + RTL hardening — preserve dir="rtl" across schema
 * parse/serialize on block-level nodes. Without this, TipTap silently
 * drops the attribute on every paragraph/heading/blockquote/list-item,
 * collapsing pasted Arabic / Hebrew / Persian / Urdu content to LTR.
 * The sanitisePastedHtml() pre-pass adds dir="rtl" wherever it detects
 * mostly-RTL text; this extension ensures the attribute survives.
 */
const DirAttribute = Extension.create({
    name: 'dirAttribute',
    addGlobalAttributes() {
        return [{
            types: ['paragraph', 'heading', 'blockquote', 'listItem'],
            attributes: {
                dir: {
                    default: null,
                    parseHTML: (element) => element.getAttribute('dir') || null,
                    renderHTML: (attributes) => {
                        if (!attributes.dir) return {};
                        return { dir: attributes.dir };
                    },
                },
            },
        }];
    },
});

/**
 * Detect whether a string already contains HTML tags. Heuristic: any
 * `<` followed by an ASCII letter or `/`. Keeps `1 < 2` and `<3` from
 * tripping the detection.
 */
function looksLikeHtml(str) {
    return /<\s*[a-zA-Z\/]/.test(String(str || ''));
}

/**
 * Convert plain text (with \n / \r\n / \r line breaks) to HTML the
 * editor can render: blank-line-separated chunks become <p>, single
 * newlines inside a chunk become <br>. Already-HTML content passes
 * through unchanged. Inbox-seeded drafts arrive as plain text from
 * Voice or Signals — without this, single newlines collapse to
 * whitespace because HTML treats them that way.
 */
function plainTextToHtml(input) {
    const s = String(input || '');
    if (s === '') return '';
    if (looksLikeHtml(s)) return s;

    const esc = (t) => t
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const normalised = s.replace(/\r\n?/g, '\n');
    const blocks = normalised.split(/\n{2,}/); // blank-line separated
    return blocks
        .map((block) => '<p>' + esc(block).replace(/\n/g, '<br>') + '</p>')
        .join('');
}

const ICON = {
    // Lucide stroke icons inlined. currentColor so the toolbar's CSS
    // controls active/hover states.
    bold:        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>',
    italic:      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>',
    strike:      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 4H9a3 3 0 0 0-2.83 4"/><path d="M14 12a4 4 0 0 1 0 8H6"/><line x1="4" y1="12" x2="20" y2="12"/></svg>',
    code:        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
    link:        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
    bulletList:  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3.5" cy="6" r="1"/><circle cx="3.5" cy="12" r="1"/><circle cx="3.5" cy="18" r="1"/></svg>',
    orderedList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>',
    quote:       '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>',
    hr:          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    h2:          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="M17 10c.7-.7 1.69-1 2.5-1 1.39 0 2.5 1 2.5 2.5 0 2-3 4-5 4.5h5"/></svg>',
    h3:          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2"/><path d="M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2"/></svg>',
    undo:        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>',
    redo:        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"/></svg>',
    image:       '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
};

/**
 * Sanitise HTML coming in from the clipboard. Word, Google Docs, Notion,
 * iOS Safari (Notes/Messages), Android Chrome (Keep), and the browser's
 * native paste all sprinkle inline styles, class names, charset blocks,
 * and platform-specific wrappers. This pre-pass keeps the editor's
 * canonical body clean without depending on the schema's own filtering.
 *
 * Removed:
 *   - inline style="…" attributes
 *   - class="…" attributes
 *   - id="…" attributes (Word repeats Mso-prefixed ids prolifically)
 *   - MS Office namespace tags (<o:p>, <v:shapetype>, etc.) along with body
 *   - <meta>, <style>, <link>, <title> — iOS Safari + Apple Mail ship these
 *   - <font>, <span> wrappers (kept if they carry no meaningful attribute)
 *   - comments (often contain MS-specific markers)
 *   - zero-width characters (​/C/D, ﻿) — invisible noise from mobile keyboards
 *   - explicit dir="ltr" attributes that Google Docs injects on every paragraph
 *   - <br type="_moz"> and consecutive <br> runs of 3+
 *
 * Preserved:
 *   - dir="rtl" — explicit RTL marking from source documents
 *   - Auto-detected RTL paragraphs (Arabic / Hebrew / Persian / Urdu) get
 *     dir="rtl" stamped on their wrapping block element so the editor's
 *     rendering matches the source language direction.
 */

// RTL character range covers the four main RTL scripts plus Arabic
// presentation/extended forms — matches the Unicode Bidi class R + AL.
// Used both to detect mostly-RTL text and to skip BiDi noise.
const RTL_CHAR_RX = /[֐-׿؀-ۿ܀-ݏݐ-ݿހ-޿ࢠ-ࣿיִ-﷿ﹰ-﻿]/;
// Invisible characters that mobile soft keyboards and autocomplete sprinkle in.
//
// Kept in sync with ADAMIRO_Text_Sanitizer::STRIP_RX (PHP), which applies the
// same policy to model output. Change one, change the other.
//
// This list used to also swallow U+200C, U+200D and the directional marks, and
// that was a bug in the exact area the paste cleanup claims to protect: U+200D
// is what glues an emoji family together (👨‍👩‍👧 is three emoji and two joiners),
// U+200C is orthographic in Persian and Urdu, and U+200E/U+200F/U+2066-U+2069
// carry directionality in mixed text. Paste any of those into the editor and
// they were silently corrupted. They are deliberately absent below.
//
// U+202A-U+202E stay stripped: they are the deprecated bidi embeddings and
// overrides that the isolates replaced, and the overrides can render text out
// of its logical order.
const ZERO_WIDTH_RX = /[­᠎​‪-‮⁠-⁤﻿]|[\u{E0000}-\u{E007F}]/gu;

function isMostlyRtl(text) {
    if (!text) return false;
    const stripped = String(text).replace(/[\s\d\p{P}]/gu, '');
    if (!stripped) return false;
    let rtlChars = 0;
    for (const ch of stripped) {
        if (RTL_CHAR_RX.test(ch)) rtlChars++;
    }
    // 40% threshold catches mixed-script paragraphs (e.g. Arabic body with
    // an English term inline) without flipping pure-LTR paragraphs that
    // happen to include a single RTL character.
    return rtlChars / stripped.length >= 0.4;
}

function sanitisePastedHtml(html) {
    let s = String(html || '');
    if (s.trim() === '') return '';

    // Strip MS Office namespaced tags + their content.
    s = s.replace(/<o:[^>]+>[\s\S]*?<\/o:[^>]+>/gi, '');
    s = s.replace(/<v:[^>]+>[\s\S]*?<\/v:[^>]+>/gi, '');
    s = s.replace(/<\?xml[^?]*\?>/gi, '');
    // Drop HTML comments (Word + Outlook bury markers in here).
    s = s.replace(/<!--[\s\S]*?-->/g, '');
    // iOS Safari + Apple Mail ship full <head> fragments on paste from
    // Notes / Messages. Drop the noise tags before they confuse the schema.
    s = s.replace(/<meta[^>]*>/gi, '');
    s = s.replace(/<link[^>]*>/gi, '');
    s = s.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
    s = s.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    s = s.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    // Drop style= class= id= attributes on every tag. Also strip the
    // explicit dir="ltr" that Google Docs stamps on every paragraph (we
    // re-add dir="rtl" below only where the content warrants it). Repeated
    // to catch multi-attribute residue after each pass.
    for (let pass = 0; pass < 2; pass++) {
        s = s.replace(/\s+style="[^"]*"/gi, '');
        s = s.replace(/\s+style='[^']*'/gi, '');
        s = s.replace(/\s+class="[^"]*"/gi, '');
        s = s.replace(/\s+class='[^']*'/gi, '');
        s = s.replace(/\s+id="[^"]*"/gi, '');
        s = s.replace(/\s+id='[^']*'/gi, '');
        s = s.replace(/\s+dir=["']ltr["']/gi, '');
    }
    // Strip empty <span> / <font> wrappers (after attribute removal they're noise).
    s = s.replace(/<font[^>]*>([\s\S]*?)<\/font>/gi, '$1');
    s = s.replace(/<span\s*>([\s\S]*?)<\/span>/gi, '$1');

    // Mobile hardening: zero-width chars + BiDi marks pollute paste from
    // iOS soft keyboard, Android Gboard, and platform autocomplete. Strip
    // unconditionally — they have no semantic value here. Keep the actual
    // RTL characters (Arabic letters etc.) intact, only drop the invisible
    // control codes.
    s = s.replace(ZERO_WIDTH_RX, '');
    // Mozilla legacy artefact + consecutive <br> runs ≥ 3 (mobile paste
    // sometimes emits 4-5 in a row from soft-wrapped messages).
    s = s.replace(/<br\s+type=["']_moz["']\s*\/?>/gi, '<br>');
    // JS \s already matches U+00A0, so the old (?:\s|<nbsp>)* split every nbsp two ways
    // and a run of N of them cost 2^N steps (finding C94, ReDoS from clipboard HTML).
    // One class, no alternation: linear.
    s = s.replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br><br>');
    // Apple-converted-space artefact: non-breaking space runs become
    // regular spaces (the class= strip already removed the marker).
    s = s.replace(/ {2,}/g, ' ');

    // RTL auto-detect on paragraph-like wrappers. Walks every block-level
    // element and adds dir="rtl" when the inner text is mostly RTL AND
    // the element doesn't already carry an explicit dir attribute. Uses
    // DOMParser when available (modern browsers); falls back to a regex
    // pass in JSDOM-less environments (test runners). The DOM path is
    // both safer and more accurate; the regex fallback covers the 80%
    // case for simple <p>/<div>/<h*> wrappers.
    if (typeof window !== 'undefined' && typeof window.DOMParser === 'function') {
        try {
            const doc = new window.DOMParser().parseFromString('<div id="__paste-root">' + s + '</div>', 'text/html');
            const root = doc.getElementById('__paste-root');
            if (root) {
                const blocks = root.querySelectorAll('p,h1,h2,h3,h4,h5,h6,blockquote,li,div');
                blocks.forEach((el) => {
                    if (el.hasAttribute('dir')) return; // respect explicit source dir
                    if (isMostlyRtl(el.textContent || '')) {
                        el.setAttribute('dir', 'rtl');
                    }
                });
                s = root.innerHTML;
            }
        } catch (_) {
            // DOM parse failure — fall through to regex path below.
        }
    } else {
        // Regex fallback for environments without DOMParser.
        s = s.replace(/<(p|h[1-6]|blockquote|li|div)([^>]*)>([\s\S]*?)<\/\1>/gi, (m, tag, attrs, body) => {
            if (/\sdir=/i.test(attrs)) return m;
            // Strip inner tags for the RTL heuristic — we want to test the
            // VISIBLE text, not markup like <strong>.
            const inner = body.replace(/<[^>]+>/g, '');
            if (isMostlyRtl(inner)) {
                return '<' + tag + attrs + ' dir="rtl">' + body + '</' + tag + '>';
            }
            return m;
        });
    }

    return s;
}

/**
 * Per-platform toolbar feature flags. Buttons whose output the platform's
 * serializer would strip are hidden so the editor doesn't lie about what
 * the creator's draft will look like once published. Per PHASE_1C.md
 * §"Toolbar — Reach LinkedIn / Substack / IG context".
 */
const PLATFORM_FEATURES = {
    linkedin:  { headings: false, bold: true, italic: true, strike: true, code: false, link: true, bulletList: true, orderedList: true, blockquote: false, hr: false, image: false, subscribe: false, paywall: false },
    substack:  { headings: true,  bold: true, italic: true, strike: true, code: true,  link: true, bulletList: true, orderedList: true, blockquote: true,  hr: true,  image: true,  subscribe: true,  paywall: true  },
    instagram: { headings: false, bold: true, italic: true, strike: false, code: false, link: true, bulletList: false, orderedList: false, blockquote: false, hr: false, image: false, subscribe: false, paywall: false },
    x:         { headings: false, bold: false, italic: false, strike: false, code: false, link: true, bulletList: false, orderedList: false, blockquote: false, hr: false, image: false, subscribe: false, paywall: false },
    // Phase 1c.S — long-form clipboard handoff target. "Blog" covers
    // WordPress, Ghost, Wix, Webflow, Medium (retired self-serve API in
    // 2023) — anything with a paste-friendly editor. Long-form HTML, no
    // Substack-specific subscribe/paywall blocks.
    blog:      { headings: true,  bold: true, italic: true, strike: true, code: true,  link: true, bulletList: true, orderedList: true, blockquote: true,  hr: true,  image: true,  subscribe: false, paywall: false },
};
const VOICE_FEATURES = { headings: true, bold: true, italic: true, strike: true, code: true, link: true, bulletList: true, orderedList: true, blockquote: true, hr: true, image: true, subscribe: false, paywall: false };

function featuresForOpts(opts) {
    if (opts.context === 'reach' && opts.platform && PLATFORM_FEATURES[opts.platform]) {
        return PLATFORM_FEATURES[opts.platform];
    }
    // Voice context, or Reach with unknown platform — full toolbar.
    return VOICE_FEATURES;
}

/**
 * Build the toolbar DOM. Returns { el, refresh } — refresh() re-syncs the
 * .is-active state of each button against the current editor selection.
 */
function buildToolbar(editor, opts) {
    const features = featuresForOpts(opts);
    const wrap = document.createElement('div');
    wrap.className = 'adm-editor__toolbar';
    wrap.setAttribute('role', 'toolbar');
    wrap.setAttribute('aria-label', 'Editor controls');

    const btns = [];
    let pendingSep = false; // collapse adjacent separators caused by hidden buttons

    function btn(spec) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'adm-editor__tool';
        b.innerHTML = spec.icon;
        b.title = spec.title;
        b.setAttribute('aria-label', spec.title);
        b.addEventListener('click', (ev) => {
            ev.preventDefault();
            spec.run();
            editor.commands.focus();
            refresh();
        });
        flushSep();
        wrap.appendChild(b);
        btns.push({ el: b, isActive: spec.isActive });
        return b;
    }

    function sep() {
        // Defer — only emit when the next visible control actually appears.
        pendingSep = true;
    }
    function flushSep() {
        if (!pendingSep) return;
        pendingSep = false;
        // Don't emit a leading separator.
        if (!wrap.firstChild) return;
        const s = document.createElement('span');
        s.className = 'adm-editor__sep';
        s.setAttribute('aria-hidden', 'true');
        wrap.appendChild(s);
    }

    // History — always visible.
    btn({ icon: ICON.undo, title: 'Undo (Ctrl+Z)',       run: () => editor.chain().focus().undo().run(), isActive: () => false });
    btn({ icon: ICON.redo, title: 'Redo (Ctrl+Shift+Z)', run: () => editor.chain().focus().redo().run(), isActive: () => false });
    sep();

    // Block style — paragraph / H2 / H3 dropdown.
    if (features.headings) {
        const styleWrap = document.createElement('div');
        styleWrap.className = 'adm-editor__select-wrap';
        const styleSelect = document.createElement('select');
        styleSelect.className = 'adm-editor__select';
        styleSelect.setAttribute('aria-label', 'Block style');
        styleSelect.innerHTML = '<option value="p">Body</option><option value="h2">Heading 2</option><option value="h3">Heading 3</option>';
        styleSelect.addEventListener('change', () => {
            const v = styleSelect.value;
            const chain = editor.chain().focus();
            if (v === 'p') chain.setParagraph().run();
            else if (v === 'h2') chain.setHeading({ level: 2 }).run();
            else if (v === 'h3') chain.setHeading({ level: 3 }).run();
        });
        styleWrap.appendChild(styleSelect);
        flushSep();
        wrap.appendChild(styleWrap);
        btns.push({
            el: styleSelect,
            isActive: () => {
                if (editor.isActive('heading', { level: 2 })) return 'h2';
                if (editor.isActive('heading', { level: 3 })) return 'h3';
                return 'p';
            },
            kind: 'select',
        });
        sep();
    }

    // Inline marks
    if (features.bold)   btn({ icon: ICON.bold,   title: 'Bold (Ctrl+B)',        run: () => editor.chain().focus().toggleBold().run(),        isActive: () => editor.isActive('bold') });
    if (features.italic) btn({ icon: ICON.italic, title: 'Italic (Ctrl+I)',      run: () => editor.chain().focus().toggleItalic().run(),      isActive: () => editor.isActive('italic') });
    if (features.strike) btn({ icon: ICON.strike, title: 'Strikethrough',        run: () => editor.chain().focus().toggleStrike().run(),      isActive: () => editor.isActive('strike') });
    if (features.code)   btn({ icon: ICON.code,   title: 'Inline code',          run: () => editor.chain().focus().toggleCode().run(),        isActive: () => editor.isActive('code') });
    sep();

    // Link
    if (features.link) {
        btn({
            icon: ICON.link,
            title: 'Insert / edit link',
            run: () => promptLink(editor),
            isActive: () => editor.isActive('link'),
        });
        sep();
    }

    // Lists + quote + hr
    if (features.bulletList)  btn({ icon: ICON.bulletList,  title: 'Bulleted list',   run: () => editor.chain().focus().toggleBulletList().run(),  isActive: () => editor.isActive('bulletList') });
    if (features.orderedList) btn({ icon: ICON.orderedList, title: 'Numbered list',   run: () => editor.chain().focus().toggleOrderedList().run(), isActive: () => editor.isActive('orderedList') });
    if (features.blockquote)  btn({ icon: ICON.quote,       title: 'Blockquote',      run: () => editor.chain().focus().toggleBlockquote().run(),  isActive: () => editor.isActive('blockquote') });
    if (features.hr)          btn({ icon: ICON.hr,          title: 'Horizontal rule', run: () => editor.chain().focus().setHorizontalRule().run(), isActive: () => false });

    // Image — opens the custom Adamiro image picker (wp.media replacement).
    // Conditions:
    //  (a) the REST config has been localised by ADAMIRO_Editor::enqueue_bundle
    //  (b) the user has a Reach plan
    //  (c) EITHER the platform's serializer keeps inline images (Substack/Blog/Voice)
    //      OR this is a Reach post with a postId (Phase 2 hero gen — image attaches
    //      to the post's media slot even on platforms that strip inline imagery).
    var canGenerateHero = opts.context === 'reach' && opts.postId;
    // Round 4.5 P3 — graphics is gated by tier capability (Starter none /
    // Growth image / Business carousel) via AdamiroEditorConfig.graphics. A
    // Reach hero post keeps its own gating (canGenerateHero). Fall back to the
    // legacy hasReach flag when the caps object is absent (old config).
    var _gfx = (window.AdamiroEditorConfig && window.AdamiroEditorConfig.graphics) || null;
    var _hasGraphicsCap = _gfx ? (!!_gfx.image || !!_gfx.carousel) : !!(window.AdamiroEditorConfig && window.AdamiroEditorConfig.hasReach);
    if (typeof window !== 'undefined'
        && window.AdamiroEditorConfig
        && window.AdamiroEditorConfig.restUrl
        && (_hasGraphicsCap || canGenerateHero)
        && (features.image || canGenerateHero)) {
        sep();
        btn({
            icon: ICON.image,
            title: canGenerateHero ? 'Cover image' : 'Insert image',
            run: () => openImagePicker(editor, opts),
            isActive: () => editor.isActive('image'),
        });
    }

    // Custom blocks — Substack-only in v1.5. Subscribe + Paywall feature
    // flags above gate visibility. Buttons use the --label variant
    // (wider, padded, fits real text) and carry a long tooltip; the
    // dedicated help button at the end of the toolbar pops the full
    // explainer for creators who haven't met these blocks before.
    if (features.subscribe || features.paywall) sep();
    if (features.subscribe) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'adm-editor__tool adm-editor__tool--label';
        b.textContent = 'Subscribe';
        b.title = 'Insert a Substack subscribe button. Renders as a "Subscribe" CTA inside the published post on Substack — stripped from LinkedIn / Instagram / X.';
        b.addEventListener('click', (ev) => {
            ev.preventDefault();
            editor.chain().focus().insertReachSubscribe().run();
        });
        wrap.appendChild(b);
    }
    if (features.paywall) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'adm-editor__tool adm-editor__tool--label';
        b.textContent = 'Paywall';
        b.title = 'Insert a Substack paywall divider. Content ABOVE the line is free public preview; content BELOW is paid-only. On LinkedIn / Instagram / X the post is truncated at the divider — only the public preview ships.';
        b.addEventListener('click', (ev) => {
            ev.preventDefault();
            editor.chain().focus().insertReachPaywall().run();
        });
        wrap.appendChild(b);
    }

    // Help "?" — opens a small popover with the full explainer for the
    // custom blocks the current platform supports. Only shown if at least
    // one Substack-only block is available.
    if (features.subscribe || features.paywall) {
        const help = document.createElement('button');
        help.type = 'button';
        help.className = 'adm-editor__help';
        help.textContent = '?';
        help.setAttribute('aria-label', 'What do Subscribe and Paywall do?');
        help.title = 'What do Subscribe and Paywall do?';
        help.addEventListener('click', (ev) => {
            ev.preventDefault();
            showCustomBlocksHelp();
        });
        wrap.appendChild(help);
    }

    function refresh() {
        for (const b of btns) {
            const active = b.isActive();
            if (b.kind === 'select') {
                if (b.el.value !== active) b.el.value = active;
            } else {
                b.el.classList.toggle('is-active', !!active);
            }
        }
    }

    return { el: wrap, refresh };
}

/**
 * Modal explainer for the Substack-only custom blocks. Opens from the
 * `?` button at the right of the toolbar — also linked from the Reach
 * Guide tab's Editor section.
 */
function showCustomBlocksHelp() {
    const overlay = document.createElement('div');
    overlay.className = 'adm-editor-prompt';
    overlay.innerHTML =
        '<div class="adm-editor-prompt__card" role="dialog" aria-modal="true" style="max-width:520px;">' +
            '<h3 class="adm-editor-prompt__title">Subscribe & Paywall blocks</h3>' +
            '<div style="font-size:13px;line-height:1.55;color:rgba(241,245,249,.85);">' +
                '<p style="margin:0 0 12px;"><strong>Subscribe</strong> inserts a "Subscribe to my newsletter" call-to-action button. ' +
                'Substack renders it as a real subscribe widget inside the published post. ' +
                'LinkedIn, Instagram, and X strip it — they have no equivalent.</p>' +
                '<p style="margin:0 0 12px;"><strong>Paywall</strong> inserts a divider that splits the post in two:</p>' +
                '<ul style="margin:0 0 12px 18px;padding:0;">' +
                    '<li style="margin-bottom:4px;">Everything <em>above</em> the divider is the public free preview.</li>' +
                    '<li style="margin-bottom:4px;">Everything <em>below</em> is paid-subscriber-only on Substack.</li>' +
                '</ul>' +
                '<p style="margin:0 0 12px;">On LinkedIn / Instagram / X the post is automatically truncated <em>at</em> the divider — only the preview ships. Use this to publish a teaser everywhere and keep the deep-dive paywalled on Substack.</p>' +
                '<p style="margin:0 0 4px;font-size:12px;color:rgba(241,245,249,.55);">Both blocks only show on Substack drafts — they\'re hidden on LinkedIn / Instagram / X variants where they\'d be no-ops.</p>' +
            '</div>' +
            '<div class="adm-editor-prompt__actions">' +
                '<button type="button" class="adm-editor-prompt__btn adm-editor-prompt__btn--ok" data-act="close">Got it</button>' +
            '</div>' +
        '</div>';
    document.body.appendChild(overlay);
    function close() { overlay.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(ev) { if (ev.key === 'Escape') { ev.preventDefault(); close(); } }
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', (ev) => {
        if (ev.target === overlay) close();
        const act = ev.target.getAttribute && ev.target.getAttribute('data-act');
        if (act === 'close') close();
    });
}

/**
 * Adamiro-styled prompt modal. Replaces window.prompt() per the
 * no-native-browser-dialogs rule. Returns a Promise that resolves to
 * the entered string, or null if cancelled.
 */
function admPrompt({ title, label, placeholder, value, validate, validateError }) {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'adm-editor-prompt';
        overlay.innerHTML =
            '<div class="adm-editor-prompt__card" role="dialog" aria-modal="true">' +
                '<h3 class="adm-editor-prompt__title">' + (title || '').replace(/</g, '&lt;') + '</h3>' +
                '<label class="adm-editor-prompt__label">' + (label || '').replace(/</g, '&lt;') + '</label>' +
                '<input type="text" class="adm-editor-prompt__input" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false">' +
                '<div class="adm-editor-prompt__err" hidden></div>' +
                '<div class="adm-editor-prompt__actions">' +
                    '<button type="button" class="adm-editor-prompt__btn adm-editor-prompt__btn--ghost" data-act="cancel">Cancel</button>' +
                    '<button type="button" class="adm-editor-prompt__btn adm-editor-prompt__btn--ok" data-act="ok">Save</button>' +
                '</div>' +
            '</div>';
        document.body.appendChild(overlay);

        const input = overlay.querySelector('.adm-editor-prompt__input');
        const errBox = overlay.querySelector('.adm-editor-prompt__err');
        input.placeholder = placeholder || '';
        input.value = value || '';
        setTimeout(() => { input.focus(); input.select(); }, 0);

        function close(result) {
            overlay.remove();
            document.removeEventListener('keydown', onKey);
            resolve(result);
        }
        function submit() {
            const v = input.value.trim();
            // Empty string IS valid — caller uses it to mean "remove".
            if (v !== '' && typeof validate === 'function' && !validate(v)) {
                errBox.textContent = validateError || 'Invalid value.';
                errBox.hidden = false;
                input.focus();
                return;
            }
            close(v);
        }
        function onKey(ev) {
            if (ev.key === 'Escape') { ev.preventDefault(); close(null); }
            else if (ev.key === 'Enter') { ev.preventDefault(); submit(); }
        }
        document.addEventListener('keydown', onKey);
        overlay.addEventListener('click', (ev) => {
            if (ev.target === overlay) close(null);
            const act = ev.target.getAttribute && ev.target.getAttribute('data-act');
            if (act === 'cancel') close(null);
            if (act === 'ok') submit();
        });
    });
}

/**
 * Open the Adamiro image picker — a custom modal that replaces wp.media.
 *
 * Two tabs (Upload / Library), per-user scoping enforced server-side by
 * ADAMIRO_Media's REST endpoints. On Insert, calls editor.setImage with
 * the chosen URL + alt text.
 *
 * Modal is built fresh each open (cheap; matches the prompt modal pattern
 * in admPrompt above). REST URL + nonce come from window.AdamiroEditorConfig
 * which is localised by ADAMIRO_Editor::enqueue_bundle.
 */
function openImagePicker(editor, mountOpts) {
    const cfg = window.AdamiroEditorConfig || {};
    if (!cfg.restUrl || !cfg.nonce) {
        console.warn('[AdamiroEditor] AdamiroEditorConfig missing — image picker disabled.');
        return;
    }
    mountOpts = mountOpts || {};
    // Cover-image-variety Task 7 — standalone mode: opened from the media
    // library grid (no editor instance, no draft/post to insert into).
    // Generation-only: no Templates/Carousel/Infographic/Upload/Library,
    // no suggest-from-draft (there is no draft text to read), and the
    // primary footer action closes the modal instead of inserting.
    //
    // Campaign-images Task 1 fix round — that "generation-only" contract was
    // written for the media library's own "Generate an image" button, which
    // never sets onPick. A *picking* caller (the campaign planner, the
    // campaign page) opens the exact same standalone entry point but wants a
    // real pick back — Generate-only leaves it with nothing when the account
    // lacks image generation, and no way to reuse an existing image even
    // when it has generation. `picking` distinguishes the two: true only
    // when standalone AND the caller passed an onPick callback.
    var standalone = !!mountOpts.standalone;
    var picking = standalone && typeof mountOpts.onPick === 'function';
    // Generate tab visibility: any user on a Reach plan, with the reach REST
    // base configured. With postId → slot-based gen (per-post hero, smart-crop,
    // 2 free attempts). Without postId → freeform gen (Voice editor, no post
    // backing — image lands in the archive). Cost is cyclic: one task covers
    // three generations (charged on the 1st, 4th, 7th… try), per ADAMIRO_Reach_Media.
    // Round 4.5 P3 — capability-gated graphics. Prefer the explicit caps object
    // (Starter none / Growth image / Business carousel); fall back to the legacy
    // hasReach flag for old config. The plan ladder guarantees carousel ⇒ image,
    // so canGenerate (image) is true whenever capCarousel is.
    var graphics = (cfg && cfg.graphics) ? cfg.graphics : null;
    var capImage = graphics ? !!graphics.image : !!cfg.hasReach;
    var capCarousel = graphics ? !!graphics.carousel : !!cfg.hasReach;
    // Voice infographics (2026-06-12) — Growth+ capability, gated separately.
    var capInfographic = graphics ? !!graphics.infographic : false;
    // Standalone (library "Generate an image") is generation-only — force
    // the artifact-producing tabs off regardless of plan tier so tabsHtml/
    // the pane markup below (both gated on these two flags) render nothing
    // for Carousel/Infographic. The blocks that build those tabs already
    // null-guard every DOM lookup off their pane (`carPane && …`, `if
    // (tplRenderBtn) …`) for Growth-tier accounts that lack the capability
    // today, so forcing the flag false here reuses that exact, already-live
    // path rather than adding new branching.
    if (standalone) { capCarousel = false; capInfographic = false; }
    var voiceRestUrl = (cfg && cfg.voiceRestUrl) ? cfg.voiceRestUrl : null;
    var canGenerate = !!(capImage && (cfg.reachRestUrl || voiceRestUrl));
    // A picking caller doesn't need generation — Upload + Library are enough
    // to hand back a pick — so only the non-picking (media-library "Generate
    // an image") entry point refuses to open without it.
    if (standalone && !canGenerate && !picking) {
        console.warn('[AdamiroEditor] Image generation is not available for this account.');
        return;
    }
    var platformSlug = mountOpts.platform || null;
    var postId = mountOpts.postId || 0;
    var freeformMode = canGenerate && !postId;
    // Campaign-cover Task 3.1 — the campaign page's "Pick an image / Change"
    // cover dialog (case B) has no post and no editor either, but it does
    // have the campaign it's planning — a new mount option, read here so
    // both the button label/wiring below and the click handler agree on it.
    var campaignId = mountOpts.campaignId || 0;
    // Two standalone callers have something to ground a drafted prompt on
    // without an editor: a Reach post (A, context:'reach' + postId) or a
    // campaign (B, campaignId). Both keep the "draft a prompt" button and
    // skip the 12-char editor-text read the normal standalone case can't
    // satisfy (there is no editor in standalone at all).
    var canDraftFromContext = standalone
        && ((mountOpts.context === 'reach' && !!postId) || campaignId > 0);

    // State held by closures, not the DOM, so re-renders don't lose selection.
    let activeTab = canGenerate ? 'generate' : 'upload';
    let selected = null; // chosen attachment object
    let carouselSelection = null; // { pdfUrl, pdfId, thumbnailUrl, thumbnailId, caption, templateSlug }
    let libraryEntries = [];
    let libraryPage = 1;
    let libraryTotalPages = 1;
    let librarySearch = '';
    let searchDebounce = null;
    let libraryLoaded = false;

    // Generate-tab state — populated when the creator runs Generate.
    // sessionId is the freeform-mode key for the 2-free-attempts counter
    // (so Voice gets the same cost model as Reach's per-slot attempts).
    let genState = {
        slotId: null,
        imageUrl: null,
        attemptsUsed: 0,
        attemptsIncluded: 2,
        // Free retries remaining in the current block AFTER the latest attempt,
        // from the server's cyclic cadence (ADAMIRO_Reach_Media::free_retries_left:
        // 2 right after a charged attempt, counting down to 0). Drives the cost
        // note + button label — do NOT re-derive as attemptsIncluded−attemptsUsed,
        // which wrongly counts the initial (charged) image against the 2 retries.
        freeRetriesLeft: 2,
        modelUsed: null,
        platforms: platformSlug ? [platformSlug] : [],
        busy: false,
        sessionId: freeformMode ? (function () {
            // Re-use the same session id across Generate clicks within one
            // modal open so attempts accumulate; uuidv4-ish via crypto when
            // available, fallback to a random string otherwise.
            if (window.crypto && typeof window.crypto.randomUUID === 'function') {
                return window.crypto.randomUUID();
            }
            return 'gs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        })() : null,
    };

    // Cover-image-variety — the account's visual style catalogue and the
    // creator's current pick for the Generate tab. Starts on the account
    // default (AdamiroEditorConfig.accountVisualStyle, itself resolved
    // server-side from the brand row, falling back to 'documentary') so the
    // select opens already on the right choice rather than blank.
    var visualStyles = (cfg && cfg.visualStyles) || [];
    var selectedStyle = (cfg && cfg.accountVisualStyle) || '';

    // Product placement — set by the Generate block below (it needs that
    // block's fetch helpers and preview). Library cards and the Upload preview
    // call it to open the placement editor on their image; null when the
    // account cannot generate, which also keeps the action off those cards.
    var placeProductOn = null;
    // The open placement editor, if any. It lives inside the picker card, so
    // anything that removes the picker (Done, backdrop, Escape, a help icon)
    // must close it first or its document key listener outlives the DOM.
    var placementApi = null;
    function closePlacementEditor() {
        if (placementApi) { placementApi.close(); placementApi = null; }
    }
    // Library / Upload images the placement editor can take: the server reads
    // png, jpeg and webp.
    function canPlaceOn(mime) {
        return mime === 'image/png' || mime === 'image/jpeg' || mime === 'image/webp';
    }

    // Build modal DOM.
    const overlay = document.createElement('div');
    overlay.className = 'adm-media-picker';
    var tabsHtml = '';
    // Standalone, non-picking (the media library's own "Generate an image"
    // button — no onPick): Generate only — canGenerate is guaranteed true
    // here (the early-return above already ruled out the false case).
    // Standalone, picking (campaign-images Task 1 fix round): Generate
    // (when canGenerate), then Upload, then Library — a caller waiting on
    // onPick needs a way to hand back a pick even on an account without
    // image generation, and a way to reuse an existing image either way.
    // The Upload/Library pane markup and wiring below are already
    // unconditional (built for the full editor mode); only the tab buttons
    // were gated to Generate-only, so this reuses that exact DOM/JS.
    var showTemplates = canGenerate && !standalone;
    if (standalone && picking) {
        if (canGenerate) {
            tabsHtml += '<button type="button" class="acp-tab-btn active" data-tab="generate" role="tab">Generate</button>';
            tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="upload" role="tab">Upload</button>';
        } else {
            tabsHtml += '<button type="button" class="acp-tab-btn active" data-tab="upload" role="tab">Upload</button>';
        }
        tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="library" role="tab">Library</button>';
    } else if (standalone) {
        tabsHtml += '<button type="button" class="acp-tab-btn active" data-tab="generate" role="tab">Generate</button>';
    } else if (canGenerate) {
        tabsHtml += '<button type="button" class="acp-tab-btn active" data-tab="generate" role="tab">Generate</button>';
        tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="templates" role="tab">Templates</button>';
        // Carousel is a Business-tier capability — gated separately from image.
        if (capCarousel) {
            tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="carousel" role="tab">Carousel</button>';
        }
        // Infographic is a Growth+ capability — gated separately from image.
        if (capInfographic && voiceRestUrl) {
            tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="infographic" role="tab">Infographic</button>';
        }
        tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="upload" role="tab">Upload</button>';
        tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="library" role="tab">Library</button>';
    } else {
        tabsHtml += '<button type="button" class="acp-tab-btn active" data-tab="upload" role="tab">Upload</button>';
        tabsHtml += '<button type="button" class="acp-tab-btn" data-tab="library" role="tab">Library</button>';
    }
    overlay.innerHTML =
        '<div class="adm-media-picker__card" role="dialog" aria-modal="true" aria-labelledby="adm-mp-title">' +
            '<div class="adm-media-picker__header">' +
                '<h3 id="adm-mp-title" class="adm-media-picker__title">' + (canGenerate ? 'Cover image' : 'Insert image') + '</h3>' +
                '<button type="button" class="adm-media-picker__close" aria-label="Close" data-act="cancel">×</button>' +
            '</div>' +
            '<div class="adm-media-picker__tabs acp-tabs" role="tablist">' +
                tabsHtml +
            '</div>' +
            // Freeform Voice drafts (no Reach post yet) have no platform
            // context — mountOpts.platform is null — so Templates/Carousel
            // can't tell an Instagram-only template apart from a generic one
            // without asking. Reach-post context already fixes the platform
            // via the variant tab the creator is editing, so this never shows
            // there (platformSlug is already set, not user-overridable).
            (freeformMode ?
            '<div class="adm-mp-platform-select" role="group" aria-label="Preview templates for" hidden>' +
                '<span class="adm-mp-platform-select__label">Show templates for</span>' +
                '<button type="button" class="adm-mp-platform-select__btn active" data-platform="linkedin">LinkedIn</button>' +
                '<button type="button" class="adm-mp-platform-select__btn" data-platform="instagram">Instagram</button>' +
            '</div>' : '') +
            '<div class="adm-media-picker__body">' +
                (canGenerate ?
                '<div class="adm-media-picker__pane is-active" data-pane="generate">' +
                    '<div class="adm-mp-gen">' +
                        // Style cards — a word like "Analog film" tells a
                        // first-time user nothing; each card shows a real
                        // sample generated with that preset (static plugin
                        // assets). Thumb may be null before the assets ship;
                        // the card then falls back to a label-only tile.
                        (visualStyles.length ?
                        '<div class="adm-mp-gen__styles-wrap">' +
                            '<span class="adm-mp-gen__field-label" id="adm-mp-gen-styles-label">Style</span>' +
                            '<div class="adm-mp-gen__styles" role="radiogroup" aria-labelledby="adm-mp-gen-styles-label">' +
                                visualStyles.map(function (s) {
                                    var sel = s.slug === selectedStyle;
                                    return '<button type="button" class="adm-mp-gen__style' + (sel ? ' is-selected' : '') + '"' +
                                        ' data-style="' + s.slug + '" role="radio" aria-checked="' + (sel ? 'true' : 'false') + '"' +
                                        ' title="' + s.description + '">' +
                                        (s.thumb
                                            ? '<img class="adm-mp-gen__style-thumb" src="' + s.thumb + '" alt="" loading="lazy">'
                                            : '<span class="adm-mp-gen__style-thumb adm-mp-gen__style-thumb--empty"></span>') +
                                        '<span class="adm-mp-gen__style-name">' + s.label + '</span>' +
                                    '</button>';
                                }).join('') +
                            '</div>' +
                        '</div>'
                        : '') +
                        // Size — freeform only. A platform post's canvas is
                        // dictated by the platform registry server-side, so
                        // offering a choice there would produce wrongly-shaped
                        // heroes; freeform (Voice cover / library) generation
                        // already accepts five ratios server-side.
                        (freeformMode ?
                        '<div class="adm-mp-gen__aspect-wrap">' +
                            '<span class="adm-mp-gen__field-label" id="adm-mp-gen-aspect-label">Size</span>' +
                            '<div class="adm-mp-gen__aspect" role="radiogroup" aria-labelledby="adm-mp-gen-aspect-label">' +
                                [['16:9', 'Wide 16:9'], ['1:1', 'Square 1:1'], ['4:3', 'Classic 4:3'], ['3:4', 'Portrait 3:4'], ['9:16', 'Tall 9:16']].map(function (a) {
                                    var on = a[0] === '16:9';
                                    return '<button type="button" class="adm-mp-gen__aspect-btn' + (on ? ' is-selected' : '') + '"' +
                                        ' data-aspect="' + a[0] + '" role="radio" aria-checked="' + (on ? 'true' : 'false') + '">' + a[1] + '</button>';
                                }).join('') +
                            '</div>' +
                        '</div>'
                        : '') +
                        '<div class="adm-mp-gen__label-row">' +
                            '<label class="adm-mp-gen__label" for="adm-mp-gen-prompt">Image prompt</label>' +
                            '<a href="#" class="adm-mp-gen__policy-link adamiro-help-icon" data-guide="reach-guide-policy-rephrase" title="What content the image generator refuses">Image policy</a>' +
                        '</div>' +
                        // Canvas note — tells the creator the shape/size that will be
                        // generated for this platform (the hero is rendered at the
                        // platform's default aspect server-side). Populated after mount.
                        '<div class="adm-mp-gen__canvas-note" hidden></div>' +
                        '<textarea id="adm-mp-gen-prompt" class="adm-mp-gen__prompt" rows="5" placeholder="Describe the picture you want — what is in it, the mood, the colours. It draws pictures, not words: any text you ask for will come out wrong."></textarea>' +
                        // Include a product — scene mode. Hidden until the
                        // company's products are fetched (once per picker open);
                        // chips are toggles (second click deselects), shot chips
                        // appear only while a product is picked.
                        '<div class="adm-mp-gen__product" hidden>' +
                            '<span class="adm-mp-gen__field-label" id="adm-mp-gen-product-label">Include a product</span>' +
                            '<div class="adm-mp-gen__product-chips" role="group" aria-labelledby="adm-mp-gen-product-label"></div>' +
                            '<a class="adm-mp-gen__product-empty" href="#" target="_blank" rel="noopener" hidden>Add a product photo in your company profile</a>' +
                            '<div class="adm-mp-gen__shots" role="group" aria-label="Shot" hidden>' +
                                [['hand', 'In a hand'], ['table', 'On a table'], ['closeup', 'Close-up']].map(function (sh) {
                                    return '<button type="button" class="adm-mp-gen__aspect-btn adm-mp-gen__shot" data-shot="' + sh[0] + '" aria-pressed="false">' + sh[1] + '</button>';
                                }).join('') +
                            '</div>' +
                        '</div>' +
                        // 🚨 THESE WORDS ARE THE FIX FOR A CUSTOMER REPORT.
                        // They used to read "Suggest from draft" and "Generate
                        // hero". Andreas Ekengren, 2026-08-29: *"Does Draft
                        // refer to the article, or to whether I write my own
                        // description for the image? I do not understand what
                        // Generate hero means. What is a hero?"* Hero is our
                        // word. Say what the button does to the box above it.
                        '<div class="adm-mp-gen__row">' +
                            '<button type="button" class="adm-mp-gen__suggest" data-act="suggest">' +
                                (canDraftFromContext
                                    ? (postId ? 'Draft a prompt from the post' : 'Draft a prompt from the campaign')
                                    : 'Write this for me') +
                            '</button>' +
                            '<button type="button" class="adm-mp-gen__btn-gen" data-act="generate">Make the image</button>' +
                            '<button type="button" class="adm-mp-gen__btn-regen" data-act="regenerate" hidden>Regenerate</button>' +
                            '<span class="adm-mp-gen__attempts">1 task per image — includes 2 free retries</span>' +
                        '</div>' +
                        // Campaign-cover Task 3.1 — free-drafting note, shown only for the
                        // two standalone contexts above that unhide the suggest button
                        // (canDraftFromContext, toggled in JS below); reuses the existing
                        // .adm-mp-gen__attempts look rather than adding a class.
                        '<div class="adm-mp-gen__attempts" data-a3-suggest-free-note hidden>Drafting a prompt is free.</div>' +
                        '<div class="adm-mp-gen__status" hidden></div>' +
                        '<div class="adm-mp-gen__policyblock" hidden>' +
                            '<div class="adm-mp-gen__policyblock-msg"></div>' +
                            '<div class="adm-mp-gen__policyblock-cta">' +
                                '<button type="button" class="adm-mp-gen__policyblock-btn" data-act="rephrase">Rephrase to pass content policy</button>' +
                                '<span class="adamiro-help-icon acp-help-icon adm-mp-gen__policyblock-help" data-guide="reach-guide-policy-rephrase" title="Why this happens and what \'Rephrase to pass content policy\' does">?</span>' +
                            '</div>' +
                        '</div>' +
                        '<div class="adm-mp-gen__progress" hidden>' +
                            '<div class="adm-mp-gen__progress-bar"></div>' +
                            '<div class="adm-mp-gen__progress-label">Generating image…</div>' +
                        '</div>' +
                        '<div class="adm-mp-gen__preview" hidden>' +
                            '<img class="adm-mp-gen__preview-img" alt="">' +
                            '<div class="adm-mp-gen__preview-meta">' +
                                '<div class="adm-mp-gen__preview-model"></div>' +
                                '<div class="adm-mp-gen__preview-attempts"></div>' +
                            '</div>' +
                            '<button type="button" class="adm-mp-gen__suggest adm-mp-gen__place" data-act="place-product" hidden>Place a product</button>' +
                        '</div>' +
                    '</div>' +
                '</div>'
                : '') +
                (showTemplates ?
                '<div class="adm-media-picker__pane" data-pane="templates">' +
                    '<div class="adm-mp-tpl">' +
                        '<div class="adm-mp-tpl__intro"> Each render uses 1 task.</div>' +
                        '<div class="adm-mp-car__field">' +
                            '<label class="adm-mp-car__label">Template</label>' +
                            '<div class="adm-mp-car__tpl-grid adm-mp-car__tpl-grid--paged"></div>' +
                            '<div class="adm-mp-car__hint adm-mp-tpl__hint--tmpl"></div>' +
                        '</div>' +
                        '<div class="adm-mp-tpl__slots-wrap" hidden>' +
                            '<div class="adm-mp-tpl__slots"></div>' +
                            '<div class="adm-mp-tpl__status" hidden></div>' +
                            '<div class="adm-mp-tpl__progress" hidden>' +
                                '<div class="adm-mp-tpl__progress-bar"></div>' +
                                '<div class="adm-mp-tpl__progress-label">Rendering template…</div>' +
                            '</div>' +
                            '<div class="adm-mp-tpl__preview" hidden>' +
                                '<img class="adm-mp-tpl__preview-img" alt="">' +
                            '</div>' +
                            '<div class="adm-mp-tpl__actions">' +
                                '<button type="button" class="adm-mp-tpl__btn-clone" data-act="tpl-clone" hidden>Clone & customise</button>' +
                                '<button type="button" class="adm-mp-tpl__btn-delete" data-act="tpl-delete" hidden>Delete</button>' +
                                '<button type="button" class="adm-mp-tpl__btn-render" data-act="tpl-render">Render</button>' +
                            '</div>' +
                        '</div>' +
                    '</div>' +
                '</div>'
                : '') +
                (capCarousel ?
                '<div class="adm-media-picker__pane" data-pane="carousel">' +
                    '<div class="adm-mp-car">' +
                        '<div class="adm-mp-car__intro">' + ((platformSlug || '') === 'instagram'
                            ? 'Compose a multi-slide Instagram carousel — each slide is a separate image, published as a native swipeable IG carousel. 1 task for the whole set; the first slide goes into the post as a preview.'
                            : 'Compose a multi-slide LinkedIn carousel (PDF). 1 task for the whole carousel; the PDF lands in your media archive and the first slide goes into the post as a clickable preview.') + '</div>' +
                        '<div class="adm-mp-car__form">' +
                            '<div class="adm-mp-car__field">' +
                                '<label class="adm-mp-car__label">Template</label>' +
                                '<div class="adm-mp-car__tpl-grid adm-mp-car__tpl-grid--paged"></div>' +
                                '<div class="adm-mp-car__hint adm-mp-car__hint--tmpl"></div>' +
                            '</div>' +
                            '<div class="adm-mp-car__row">' +
                                '<div class="adm-mp-car__field adm-mp-car__field--narrow">' +
                                    '<label class="adm-mp-car__label" for="adm-mp-car-count">Slides</label>' +
                                    '<input id="adm-mp-car-count" type="number" class="adm-mp-car__select" min="3" max="10" value="5">' +
                                '</div>' +
                                '<div class="adm-mp-car__field">' +
                                    '<label class="adm-mp-car__label" for="adm-mp-car-topic">Topic</label>' +
                                    '<textarea id="adm-mp-car-topic" class="adm-mp-car__textarea" rows="2" placeholder="What is the carousel about? Be specific."></textarea>' +
                                '</div>' +
                            '</div>' +
                            '<div class="adm-mp-car__row">' +
                                '<div class="adm-mp-car__field">' +
                                    '<label class="adm-mp-car__label" for="adm-mp-car-tone">Tone</label>' +
                                    '<select id="adm-mp-car-tone" class="adm-mp-car__select">' +
                                        '<option value="direct">Direct</option>' +
                                        '<option value="warm">Warm</option>' +
                                        '<option value="urgent">Urgent</option>' +
                                        '<option value="reflective">Reflective</option>' +
                                    '</select>' +
                                '</div>' +
                                '<div class="adm-mp-car__field">' +
                                    '<label class="adm-mp-car__label" for="adm-mp-car-lang">Language</label>' +
                                    '<select id="adm-mp-car-lang" class="adm-mp-car__select">' +
                                        '<option value="en">English</option>' +
                                        '<option value="sv">Svenska</option>' +
                                        '<option value="da">Dansk</option>' +
                                        '<option value="no">Norsk</option>' +
                                        '<option value="de">Deutsch</option>' +
                                        '<option value="fr">Français</option>' +
                                        '<option value="es">Español</option>' +
                                    '</select>' +
                                '</div>' +
                            '</div>' +
                            '<div class="adm-mp-car__actions">' +
                                '<button type="button" class="adm-mp-gen__btn-gen" data-act="car-draft" disabled>Draft slides</button>' +
                                '<span class="adm-mp-gen__attempts">Free draft · review + edit before generating</span>' +
                            '</div>' +
                            '<div class="adm-mp-car__status" hidden></div>' +
                        '</div>' +
                        '<div class="adm-mp-car__progress adm-mp-car__progress--bar" hidden>' +
                            '<div class="acp-progress-wrap">' +
                                '<div class="acp-progress-bar a3-progress"><span class="acp-progress-fill"></span></div>' +
                                '<div class="acp-progress-meta">' +
                                    '<span class="acp-progress-label">Starting…</span>' +
                                    '<span class="acp-progress-elapsed">0s</span>' +
                                '</div>' +
                            '</div>' +
                            '<div class="adm-mp-car__progress-hint">Usually 45–90 seconds; up to 2 minutes for 7 slides when the image service is busy. Please keep this tab open.</div>' +
                        '</div>' +
                        '<div class="adm-mp-car__draft-progress" hidden>' +
                            '<div class="adm-mp-car__progress-bar"></div>' +
                            '<div class="adm-mp-car__progress-label">Drafting slide text…</div>' +
                            '<div class="adm-mp-car__progress-hint">Typically 5–15 seconds.</div>' +
                        '</div>' +
                        '<div class="adm-mp-car__review" hidden>' +
                            '<div class="adm-mp-car__review-head">' +
                                '<div>' +
                                    '<div class="adm-mp-car__review-title">Review &amp; edit slides</div>' +
                                    '<div class="adm-mp-car__review-sub">Tweak any text below, then generate the carousel. Image generation runs only when you press Generate.</div>' +
                                '</div>' +
                                '<button type="button" class="adm-mp-car__review-back" data-act="car-review-back">← Back to settings</button>' +
                            '</div>' +
                            '<div class="adm-mp-car__slides"></div>' +
                            '<div class="adm-mp-car__caption-wrap">' +
                                '<label class="adm-mp-car__label" for="adm-mp-car-caption">LinkedIn post body (caption)</label>' +
                                '<textarea id="adm-mp-car-caption" class="adm-mp-car__textarea" rows="4" placeholder="The text that runs alongside the carousel on LinkedIn."></textarea>' +
                            '</div>' +
                            '<div class="adm-mp-car__review-actions">' +
                                '<button type="button" class="adm-mp-car__btn-redraft" data-act="car-redraft">Re-draft text</button>' +
                                '<button type="button" class="adm-mp-gen__btn-gen" data-act="car-generate">Generate carousel — 1 task</button>' +
                            '</div>' +
                            '<div class="adm-mp-car__status adm-mp-car__status--review" hidden></div>' +
                        '</div>' +
                        '<div class="adm-mp-car__preview" hidden>' +
                            '<div class="adm-mp-car__preview-thumb"><img class="adm-mp-car__preview-img" alt=""></div>' +
                            '<div class="adm-mp-car__preview-meta">' +
                                '<div class="adm-mp-car__preview-title"></div>' +
                                '<div class="adm-mp-car__preview-caption"></div>' +
                                '<div class="adm-mp-car__status adm-mp-car__status--note" hidden></div>' +
                                '<div class="adm-mp-car__preview-actions">' +
                                    '<a class="adm-mp-car__preview-pdflink" href="#" target="_blank" rel="noopener">Open PDF</a>' +
                                    '<button type="button" class="adm-mp-car__preview-redo" data-act="car-redo">Compose another</button>' +
                                '</div>' +
                            '</div>' +
                        '</div>' +
                    '</div>' +
                '</div>'
                : '') +
                (capInfographic && voiceRestUrl ?
                '<div class="adm-media-picker__pane" data-pane="infographic">' +
                    '<div class="adm-mp-car adm-mp-inf">' +
                        '<div class="adm-mp-car__intro">Build an infographic from your draft — stat boards, step-by-step paths, comparisons, checklists, and more. The facts, charts, and icon suggestions come from what you wrote; photos come from your uploads or library, never generated. Suggesting is free; rendering costs 1 task and produces every size in one go.</div>' +
                        '<div class="adm-mp-car__form adm-mp-inf__form">' +
                            '<div class="adm-mp-car__field">' +
                                '<label class="adm-mp-car__label">Layout</label>' +
                                '<div class="adm-mp-car__actions adm-mp-inf__tpl-actions">' +
                                    '<button type="button" class="adm-mp-gen__suggest" data-act="inf-suggest-template">Suggest a template</button>' +
                                    '<span class="adm-mp-gen__attempts">Free — reads your draft and picks the layout that fits it best</span>' +
                                '</div>' +
                                '<div class="adm-mp-car__tpl-grid adm-mp-inf__tpl-grid"></div>' +
                                '<div class="adm-mp-car__hint adm-mp-inf__hint--tmpl"></div>' +
                            '</div>' +
                            '<div class="adm-mp-car__actions">' +
                                '<button type="button" class="adm-mp-gen__suggest" data-act="inf-suggest">Suggest texts</button>' +
                                '<span class="adm-mp-gen__attempts">Free — reads your draft and fills every field for this layout</span>' +
                            '</div>' +
                            '<div class="adm-mp-inf__slots-wrap" hidden>' +
                                '<div class="adm-mp-car__slot-grid adm-mp-inf__slots"></div>' +
                                '<div class="adm-mp-car__actions">' +
                                    '<button type="button" class="adm-mp-gen__suggest" data-act="inf-preview">Preview — free</button>' +
                                    '<button type="button" class="adm-mp-gen__btn-gen" data-act="inf-generate">Generate infographic — 1 task</button>' +
                                '</div>' +
                                '<div class="adm-mp-inf__report" data-inf-report hidden></div>' +
                            '</div>' +
                            '<div class="adm-mp-car__status adm-mp-inf__status" hidden></div>' +
                        '</div>' +
                        '<div class="adm-mp-car__draft-progress adm-mp-inf__suggest-progress" hidden>' +
                            '<div class="adm-mp-car__progress-bar"></div>' +
                            '<div class="adm-mp-car__progress-label">Reading your draft…</div>' +
                            '<div class="adm-mp-car__progress-hint">Typically 5–15 seconds.</div>' +
                        '</div>' +
                        '<div class="adm-mp-car__progress adm-mp-inf__progress" hidden>' +
                            '<div class="adm-mp-car__progress-bar"></div>' +
                            '<div class="adm-mp-car__progress-label">Rendering infographic…</div>' +
                            '<div class="adm-mp-car__progress-hint">Typically 10–30 seconds for all aspects.</div>' +
                        '</div>' +
                        '<div class="adm-mp-inf__result" hidden>' +
                            '<div class="adm-mp-car__review-head">' +
                                '<div>' +
                                    '<div class="adm-mp-car__review-title">Pick the rendition to insert</div>' +
                                    '<div class="adm-mp-car__review-sub">All sizes are saved to your library — pick the one for this post.</div>' +
                                '</div>' +
                                '<button type="button" class="adm-mp-car__review-back" data-act="inf-redo">← Back to layouts</button>' +
                            '</div>' +
                            '<div class="adm-mp-inf__result-grid"></div>' +
                        '</div>' +
                    '</div>' +
                '</div>'
                : '') +
                '<div class="adm-media-picker__pane' + (canGenerate ? '' : ' is-active') + '" data-pane="upload">' +
                    '<div class="adm-media-picker__drop" tabindex="0" role="button" aria-label="Drop image here or click to choose a file">' +
                        '<svg class="adm-media-picker__drop-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                            '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
                            '<polyline points="17 8 12 3 7 8"/>' +
                            '<line x1="12" y1="3" x2="12" y2="15"/>' +
                        '</svg>' +
                        '<p class="adm-media-picker__drop-title">Drop an image here</p>' +
                        '<p class="adm-media-picker__drop-hint">or click to choose · JPG, PNG, GIF, WebP</p>' +
                        '<input type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden>' +
                    '</div>' +
                    '<div class="adm-media-picker__upload-status" hidden></div>' +
                    '<div class="adm-media-picker__upload-progress" hidden>' +
                        '<div class="adm-media-picker__upload-progress-bar"></div>' +
                    '</div>' +
                    '<div class="adm-media-picker__preview" hidden>' +
                        '<img class="adm-media-picker__preview-img" alt="">' +
                        '<div class="adm-media-picker__preview-meta">' +
                            '<div class="adm-media-picker__preview-name"></div>' +
                            '<div class="adm-media-picker__preview-dims"></div>' +
                        '</div>' +
                        '<button type="button" class="adm-media-picker__preview-swap" data-act="swap-file">Choose a different image</button>' +
                        (canGenerate ? '<button type="button" class="adm-media-picker__preview-swap adm-mp-upload-place" data-act="place-product-upload" hidden>Place a product</button>' : '') +
                    '</div>' +
                '</div>' +
                '<div class="adm-media-picker__pane" data-pane="library">' +
                    '<div class="adm-media-picker__toolbar">' +
                        '<input type="search" class="adm-media-picker__search" placeholder="Search your images…" aria-label="Search your images">' +
                    '</div>' +
                    '<div class="adm-media-picker__grid" role="listbox"></div>' +
                    '<div class="adm-media-picker__pager" hidden></div>' +
                '</div>' +
            '</div>' +
            '<div class="adm-media-picker__footer">' +
                '<div class="adm-media-picker__alt-wrap">' +
                    '<label class="adm-media-picker__alt-label" for="adm-mp-alt">Alt text (recommended)</label>' +
                    '<input id="adm-mp-alt" type="text" class="adm-media-picker__alt" placeholder="Describe the image for screen readers">' +
                '</div>' +
                '<div class="adm-media-picker__actions">' +
                    '<button type="button" class="adm-media-picker__btn" data-act="cancel">Cancel</button>' +
                    '<button type="button" class="adm-media-picker__btn adm-media-picker__btn--primary" data-act="insert" disabled>Insert</button>' +
                '</div>' +
            '</div>' +
        '</div>';
    document.body.appendChild(overlay);

    const tabs = overlay.querySelectorAll('.adm-media-picker__tabs .acp-tab-btn');
    const panes = overlay.querySelectorAll('.adm-media-picker__pane');
    const drop = overlay.querySelector('.adm-media-picker__drop');
    const fileInput = overlay.querySelector('input[type="file"]');
    const status = overlay.querySelector('.adm-media-picker__upload-status');
    const progress = overlay.querySelector('.adm-media-picker__upload-progress');
    const progressBar = overlay.querySelector('.adm-media-picker__upload-progress-bar');
    const preview = overlay.querySelector('.adm-media-picker__preview');
    const previewImg = overlay.querySelector('.adm-media-picker__preview-img');
    const previewName = overlay.querySelector('.adm-media-picker__preview-name');
    const previewDims = overlay.querySelector('.adm-media-picker__preview-dims');
    const previewSwap = overlay.querySelector('.adm-media-picker__preview-swap');
    const uploadPlaceBtn = overlay.querySelector('[data-act="place-product-upload"]');
    let uploadedAtt = null;
    const search = overlay.querySelector('.adm-media-picker__search');
    const grid = overlay.querySelector('.adm-media-picker__grid');
    const libraryPagerEl = overlay.querySelector('.adm-media-picker__pager');
    const altInput = overlay.querySelector('.adm-media-picker__alt');
    const insertBtn = overlay.querySelector('[data-act="insert"]');
    // Standalone's primary action closes the modal instead of inserting —
    // there's nothing to insert into. Same disabled-until-selected gating
    // (refreshInsertBtn below) so it only lights up once a generate succeeds.
    if (standalone && insertBtn) { insertBtn.textContent = 'Done'; }

    // Pre-fill the alt text from context so the field is never blank. The post
    // title is the best available description until the user types something
    // more specific. A real per-image alt (a library image's stored alt, or a
    // generated image's description) takes precedence over the title; once the
    // user edits the field by hand, nothing overrides it.
    function contextAlt() {
        // v2's editor is a set of ids (#draft-editor-title-input); the live
        // v3 editor marks its title input with [data-a3-write-title]. Check
        // both — reading only the v2 id is how this default silently died on
        // the v3 surface (same trap as VoiceSendToReachV3Test documents).
        var t = document.getElementById('draft-editor-title-input')
            || document.querySelector('[data-a3-write-title]');
        var v = (t && t.value) ? String(t.value).trim() : '';
        if (!v && window.AdamiroEditorConfig && window.AdamiroEditorConfig.contextTitle) {
            v = String(window.AdamiroEditorConfig.contextTitle).trim();
        }
        return v;
    }
    var altUserEdited = false;
    altInput.addEventListener('input', function () { altUserEdited = true; });
    function applyDefaultAlt(preferred) {
        if (altUserEdited) return;
        var p = (preferred && String(preferred).trim()) || '';
        altInput.value = p || contextAlt();
    }
    applyDefaultAlt('');

    function close(insertedAttachment, insertedCarousel) {
        closePlacementEditor();
        overlay.remove();
        document.removeEventListener('keydown', onKey);
        // Campaign-images Task 1 — wrap the body in try/finally so onClose()
        // fires exactly once per close() call, in every mode (standalone or
        // not) and on every path, including the early `return`s below, and
        // — for the Reach hero/attach + carousel-attach fetches — only after
        // that fetch has been sent (fetch() is fire-and-forget: the request
        // goes out synchronously even though the promise resolves later).
        try {
        if (insertedCarousel) {
            var carAlt = (altInput.value.trim()) || contextAlt() || ('Carousel: ' + (insertedCarousel.templateSlug || ''));
            var safeAlt = String(carAlt).replace(/"/g, '&quot;').replace(/</g, '&lt;');
            // Instagram carousel: per-slide PNGs (no PDF). Insert the first slide
            // as a plain image for a visible breadcrumb, then wire the variant
            // via attach-ig-set so publish_now dispatches publish_carousel with
            // the per-slide PNG URLs.
            if (insertedCarousel.isInstagram) {
                var firstPng = insertedCarousel.thumbnailUrl || (insertedCarousel.pngUrls && insertedCarousel.pngUrls[0]) || '';
                // Standalone has no editor to insert into (nothing calls
                // close() with a carousel selection there — Carousel isn't
                // rendered — but guard anyway per the same rule as every
                // other editor.chain() site in this function).
                if (firstPng && editor) {
                    editor.chain().focus().setImage({ src: firstPng, alt: carAlt }).run();
                }
                if (mountOpts.context === 'reach' && postId && cfg.reachRestUrl && cfg.nonce) {
                    try {
                        fetch(cfg.reachRestUrl + 'posts/' + postId + '/carousel/attach-ig-set', {
                            method: 'POST',
                            credentials: 'same-origin',
                            headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                            body: JSON.stringify({
                                platform: 'instagram',
                                aspect: insertedCarousel.aspect || 'square',
                                png_attachment_ids: insertedCarousel.pngAttachmentIds || [],
                                caption: insertedCarousel.caption || '',
                                slide_count: insertedCarousel.slideCount || 0,
                                template_slug: insertedCarousel.templateSlug || '',
                            }),
                        }).catch(function () { /* non-fatal — first slide still inline */ });
                    } catch (_) { /* ignore */ }
                }
                return;
            }
            // LinkedIn carousel — embed a clickable thumbnail linking to the PDF.
            // TipTap's setImage extension only writes <img>; we use insertContent
            // with a raw paragraph wrapper so the schema accepts the anchor.
            var safePdf = String(insertedCarousel.pdfUrl).replace(/"/g, '%22');
            var safeThumb = String(insertedCarousel.thumbnailUrl).replace(/"/g, '%22');
            var html = '<p><a href="' + safePdf + '" target="_blank" rel="noopener">' +
                '<img src="' + safeThumb + '" alt="' + safeAlt + '" />' +
                '</a></p>';
            if (editor) { editor.chain().focus().insertContent(html).run(); }
            // Phase 3b — for Reach posts, also wire the carousel to the
            // platform variant so publish_now dispatches publish_carousel
            // (native LinkedIn doc-carousel) instead of falling back to
            // publish_text. Without this, only the body anchor is set and
            // LinkedIn publishes text-only.
            if (mountOpts.context === 'reach' && postId && cfg.reachRestUrl && cfg.nonce) {
                try {
                    fetch(cfg.reachRestUrl + 'posts/' + postId + '/carousel/attach-freeform', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({
                            platform: mountOpts.platform || 'linkedin',
                            pdf_attachment_id: insertedCarousel.pdfId,
                            thumbnail_attachment_id: insertedCarousel.thumbnailId || 0,
                            caption: insertedCarousel.caption || '',
                            slide_count: insertedCarousel.slideCount || 0,
                            template_slug: insertedCarousel.templateSlug || '',
                        }),
                    }).catch(function () { /* non-fatal — anchor still in body */ });
                } catch (_) { /* ignore */ }
            }
            return;
        }
        if (insertedAttachment) {
            var altText = altInput.value.trim() || insertedAttachment.alt || '';
            // Documents (PDFs picked from the Library, e.g. previously composed
            // carousels) can't render through TipTap's image extension —
            // <img src=foo.pdf> shows nothing. Wrap them as a clickable thumb
            // pointing at the PDF instead, mirroring the Carousel-tab insert.
            // For Reach LinkedIn the actual swipeable upload happens via the
            // Carousel composer's hero-attach path; the body anchor is just a
            // user-visible breadcrumb for platforms that keep inline links.
            if (insertedAttachment.is_document || insertedAttachment.mime === 'application/pdf') {
                var safeAltDoc = String(altText || insertedAttachment.filename || 'Document').replace(/"/g, '&quot;').replace(/</g, '&lt;');
                var safePdf2 = String(insertedAttachment.url).replace(/"/g, '%22');
                var safeThumb2 = String(insertedAttachment.thumb_url || insertedAttachment.url).replace(/"/g, '%22');
                var htmlDoc = '<p><a href="' + safePdf2 + '" target="_blank" rel="noopener">' +
                    '<img src="' + safeThumb2 + '" alt="' + safeAltDoc + '" />' +
                    '</a></p>';
                if (editor) { editor.chain().focus().insertContent(htmlDoc).run(); }
                // Phase 3b — same hook as Carousel tab: when a PDF is picked
                // from the Library in Reach mode, wire it to the variant as
                // media_kind='carousel' so publish_now dispatches the native
                // LinkedIn doc-carousel path. Without this, picking an existing
                // carousel PDF only embeds a body anchor and LinkedIn publishes
                // text + thumbnail-as-image.
                if (mountOpts.context === 'reach' && postId && cfg.reachRestUrl && cfg.nonce) {
                    try {
                        fetch(cfg.reachRestUrl + 'posts/' + postId + '/carousel/attach-freeform', {
                            method: 'POST',
                            credentials: 'same-origin',
                            headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                            body: JSON.stringify({
                                platform: mountOpts.platform || 'linkedin',
                                pdf_attachment_id: insertedAttachment.id,
                                caption: altText || insertedAttachment.filename || '',
                            }),
                        }).catch(function () { /* non-fatal — body anchor still works */ });
                    } catch (_) { /* ignore */ }
                }
                return;
            }
            if (editor) {
                editor.chain().focus().setImage({
                    src: insertedAttachment.url,
                    alt: altText,
                }).run();
            }
            // For Reach posts, also attach the image to the post's hero slot
            // so platforms that strip inline imagery (LinkedIn / X / Instagram)
            // still get the image via content.media. Without this, only the
            // Generate tab populated the slot — Upload + Library inserts left
            // LinkedIn publishing text-only despite the image being in the
            // editor. cfg.reachRestUrl is the Reach REST namespace localised by
            // ADAMIRO_Editor::enqueue_bundle.
            if (mountOpts.context === 'reach' && postId && cfg.reachRestUrl && cfg.nonce) {
                try {
                    fetch(cfg.reachRestUrl + 'posts/' + postId + '/hero/attach', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({ url: insertedAttachment.url, alt: altText }),
                    }).catch(function () { /* non-fatal — image still inline */ });
                } catch (_) { /* ignore */ }
            }
        }
        } finally {
            if (typeof mountOpts.onClose === 'function') { mountOpts.onClose(); }
        }
    }
    function onKey(ev) {
        if (ev.key === 'Escape') { ev.preventDefault(); close(null); }
    }
    document.addEventListener('keydown', onKey);

    overlay.addEventListener('click', (ev) => {
        if (ev.target === overlay) close(null);
        const act = ev.target.getAttribute && ev.target.getAttribute('data-act');
        if (act === 'cancel') close(null);
        if (act === 'insert') {
            // Standalone: nothing to insert into (no editor) — the primary
            // action is relabelled 'Done' below and just closes the modal.
            // The freeform generate flow already archived the image server
            // side, so there's nothing left to write; but a caller (campaign
            // planner, campaign page — campaign-images Task 1) still needs
            // the chosen attachment handed back, so hand it to onPick before
            // closing.
            if (standalone) {
                if (selected && typeof mountOpts.onPick === 'function') { mountOpts.onPick(selected); }
                close(null);
            } else if (activeTab === 'carousel' && carouselSelection) {
                close(null, carouselSelection);
            } else if (selected) {
                close(selected);
            }
        }
    });

    // Tab switching. Insert button is disabled per-tab unless that tab has a
    // current selection (image attachment for most tabs, a composed carousel
    // for the Carousel tab).
    function refreshInsertBtn() {
        if (activeTab === 'carousel') {
            insertBtn.disabled = !carouselSelection;
        } else {
            insertBtn.disabled = !selected;
        }
    }
    // Title follows the active tab — Carousel and Infographic produce their
    // own artifacts, so the cover-image title would be wrong there.
    var mpDefaultTitle = canGenerate ? 'Cover image' : 'Insert image';
    var MP_TAB_TITLES = { carousel: 'Carousel', infographic: 'Infographic' };
    var mpTitleEl = overlay.querySelector('#adm-mp-title');
    tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
            activeTab = tab.getAttribute('data-tab');
            tabs.forEach((t) => t.classList.toggle('active', t === tab));
            panes.forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-pane') === activeTab));
            if (mpTitleEl) mpTitleEl.textContent = MP_TAB_TITLES[activeTab] || mpDefaultTitle;
            if (activeTab === 'library' && !libraryLoaded) {
                loadLibrary(1);
            }
            // The platform switch only affects Templates/Carousel filtering —
            // showing it on Generate/Infographic/Upload/Library looks like a
            // dead control since nothing there reads platformSlug this way.
            if (platformSelectEl) {
                platformSelectEl.hidden = (activeTab !== 'templates' && activeTab !== 'carousel');
            }
            refreshInsertBtn();
        });
    });

    // Freeform platform selector (LinkedIn/Instagram) — only present when
    // freeformMode is true. Re-renders whichever of Templates/Carousel have
    // already loaded so the Instagram-only cards appear without a re-fetch.
    // Only the state/UI bits that don't need the Templates/Carousel blocks'
    // internals live here. renderTemplateGrid/renderCarTplGrid are `function`
    // declarations block-scoped inside their own `if (canGenerate) {}` below
    // (strict mode) — calling them from this outer scope throws a silent
    // ReferenceError inside the click handler. Each block registers its own
    // extra listener on these same buttons instead, mirroring the existing
    // "lazy-load when the user opens the tab" pattern already used for
    // `tabs` (see the `tabs.forEach` inside the Templates block below).
    var platformSelectEl = overlay.querySelector('.adm-mp-platform-select');
    var platformBtns = platformSelectEl ? platformSelectEl.querySelectorAll('.adm-mp-platform-select__btn') : null;
    if (platformSelectEl && platformBtns) {
        platformBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                var plat = btn.getAttribute('data-platform');
                if (plat === platformSlug) return;
                platformSlug = plat;
                platformBtns.forEach(function (b) { b.classList.toggle('active', b === btn); });
                var carIntroEl = overlay.querySelector('.adm-mp-car__intro');
                if (carIntroEl) {
                    carIntroEl.textContent = (platformSlug === 'instagram')
                        ? 'Compose a multi-slide Instagram carousel — each slide is a separate image, published as a native swipeable IG carousel. 1 task for the whole set; the first slide goes into the post as a preview.'
                        : 'Compose a multi-slide LinkedIn carousel (PDF). 1 task for the whole carousel; the PDF lands in your media archive and the first slide goes into the post as a clickable preview.';
                }
            });
        });
    }

    // ── Upload flow ──────────────────────────────────────────────────────
    function selectAttachment(att) {
        // Campaign-images Task 1 fix round — a picking caller only ever gets
        // an attachment id back through onPick, so a document slipping
        // through here (a stale Library page fetched before this fix, or any
        // future entry point into this pane) must never become `selected`:
        // the id it would produce is exactly the one resolve_cover() refuses
        // server-side, but silently. loadLibrary()'s kind=images already
        // keeps documents out of the grid for a picking caller — this is
        // defence in depth, not the primary guard, so it stays a silent
        // no-op; the caller-side hint ("Pick an image, not a document.")
        // lives in dashboard-v3.js, closer to the reader.
        if (picking && (att.is_document || (att.mime && String(att.mime).indexOf('image/') !== 0))) {
            return;
        }
        selected = att;
        insertBtn.disabled = false;
        // Prefer the image's own alt; fall back to the post title.
        applyDefaultAlt(att.alt);
    }
    function setStatus(msg, isError) {
        if (!msg) { status.hidden = true; status.textContent = ''; return; }
        status.hidden = false;
        status.textContent = msg;
        status.classList.toggle('is-error', !!isError);
    }
    function showProgress(pct) {
        progress.hidden = false;
        progressBar.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }
    function hideProgress() {
        progress.hidden = true;
        progressBar.style.width = '0%';
    }
    function showPreview(att) {
        previewImg.src = att.thumb_url || att.url;
        previewImg.alt = att.alt || att.filename || '';
        previewName.textContent = att.filename || 'Image ' + att.id;
        previewDims.textContent = (att.width && att.height) ? (att.width + ' × ' + att.height + ' px') : '';
        uploadedAtt = att;
        if (uploadPlaceBtn) { uploadPlaceBtn.hidden = !(att.id > 0 && canPlaceOn(att.mime)); }
        preview.hidden = false;
        drop.hidden = true;
        setStatus(null);
    }
    function resetUploadPane() {
        uploadedAtt = null;
        if (uploadPlaceBtn) { uploadPlaceBtn.hidden = true; }
        preview.hidden = true;
        drop.hidden = false;
        fileInput.value = '';
        hideProgress();
        setStatus(null);
        selected = null;
        insertBtn.disabled = true;
    }
    if (uploadPlaceBtn) {
        uploadPlaceBtn.addEventListener('click', function () {
            if (uploadedAtt && placeProductOn) { placeProductOn(uploadedAtt.id, uploadedAtt.url); }
        });
    }
    if (previewSwap) {
        previewSwap.addEventListener('click', function () {
            resetUploadPane();
            fileInput.click();
        });
    }

    drop.addEventListener('click', () => fileInput.click());
    drop.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); fileInput.click(); }
    });
    fileInput.addEventListener('change', () => {
        if (fileInput.files && fileInput.files[0]) uploadFile(fileInput.files[0]);
    });
    drop.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        drop.classList.add('is-dragover');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-dragover'));
    drop.addEventListener('drop', (ev) => {
        ev.preventDefault();
        drop.classList.remove('is-dragover');
        const file = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
        if (file) uploadFile(file);
    });

    function uploadFile(file) {
        const ALLOWED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!ALLOWED.includes(file.type)) {
            setStatus('Only JPEG, PNG, GIF, or WebP images allowed.', true);
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            setStatus('File too large. Max 50 MB.', true);
            return;
        }
        setStatus('Uploading ' + file.name + '…', false);
        showProgress(0);

        const fd = new FormData();
        fd.append('file', file);
        if (altInput.value.trim()) fd.append('alt', altInput.value.trim());

        const xhr = new XMLHttpRequest();
        xhr.open('POST', cfg.restUrl + 'upload');
        admSetRestHeaders(xhr);
        xhr.upload.addEventListener('progress', (ev) => {
            if (ev.lengthComputable) showProgress((ev.loaded / ev.total) * 100);
        });
        xhr.onload = () => {
            hideProgress();
            let data = null;
            try { data = JSON.parse(xhr.responseText); } catch (e) { /* fall-through */ }
            if (xhr.status >= 200 && xhr.status < 300 && data && data.url) {
                selectAttachment(data);
                showPreview(data);
                // Invalidate the library cache so the next switch to the
                // Library tab re-fetches and shows the new upload. Don't
                // mutate libraryEntries here — the fresh fetch is authoritative
                // (server-side filename hashing means the canonical filename
                // can differ from what XHR returned in edge cases).
                libraryLoaded = false;
                if (activeTab === 'library') loadLibrary(1);
                // Notify any dashboard listening for media changes (eg the
                // Reach Media tab, which would otherwise show a stale grid
                // until the user hard-refreshes). Bubbles via window so
                // it crosses the bundle/dashboard boundary cleanly.
                try {
                    window.dispatchEvent(new CustomEvent('adamiro:media-uploaded', { detail: data }));
                } catch (e) { /* old IE only — ignore */ }
            } else {
                const msg = (data && (data.message || data.error)) || 'Upload failed (HTTP ' + xhr.status + ').';
                setStatus(msg, true);
            }
        };
        xhr.onerror = () => {
            hideProgress();
            setStatus('Network error — try again.', true);
        };
        xhr.send(fd);
    }

    // ── Library flow ─────────────────────────────────────────────────────
    // Short format label from a MIME type, for the corner pill. PDF (carousel
    // exports) is the load-bearing case — mirrors the Voice Media tab.
    function libFormatLabel(mime) {
        mime = String(mime || '').toLowerCase();
        if (mime === 'application/pdf') return 'PDF';
        if (mime === 'image/png') return 'PNG';
        if (mime === 'image/jpeg' || mime === 'image/jpg') return 'JPG';
        if (mime === 'image/gif') return 'GIF';
        if (mime === 'image/webp') return 'WEBP';
        if (mime === 'image/svg+xml') return 'SVG';
        return '';
    }
    function renderGrid() {
        grid.innerHTML = '';
        if (libraryEntries.length === 0) {
            grid.innerHTML = '<div class="adm-media-picker__empty">No images yet. Upload one to get started.</div>';
            libraryPagerEl.hidden = true;
            return;
        }
        libraryEntries.forEach((entry) => {
            const thumb = document.createElement('div');
            thumb.className = 'adm-media-picker__thumb';
            thumb.setAttribute('data-id', String(entry.id));
            thumb.setAttribute('data-acp-card', '');
            thumb.setAttribute('role', 'option');
            thumb.setAttribute('aria-label', entry.filename || 'Image ' + entry.id);
            // Canonical Adamiro delete affordance (corner X, hover-reveal). Wired
            // to the global .acp-card-delete handler via data-acp-del-url; the
            // grid-scoped acp-card:deleted listener below prunes libraryEntries.
            // Format pill (top-left) — same component as the Voice Media tab
            // (.vmd-media-format). Lets users tell a carousel PDF apart from a
            // plain image at a glance. Top-left so it never collides with the
            // corner X / select checkmark (both top-right).
            var fmtLabel = libFormatLabel(entry.mime);
            var fmtPill = fmtLabel
                ? '<span class="vmd-media-format" style="position:absolute;top:8px;left:8px;z-index:2;padding:3px 8px;border-radius:5px;background:rgba(11,20,38,.85);border:1px solid rgba(47,184,160,.45);color:#2FB8A0;font-size:10px;font-weight:700;letter-spacing:.6px;font-family:JetBrains Mono,monospace;pointer-events:none;">' + fmtLabel + '</span>'
                : '';
            thumb.innerHTML =
                '<img src="' + entry.thumb_url + '" alt="" loading="lazy">' +
                fmtPill +
                '<span class="adm-media-picker__thumb-check" aria-hidden="true">✓</span>' +
                (canGenerate && canPlaceOn(entry.mime)
                    ? '<button type="button" class="adm-media-picker__thumb-place" data-act="place-product-lib">Place a product</button>'
                    : '') +
                '<button type="button" class="acp-card-delete" data-acp-tip="Delete" data-acp-tip-pos="left"' +
                    ' data-acp-del-url="/media/' + entry.id + '"' +
                    ' data-acp-del-title="Delete this image?"' +
                    ' data-acp-del-msg="It will be removed from your library and from any drafts that still reference it."' +
                    ' aria-label="Delete">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
                '</button>';
            thumb.addEventListener('click', (e) => {
                // Don't treat a delete-X click as an image pick.
                if (e.target.closest('.acp-card-delete')) return;
                if (e.target.closest('[data-act="place-product-lib"]')) {
                    grid.querySelectorAll('.adm-media-picker__thumb').forEach((t) => t.classList.remove('is-selected'));
                    thumb.classList.add('is-selected');
                    selectAttachment(entry);
                    if (placeProductOn) { placeProductOn(entry.id, entry.url || entry.thumb_url); }
                    return;
                }
                grid.querySelectorAll('.adm-media-picker__thumb').forEach((t) => t.classList.remove('is-selected'));
                thumb.classList.add('is-selected');
                selectAttachment(entry);
            });
            grid.appendChild(thumb);
        });
        renderLibraryPager();
    }

    // Prev/next pager (2026-07-10) — replaces the old "Load more" append
    // button so the Library tab matches the Templates/Carousel/Infographic
    // tabs' paging pattern. The REST endpoint already returns page/total_pages
    // (it was already paginated server-side; only the client UX changes).
    function renderLibraryPager() {
        if (libraryTotalPages <= 1) {
            libraryPagerEl.hidden = true;
            return;
        }
        libraryPagerEl.hidden = false;
        libraryPagerEl.innerHTML =
            '<button type="button" class="adm-mp-gen__suggest" data-lib-page="prev"' + (libraryPage <= 1 ? ' disabled' : '') + '>&#8592; Prev</button>' +
            '<span class="adm-media-picker__pager-count">' + libraryPage + ' / ' + libraryTotalPages + '</span>' +
            '<button type="button" class="adm-mp-gen__suggest" data-lib-page="next"' + (libraryPage >= libraryTotalPages ? ' disabled' : '') + '>Next &#8594;</button>';
        libraryPagerEl.querySelectorAll('[data-lib-page]').forEach((btn) => {
            btn.addEventListener('click', () => {
                loadLibrary(libraryPage + (btn.getAttribute('data-lib-page') === 'next' ? 1 : -1));
            });
        });
    }

    function loadLibrary(page) {
        grid.innerHTML = '<div class="adm-media-picker__empty"><span class="adm-media-picker__spinner"></span>Loading your images…</div>';
        libraryPagerEl.hidden = true;

        const url = new URL(cfg.restUrl + 'library', window.location.origin);
        url.searchParams.set('page', String(page));
        url.searchParams.set('per_page', '10');
        if (librarySearch) url.searchParams.set('search', librarySearch);
        // Campaign-images Task 1 fix round — a picking caller (onPick set)
        // only ever hands the id straight back to the server as a cover or a
        // hero attachment; the Library tab must not offer a PDF as if it
        // were one. rest_library() already understands 'images' (it filters
        // to the same non-document mime set library_for() uses elsewhere).
        if (picking) { url.search += (url.search ? '&' : '?') + 'kind=images'; }

        fetch(url.toString(), {
            credentials: 'same-origin',
            headers: admRestHeaders(),
        })
            .then((r) => r.json())
            .then((data) => {
                libraryPage = data.page || page;
                libraryTotalPages = Math.max(1, data.total_pages || 1);
                libraryLoaded = true;
                libraryEntries = data.entries || [];
                renderGrid();
            })
            .catch(() => {
                grid.innerHTML = '<div class="adm-media-picker__empty" style="color:#FCA5A5;">Could not load your images. Try again.</div>';
            });
    }

    // When a library image is deleted via the canonical corner X, the global
    // handler removes the thumb DOM node and bubbles acp-card:deleted up to the
    // grid. Prune libraryEntries and re-render so the pager count stays right;
    // an emptied last page falls back to reloading page 1.
    grid.addEventListener('acp-card:deleted', (e) => {
        const url = (e.detail && e.detail.url) || '';
        const mm = url.match(/\/media\/(\d+)/);
        if (!mm) return;
        libraryEntries = libraryEntries.filter((m) => String(m.id) !== mm[1]);
        if (libraryEntries.length === 0 && libraryPage > 1) {
            loadLibrary(libraryPage - 1);
        } else {
            renderGrid();
        }
    });

    search.addEventListener('input', () => {
        clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
            librarySearch = search.value.trim();
            loadLibrary(1);
        }, 250);
    });

    // ── Generate flow (Phase 2a/2b) ─────────────────────────────────────
    if (canGenerate) {
        const genPane = overlay.querySelector('[data-pane="generate"]');
        const genPrompt = genPane.querySelector('.adm-mp-gen__prompt');
        const genStylesGrid = genPane.querySelector('.adm-mp-gen__styles');
        if (genStylesGrid) {
            genStylesGrid.addEventListener('click', function (ev) {
                var btn = ev.target.closest('.adm-mp-gen__style');
                if (!btn || !genStylesGrid.contains(btn)) { return; }
                selectedStyle = btn.getAttribute('data-style') || selectedStyle;
                var cards = genStylesGrid.querySelectorAll('.adm-mp-gen__style');
                for (var i = 0; i < cards.length; i++) {
                    var on = cards[i] === btn;
                    cards[i].classList.toggle('is-selected', on);
                    cards[i].setAttribute('aria-checked', on ? 'true' : 'false');
                }
            });
        }
        // Freeform size control — mirrors the style cards' selection pattern.
        // selectedAspect feeds the generate body and the canvas note below.
        var selectedAspect = '16:9';
        const genAspectRow = genPane.querySelector('.adm-mp-gen__aspect');
        if (genAspectRow) {
            genAspectRow.addEventListener('click', function (ev) {
                var btn = ev.target.closest('.adm-mp-gen__aspect-btn');
                if (!btn || !genAspectRow.contains(btn)) { return; }
                selectedAspect = btn.getAttribute('data-aspect') || selectedAspect;
                var btns = genAspectRow.querySelectorAll('.adm-mp-gen__aspect-btn');
                for (var i = 0; i < btns.length; i++) {
                    var on = btns[i] === btn;
                    btns[i].classList.toggle('is-selected', on);
                    btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
                }
                updateCanvasNote();
            });
        }
        const genSuggestBtn = genPane.querySelector('[data-act="suggest"]');
        const genSuggestFreeNote = genPane.querySelector('[data-a3-suggest-free-note]');
        // Standalone has no draft to write the prompt from — the useVoiceGraphics
        // suggest path reads editor text there is none of. Campaign-cover Task
        // 3.1 — two standalone callers DO have something to ground a prompt on
        // without an editor (canDraftFromContext, set above): keep the button
        // for those, with its from-what label already baked into the markup.
        if (standalone && genSuggestBtn) { genSuggestBtn.hidden = !canDraftFromContext; }
        if (genSuggestFreeNote) { genSuggestFreeNote.hidden = !canDraftFromContext; }
        const genGenerateBtn = genPane.querySelector('[data-act="generate"]');
        const genRegenBtn = genPane.querySelector('[data-act="regenerate"]');
        const genStatus = genPane.querySelector('.adm-mp-gen__status');
        const genProgress = genPane.querySelector('.adm-mp-gen__progress');
        const genProgressBar = genPane.querySelector('.adm-mp-gen__progress-bar');
        const genProgressLabel = genPane.querySelector('.adm-mp-gen__progress-label');
        let genProgressTimer = null;
        let genProgressElapsed = 0;
        const genPreview = genPane.querySelector('.adm-mp-gen__preview');
        const genPreviewImg = genPane.querySelector('.adm-mp-gen__preview-img');
        const genPreviewModel = genPane.querySelector('.adm-mp-gen__preview-model');
        const genPreviewAttempts = genPane.querySelector('.adm-mp-gen__preview-attempts');
        const genPolicyblock = genPane.querySelector('.adm-mp-gen__policyblock');
        const genPolicyblockMsg = genPane.querySelector('.adm-mp-gen__policyblock-msg');
        const genPolicyblockBtn = genPane.querySelector('.adm-mp-gen__policyblock-btn');
        const genCanvasNote = genPane.querySelector('.adm-mp-gen__canvas-note');

        // Canvas note — the hero is generated at the platform's default aspect
        // server-side, so tell the creator the shape/size up front. Mirrors the
        // platform registry's first (default) aspect per platform; freeform Voice
        // generation follows the Size control above (re-run on every change).
        function updateCanvasNote() {
            if (!genCanvasNote) { return; }
            var CANVAS_BY_PLATFORM = {
                instagram: 'a square 1080×1080 Instagram image',
                linkedin:  'a landscape 1200×627 LinkedIn image',
                x:         'a landscape 1200×675 X image',
                substack:  'a 1456×816 cover image',
                blog:      'a 1200×675 cover image',
            };
            var FREEFORM_BY_ASPECT = {
                '16:9': 'a wide landscape 16:9 image',
                '1:1':  'a square 1:1 image',
                '4:3':  'a landscape 4:3 image',
                '3:4':  'a portrait 3:4 image',
                '9:16': 'a tall portrait 9:16 image',
            };
            var desc = (platformSlug && CANVAS_BY_PLATFORM[platformSlug])
                ? CANVAS_BY_PLATFORM[platformSlug]
                : (freeformMode ? (FREEFORM_BY_ASPECT[selectedAspect] || 'a wide landscape 16:9 image') : '');
            if (!desc) { genCanvasNote.hidden = true; return; }
            genCanvasNote.textContent = 'You’re about to generate ' + desc + '.';
            genCanvasNote.hidden = false;
        }
        updateCanvasNote();

        function setGenStatus(msg, isError) {
            if (!msg) { genStatus.hidden = true; genStatus.textContent = ''; return; }
            genStatus.hidden = false;
            genStatus.textContent = msg;
            genStatus.classList.toggle('is-error', !!isError);
        }

        // Cost-warning toast (>= 5 attempts on this slot/session). Server flags
        // it via should_warn_cost; the user has seen the per-attempt counter,
        // this is a one-shot louder reminder so they don't accidentally burn
        // 10 tasks at €0.04 each clicking Regenerate to fine-tune mood.
        // One-line status after a generation, reflecting the cyclic cost model:
        // a charged try buys 2 free retries; free tries count down to the next
        // charge. Reads free_retries_left from the server so the cadence lives
        // in one place (ADAMIRO_Reach_Media::free_retries_left).
        function genChargeNote(payload) {
            if (!payload) return null;
            if (payload.charged_task) return 'Used 1 task · 2 free retries included.';
            var left = (typeof payload.free_retries_left === 'number') ? payload.free_retries_left : null;
            if (left === null) return null;
            if (left <= 0) return 'Free retry · the next image costs 1 task.';
            return 'Free retry · ' + left + ' more free before the next task.';
        }

        function maybeCostWarn(payload) {
            if (!payload || !payload.should_warn_cost) return;
            var msg = 'You\'re ' + (payload.attempts_used || 5) + ' attempts in on this image. Regenerating keeps costing tasks (one per three) — consider editing the prompt or picking from your library.';
            if (typeof window !== 'undefined' && typeof window.acpToast === 'function') {
                window.acpToast(msg, { type: 'warning', duration: 6000 });
            } else {
                setGenStatus(msg, true);
            }
        }

        // Recovery banner shown when Imagen blocked the prompt for
        // content-policy reasons. policySafePrompt is pre-baked server-side
        // by Gemini Flash; clicking the button drops it into the prompt
        // textarea and the user re-clicks Generate.
        function showPolicyBlock(message, policySafePrompt) {
            genPolicyblock.hidden = false;
            genPolicyblockMsg.textContent = message || 'Content policy blocked this prompt.';
            genPolicyblockBtn.dataset.rephrase = policySafePrompt || '';
            genPolicyblockBtn.textContent = 'Rephrase to pass content policy';
        }
        function hidePolicyBlock() {
            genPolicyblock.hidden = true;
            genPolicyblockMsg.textContent = '';
            genPolicyblockBtn.dataset.rephrase = '';
        }
        genPolicyblockBtn.addEventListener('click', function () {
            var rephrase = genPolicyblockBtn.dataset.rephrase || '';
            if (rephrase) {
                genPrompt.value = rephrase;
                hidePolicyBlock();
                setGenStatus('Prompt rephrased — review and click Generate.', false);
                return;
            }
            var current = (genPrompt.value || '').trim();
            if (!current) { hidePolicyBlock(); return; }
            genPolicyblockBtn.disabled = true;
            reachFetch('hero/rephrase-for-policy', { prompt: current }).then(function (resp) {
                genPolicyblockBtn.disabled = false;
                if (!resp.ok || !resp.data || !resp.data.success || !resp.data.policy_safe_prompt) {
                    setGenStatus('Could not rephrase the prompt. Edit it manually.', true);
                    return;
                }
                genPrompt.value = resp.data.policy_safe_prompt;
                hidePolicyBlock();
                setGenStatus('Prompt rephrased — review and click Generate.', false);
            }).catch(function () {
                genPolicyblockBtn.disabled = false;
                setGenStatus('Network error fetching rephrase.', true);
            });
        });

        // Help icon next to the rephrase button — point at whichever
        // dashboard's Guide tab we're floating over (Reach when editing a
        // Reach post, Voice when generating a cover image in the Voice
        // editor). Close the picker first so the Guide is visible after
        // the global help.js handler runs.
        var genPolicyblockHelp = genPane.querySelector('.adm-mp-gen__policyblock-help');
        if (genPolicyblockHelp) {
            genPolicyblockHelp.setAttribute(
                'data-guide',
                freeformMode ? 'voice-guide-policy-rephrase' : 'reach-guide-policy-rephrase'
            );
            genPolicyblockHelp.addEventListener('click', function () {
                closePlacementEditor();
                overlay.remove();
            }, { capture: true });
        }
        // "Image policy" link next to the IMAGE PROMPT label — same
        // contextual routing as the policyblock help icon so the link
        // works whether the picker was opened from Voice or Reach.
        var genPolicyLink = genPane.querySelector('.adm-mp-gen__policy-link');
        if (genPolicyLink) {
            genPolicyLink.setAttribute(
                'data-guide',
                freeformMode ? 'voice-guide-policy-rephrase' : 'reach-guide-policy-rephrase'
            );
            genPolicyLink.addEventListener('click', function (ev) {
                ev.preventDefault();
                closePlacementEditor();
                overlay.remove();
            }, { capture: true });
        }

        function setGenBusy(busy) {
            genState.busy = busy;
            genSuggestBtn.disabled = busy;
            genGenerateBtn.disabled = busy;
            genRegenBtn.disabled = busy;
            genPlaceBtn.disabled = busy;
            genPrompt.disabled = busy;
        }

        // Indeterminate progress with creep. Imagen 3 typically returns in
        // 8-15s; we creep to 88% over 18s, then jump to 100% when the response
        // arrives. If Imagen takes longer the bar holds at 88% rather than
        // misleading the user.
        function startGenProgress(label) {
            genProgress.hidden = false;
            genProgressElapsed = 0;
            genProgressBar.style.transition = 'width 240ms ease';
            genProgressBar.style.width = '6%';
            if (label) genProgressLabel.textContent = label;
            clearInterval(genProgressTimer);
            genProgressTimer = setInterval(function () {
                genProgressElapsed += 250;
                var secs = Math.round(genProgressElapsed / 1000);
                // Easing curve: faster early, slower as we approach 88%.
                var pct = Math.min(88, 6 + (1 - Math.exp(-genProgressElapsed / 6500)) * 82);
                genProgressBar.style.width = pct.toFixed(1) + '%';
                if (label && secs > 0) {
                    genProgressLabel.textContent = label + ' · ' + secs + 's';
                }
            }, 250);
        }
        function finishGenProgress(success) {
            clearInterval(genProgressTimer);
            genProgressTimer = null;
            genProgressBar.style.transition = 'width 220ms ease';
            genProgressBar.style.width = success ? '100%' : '0%';
            if (success) {
                genProgressLabel.textContent = 'Done · ' + Math.round(genProgressElapsed / 1000) + 's';
                setTimeout(function () { genProgress.hidden = true; }, 600);
            } else {
                genProgress.hidden = true;
            }
        }

        function showGenPreview(payload) {
            genPreview.hidden = false;
            // "Place a product" works on the image on show, if it is a saved
            // attachment (every generated image is).
            genPreviewAtt = { id: payload.attachmentId || 0, url: payload.url || '' };
            genPlaceBtn.hidden = !(genPreviewAtt.id > 0);
            genPreviewImg.src = payload.url || '';
            genPreviewModel.textContent = payload.modelUsed || '';
            // Free retries left in this block AFTER the current attempt. Use the
            // server's cyclic value (free_retries_left); fall back to the same
            // cadence (2 − (n−1) mod 3) if absent. NOT attemptsIncluded−attemptsUsed,
            // which counts the initial charged image against the 2 free retries
            // and so wrongly bills the 1st/2nd retry as a task.
            var remaining = (typeof payload.freeRetriesLeft === 'number')
                ? payload.freeRetriesLeft
                : Math.max(0, 2 - (((payload.attemptsUsed || 1) - 1) % 3));
            var costNote = remaining > 0
                ? (remaining === 1 ? '1 free retry left' : remaining + ' free retries left')
                : 'Next regenerate consumes 1 task.';
            genPreviewAttempts.textContent = 'Attempt ' + (payload.attemptsUsed || 1) + ' · ' + costNote;
            // Update Insert button at the footer. Campaign-images Task 1 —
            // carry the real attachment id through (both generate_freeform
            // and generate_hero/regenerate_slot return `attachment_id`) so
            // standalone's Done → onPick(selected) hands back a usable
            // attachment, not a stub with id 0.
            selectAttachment({ url: payload.url, alt: '', id: payload.attachmentId || 0, filename: '', source: 'generated' });
            if (payload.freeform) {
                // No slot → reuse the same Generate button for the next image.
                // (Reach mode uses a separate Regenerate button because the
                // slot keeps the same id across regens.)
                genGenerateBtn.hidden = false;
                genGenerateBtn.textContent = remaining > 0
                    ? 'Generate again'
                    : 'Generate again (1 task)';
                genRegenBtn.hidden = true;
            } else {
                genGenerateBtn.hidden = true;
                genRegenBtn.hidden = false;
            }
        }

        function reachFetch(path, body, method) {
            return fetch(cfg.reachRestUrl + path, {
                method: method || 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body || {}),
            }).then((r) => r.json().then((j) => ({ ok: r.ok, status: r.status, data: j })));
        }

        // Round 4.5 P3 — freeform (no-postId / Voice) graphics call the
        // Voice-namespaced endpoint so generation is gated + accounted under
        // Voice. Reach-post (postId) generation keeps using reachFetch.
        function voiceFetch(path, body, method) {
            return fetch(voiceRestUrl + path, {
                method: method || 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body || {}),
            }).then((r) => r.json().then((j) => ({ ok: r.ok, status: r.status, data: j })));
        }
        // Use the Voice endpoint for freeform graphics when it's configured.
        var useVoiceGraphics = !!(freeformMode && voiceRestUrl);

        // ── Include a product (scene mode) and Place a product ───────────────
        // The company profile's products that have a photo, fetched once per
        // picker open. The Generate tab offers them as one-pick chips; the
        // placement editor gets the same list. One product at most, shot chips
        // only while one is picked, no setting anywhere.
        var genProducts = [];
        var genProductsLoaded = false;
        var selectedProductId = '';
        var selectedShot = '';
        var genPreviewAtt = { id: 0, url: '' };
        var placeFallbackSession = null;
        const genProductRow = genPane.querySelector('.adm-mp-gen__product');
        const genProductChips = genPane.querySelector('.adm-mp-gen__product-chips');
        const genProductEmpty = genPane.querySelector('.adm-mp-gen__product-empty');
        const genShots = genPane.querySelector('.adm-mp-gen__shots');
        const genPlaceBtn = genPane.querySelector('[data-act="place-product"]');

        function syncShotChips() {
            genShots.hidden = !selectedProductId;
            var shotBtns = genShots.querySelectorAll('.adm-mp-gen__shot');
            for (var i = 0; i < shotBtns.length; i++) {
                var on = !!selectedProductId && shotBtns[i].getAttribute('data-shot') === selectedShot;
                shotBtns[i].classList.toggle('is-selected', on);
                shotBtns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
            }
        }
        function renderProductRow() {
            genProductChips.textContent = '';
            // No company page address to point the empty state at, or the
            // fetch has not come back: leave the row out rather than offer a
            // dead link.
            if (!genProductsLoaded || (!genProducts.length && !cfg.companyUrl)) {
                genProductRow.hidden = true;
                return;
            }
            genProductRow.hidden = false;
            genProductEmpty.hidden = genProducts.length > 0;
            if (!genProducts.length) { genProductEmpty.href = cfg.companyUrl; }
            genProducts.forEach(function (p) {
                var chip = document.createElement('button');
                chip.type = 'button';
                chip.className = 'adm-mp-gen__aspect-btn adm-mp-gen__product-chip' + (p.id === selectedProductId ? ' is-selected' : '');
                chip.setAttribute('data-product-id', p.id);
                chip.setAttribute('aria-pressed', p.id === selectedProductId ? 'true' : 'false');
                var thumb = document.createElement('img');
                thumb.className = 'adm-mp-gen__product-thumb';
                thumb.alt = '';
                thumb.src = p.thumb_url || '';
                var label = document.createElement('span');
                label.textContent = p.name || 'Product';
                chip.appendChild(thumb);
                chip.appendChild(label);
                genProductChips.appendChild(chip);
            });
            syncShotChips();
        }
        genProductChips.addEventListener('click', function (ev) {
            var chip = ev.target.closest('.adm-mp-gen__product-chip');
            if (!chip || !genProductChips.contains(chip)) { return; }
            var id = chip.getAttribute('data-product-id') || '';
            // A second click on the picked product lets go of it.
            selectedProductId = (selectedProductId === id) ? '' : id;
            if (!selectedProductId) { selectedShot = ''; }
            var chipEls = genProductChips.querySelectorAll('.adm-mp-gen__product-chip');
            for (var i = 0; i < chipEls.length; i++) {
                var on = chipEls[i].getAttribute('data-product-id') === selectedProductId;
                chipEls[i].classList.toggle('is-selected', on);
                chipEls[i].setAttribute('aria-pressed', on ? 'true' : 'false');
            }
            syncShotChips();
        });
        genShots.addEventListener('click', function (ev) {
            var btn = ev.target.closest('.adm-mp-gen__shot');
            if (!btn || !genShots.contains(btn)) { return; }
            var shot = btn.getAttribute('data-shot') || '';
            selectedShot = (selectedShot === shot) ? '' : shot;
            syncShotChips();
        });
        // product_id + shot ride on every generate and regenerate call while a
        // product is picked; with none picked the body is exactly what it was.
        function withProduct(body) {
            if (selectedProductId) {
                body.product_id = selectedProductId;
                body.shot = selectedShot;
            }
            return body;
        }
        function loadGenProducts() {
            return fetch(cfg.reachRestUrl + 'products', {
                credentials: 'same-origin',
                headers: admRestHeaders(),
            }).then(function (r) {
                return r.json().then(function (j) { return { ok: r.ok, data: j }; });
            }).then(function (resp) {
                if (!resp.ok || !Array.isArray(resp.data)) { return; }
                genProducts = resp.data.filter(function (p) { return p && p.id; });
                genProductsLoaded = true;
                renderProductRow();
            }).catch(function () { /* the row simply stays out */ });
        }
        // Resolves once the list has come back (or failed), never rejects: the
        // Place buttons wait on it so a press in the first moments does not show
        // the empty state to an account that has product photos.
        var genProductsReady = Promise.resolve();
        if (cfg.reachRestUrl) { genProductsReady = loadGenProducts(); }

        // Placement shares the Generate tab's session counter, so a placement
        // is charged like the next image of the session. A post-bound hero has
        // no session of its own; a placement there gets one for the picker's life.
        function placementSessionId() {
            if (genState.sessionId) { return genState.sessionId; }
            if (!placeFallbackSession) {
                placeFallbackSession = (window.crypto && typeof window.crypto.randomUUID === 'function')
                    ? window.crypto.randomUUID()
                    : 'gs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
            }
            return placeFallbackSession;
        }
        function onProductPlaced(data) {
            // The picker was closed while the request ran (the placement
            // editor cannot be closed by the user while busy, but the backdrop
            // click can remove the whole picker): nothing to show it in.
            if (!overlay.isConnected) { return; }
            // Freeform has no slot. A post-bound picker keeps its slot, so the
            // preview keeps Regenerate; "Generate again" there would run the free
            // initial-hero route.
            if (freeformMode) { genState.slotId = null; }
            genState.imageUrl = data.url || '';
            genState.modelUsed = data.model_used || '';
            genState.attemptsUsed = data.attempts_used || (genState.attemptsUsed + 1);
            genState.attemptsIncluded = data.attempts_included || 2;
            genState.freeRetriesLeft = (typeof data.free_retries_left === 'number')
                ? data.free_retries_left : genState.freeRetriesLeft;
            // The result is shown where every generated image is shown, so
            // "Place a product" can run again on it.
            var genTabBtn = overlay.querySelector('.adm-media-picker__tabs .acp-tab-btn[data-tab="generate"]');
            if (genTabBtn && activeTab !== 'generate') { genTabBtn.click(); }
            hidePolicyBlock();
            showGenPreview({
                url: genState.imageUrl,
                modelUsed: genState.modelUsed,
                attemptsUsed: genState.attemptsUsed,
                attemptsIncluded: genState.attemptsIncluded,
                freeRetriesLeft: genState.freeRetriesLeft,
                freeform: freeformMode,
                attachmentId: data.attachment_id || 0,
            });
            setGenStatus(standalone ? 'Saved to your library.' : genChargeNote(data), false);
            maybeCostWarn(data);
        }
        placeProductOn = function (attachmentId, imageUrl) {
            if (!attachmentId || !imageUrl || genState.busy) { return; }
            genProductsReady.then(function () {
                // The picker may have been closed, or a generation started, while
                // the list was loading.
                if (!overlay.isConnected || genState.busy) { return; }
                closePlacementEditor();
                placementApi = openProductPlacement({
                    imageUrl: imageUrl,
                    attachmentId: attachmentId,
                    products: genProducts,
                    fetchFn: reachFetch,
                    sessionId: placementSessionId(),
                    draftId: mountOpts.voiceDraftId || 0,
                    host: overlay.querySelector('.adm-media-picker__card'),
                    companyUrl: cfg.companyUrl || '',
                    onPlaced: onProductPlaced,
                });
            });
        };
        genPlaceBtn.addEventListener('click', function () {
            placeProductOn(genPreviewAtt.id, genPreviewAtt.url);
        });

        // Plain-text extract of the current editor body for the suggest call.
        // Unreachable in standalone (genSuggestBtn is hidden, above) but
        // guarded anyway — there is no editor instance to read from there.
        function currentEditorPlainText() {
            if (!editor) return '';
            try {
                if (typeof editor.getText === 'function') return editor.getText();
                return (editor.getHTML() || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            } catch (e) { return ''; }
        }

        genSuggestBtn.addEventListener('click', function () {
            var text = '';
            // Campaign-cover Task 3.1 — the two standalone contexts (A: a
            // Reach post, B: a campaign) ground the prompt server-side and
            // send no post_text at all, so they skip the editor-text read
            // (and its 12-char floor) entirely rather than failing it.
            if (!canDraftFromContext) {
                text = currentEditorPlainText();
                if (!text || text.length < 12) {
                    setGenStatus('Write at least a sentence of body text before suggesting an image prompt.', true);
                    return;
                }
            }
            setGenStatus(canDraftFromContext ? 'Drafting a prompt…' : 'Drafting a prompt from your draft…', false);
            setGenBusy(true);
            // Case B (campaignId, no postId) always uses the Reach freeform
            // suggest route — the campaign it grounds on lives in Reach's own
            // campaigns table, never Voice's, so this overrides useVoiceGraphics
            // even when a voiceRestUrl is configured on the page.
            var suggestForCampaign = canDraftFromContext && campaignId > 0 && !postId;
            var suggestPath = suggestForCampaign
                ? 'hero/suggest-prompt-freeform'
                : (useVoiceGraphics
                    ? 'graphics/image/suggest-prompt'
                    : (freeformMode
                        ? 'hero/suggest-prompt-freeform'
                        : ('posts/' + postId + '/platforms/' + (platformSlug || 'linkedin') + '/hero/suggest-prompt')));
            var suggestFetchFn = suggestForCampaign ? reachFetch : (useVoiceGraphics ? voiceFetch : reachFetch);
            var suggestBody;
            if (canDraftFromContext) {
                suggestBody = { style: selectedStyle };
                if (suggestForCampaign) { suggestBody.campaign_id = campaignId; }
            } else {
                suggestBody = freeformMode
                    ? { post_text: text, platform: 'blog', style: selectedStyle }
                    : { post_text: text, style: selectedStyle };
            }
            // Voice draft id — lets the suggest call pull the same recent-covers
            // memory the post itself will later draw from, so the drafted prompt
            // already avoids what this draft's account has shown recently.
            if (mountOpts.voiceDraftId) {
                suggestBody.draft_id = mountOpts.voiceDraftId;
            }
            suggestFetchFn(suggestPath, suggestBody).then(function (resp) {
                setGenBusy(false);
                if (!resp.ok || !resp.data || !resp.data.prompt) {
                    setGenStatus((resp.data && resp.data.message) || 'Could not draft a prompt. Try writing one yourself.', true);
                    return;
                }
                genPrompt.value = resp.data.prompt;
                setGenStatus(null);
            }).catch(function () {
                setGenBusy(false);
                setGenStatus('Network error drafting prompt.', true);
            });
        });

        genGenerateBtn.addEventListener('click', function () {
            var prompt = (genPrompt.value || '').trim();
            if (!prompt) {
                setGenStatus('Write or suggest a prompt first.', true);
                return;
            }
            hidePolicyBlock();
            setGenStatus(null);
            setGenBusy(true);
            startGenProgress('Generating image');
            var genPath = useVoiceGraphics
                ? 'graphics/image'
                : (freeformMode
                    ? 'hero/generate-freeform'
                    : ('posts/' + postId + '/hero/generate'));
            var genBody = freeformMode
                ? { prompt: prompt, aspect: selectedAspect, session_id: genState.sessionId, style: selectedStyle }
                : { prompt: prompt, platforms: genState.platforms, style: selectedStyle };
            withProduct(genBody);
            // Voice draft id — lets the seats pricing model tell this draft's
            // one included image from an extra one (spec §3.3, Q7). Tiers
            // mode ignores it; the field is simply absent for a Reach hero
            // (freeformMode without useVoiceGraphics never sets voiceDraftId).
            if (useVoiceGraphics && mountOpts.voiceDraftId) {
                genBody.draft_id = mountOpts.voiceDraftId;
            }
            (useVoiceGraphics ? voiceFetch : reachFetch)(genPath, genBody).then(function (resp) {
                setGenBusy(false);
                if (resp.data && resp.data.prompt_blocked) {
                    finishGenProgress(false);
                    showPolicyBlock(resp.data.message, resp.data.policy_safe_prompt);
                    return;
                }
                if (!resp.ok || !resp.data || !resp.data.success) {
                    finishGenProgress(false);
                    setGenStatus((resp.data && resp.data.message) || 'Image generation failed.', true);
                    return;
                }
                if (freeformMode) {
                    genState.slotId = null;
                    genState.imageUrl = resp.data.url || '';
                    genState.modelUsed = resp.data.model_used || '';
                    genState.attemptsUsed = resp.data.attempts_used || (genState.attemptsUsed + 1);
                    genState.attemptsIncluded = resp.data.attempts_included || 2;
                    genState.freeRetriesLeft = (typeof resp.data.free_retries_left === 'number')
                        ? resp.data.free_retries_left : genState.freeRetriesLeft;
                } else {
                    var slots = resp.data.slots || {};
                    var urls = resp.data.urls || {};
                    var firstPlatform = genState.platforms[0];
                    genState.slotId = slots[firstPlatform] || null;
                    genState.imageUrl = urls[firstPlatform] || resp.data.source_url || '';
                    genState.modelUsed = resp.data.model_used || '';
                    genState.attemptsUsed = 1;
                    genState.attemptsIncluded = 2;
                    genState.freeRetriesLeft = (typeof resp.data.free_retries_left === 'number')
                        ? resp.data.free_retries_left : 2;
                }
                finishGenProgress(true);
                // Standalone (library "Generate an image") has no doc to insert
                // into — the freeform generate call already archived the file,
                // so tell the creator that plainly instead of the per-attempt
                // charge note (still shown via the cost-warning toast below).
                setGenStatus(standalone ? 'Saved to your library.' : genChargeNote(resp.data), false);
                showGenPreview({
                    url: genState.imageUrl,
                    modelUsed: genState.modelUsed,
                    attemptsUsed: genState.attemptsUsed,
                    attemptsIncluded: genState.attemptsIncluded,
                    freeRetriesLeft: genState.freeRetriesLeft,
                    freeform: freeformMode,
                    attachmentId: (resp.data && resp.data.attachment_id) || 0,
                });
                maybeCostWarn(resp.data);
            }).catch(function () {
                setGenBusy(false);
                finishGenProgress(false);
                setGenStatus('Network error generating image.', true);
            });
        });

        genRegenBtn.addEventListener('click', function () {
            var prompt = (genPrompt.value || '').trim();
            if (!prompt) {
                setGenStatus('Prompt is required.', true);
                return;
            }
            if (!genState.slotId) {
                setGenStatus('No slot — run Generate first.', true);
                return;
            }
            hidePolicyBlock();
            setGenStatus(null);
            setGenBusy(true);
            startGenProgress('Regenerating');
            reachFetch(
                'hero/slots/' + genState.slotId + '/regenerate',
                withProduct({ prompt: prompt, style: selectedStyle })
            ).then(function (resp) {
                setGenBusy(false);
                if (resp.data && resp.data.prompt_blocked) {
                    finishGenProgress(false);
                    showPolicyBlock(resp.data.message, resp.data.policy_safe_prompt);
                    return;
                }
                if (!resp.ok || !resp.data || !resp.data.success) {
                    finishGenProgress(false);
                    setGenStatus((resp.data && resp.data.message) || 'Regenerate failed.', true);
                    return;
                }
                genState.imageUrl = resp.data.url || genState.imageUrl;
                genState.attemptsUsed = resp.data.attempts_used || (genState.attemptsUsed + 1);
                genState.attemptsIncluded = resp.data.attempts_included || genState.attemptsIncluded;
                genState.freeRetriesLeft = (typeof resp.data.free_retries_left === 'number')
                    ? resp.data.free_retries_left : genState.freeRetriesLeft;
                genState.modelUsed = resp.data.model_used || genState.modelUsed;
                var note = genChargeNote(resp.data);
                finishGenProgress(true);
                showGenPreview({
                    url: genState.imageUrl,
                    modelUsed: genState.modelUsed,
                    attemptsUsed: genState.attemptsUsed,
                    attemptsIncluded: genState.attemptsIncluded,
                    freeRetriesLeft: genState.freeRetriesLeft,
                    attachmentId: (resp.data && resp.data.attachment_id) || 0,
                });
                setGenStatus(note, false);
                maybeCostWarn(resp.data);
            }).catch(function () {
                setGenBusy(false);
                finishGenProgress(false);
                setGenStatus('Network error regenerating.', true);
            });
        });
    }

    function escHtml(s) {
        return String(s || '').replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }
    function escAttr(s) { return escHtml(s); }

    // Sub-picker overlay used by image-kind slots (Templates pane, Infographic
    // photo slots). Re-uses the canonical /media/library + /media/upload REST
    // endpoints so the user sees exactly the same library as the main image
    // picker. Overlay layers OVER the existing modal — same z-index discipline
    // as the main picker (just one notch higher so it actually overlays).
    // Hoisted to this top-level, unconditional scope (2026-07-09) — it used to
    // live inside the `if (canGenerate)` Templates block, which is a sibling
    // block (not an ancestor) of the Infographic block below; a block-scoped
    // function declaration there is invisible to sibling blocks in strict
    // mode, so calling it from the Infographic photo-slot picker threw
    // "openImageSubPicker is not defined" — reported 2026-07-09.
    function openImageSubPicker(onPick) {
        var sub = document.createElement('div');
        sub.className = 'adm-mp-subpicker';
        sub.innerHTML =
            '<div class="adm-mp-subpicker__card" role="dialog" aria-modal="true" aria-label="Choose an image">' +
                '<div class="adm-mp-subpicker__head">' +
                    '<strong class="adm-mp-subpicker__title">Choose an image</strong>' +
                    '<button type="button" class="adm-mp-subpicker__close" aria-label="Close">×</button>' +
                '</div>' +
                '<div class="adm-mp-subpicker__toolbar">' +
                    '<input type="search" class="adm-mp-subpicker__search" placeholder="Search your library…" aria-label="Search images">' +
                    '<button type="button" class="adm-mp-subpicker__upload-btn">Upload new</button>' +
                    '<input type="file" class="adm-mp-subpicker__file" accept="image/jpeg,image/png,image/gif,image/webp" hidden>' +
                '</div>' +
                '<div class="adm-mp-subpicker__grid" role="listbox" aria-label="Your images"></div>' +
                '<button type="button" class="adm-mp-subpicker__more" hidden>Load more</button>' +
                '<div class="adm-mp-subpicker__status" hidden></div>' +
            '</div>';
        overlay.appendChild(sub);

        var gridEl = sub.querySelector('.adm-mp-subpicker__grid');
        var moreEl = sub.querySelector('.adm-mp-subpicker__more');
        var statusEl = sub.querySelector('.adm-mp-subpicker__status');
        var closeBtn = sub.querySelector('.adm-mp-subpicker__close');
        var searchEl = sub.querySelector('.adm-mp-subpicker__search');
        var uploadBtn = sub.querySelector('.adm-mp-subpicker__upload-btn');
        var fileInput = sub.querySelector('.adm-mp-subpicker__file');
        var subPage = 1, subHasMore = false, subQ = '', subLoading = false, subDebounce = null;

        function setSubStatus(msg, isError) {
            if (!msg) { statusEl.hidden = true; statusEl.textContent = ''; return; }
            statusEl.hidden = false;
            statusEl.textContent = msg;
            statusEl.classList.toggle('is-error', !!isError);
        }
        function closeSub() {
            document.removeEventListener('keydown', onSubKey);
            sub.remove();
        }
        function onSubKey(ev) {
            if (ev.key === 'Escape') { ev.preventDefault(); closeSub(); }
        }
        closeBtn.addEventListener('click', closeSub);
        sub.addEventListener('click', function (ev) {
            if (ev.target === sub) closeSub();
        });
        document.addEventListener('keydown', onSubKey);

        function loadSubLibrary(page, replace) {
            if (subLoading) return;
            subLoading = true;
            if (replace) gridEl.innerHTML = '<div class="adm-mp-subpicker__empty"><span class="adm-media-picker__spinner"></span>Loading your images…</div>';
            moreEl.disabled = true;
            moreEl.textContent = page === 1 ? 'Loading…' : 'Loading more…';

            var url = new URL(cfg.restUrl + 'library', window.location.origin);
            url.searchParams.set('page', String(page));
            url.searchParams.set('per_page', '24');
            if (subQ) url.searchParams.set('search', subQ);

            fetch(url.toString(), {
                credentials: 'same-origin',
                headers: admRestHeaders(),
            }).then(function (r) { return r.json(); })
              .then(function (data) {
                  subLoading = false;
                  subPage = page;
                  subHasMore = data.page < data.total_pages;
                  if (page === 1) gridEl.innerHTML = '';
                  var entries = data.entries || [];
                  if (!entries.length && page === 1) {
                      gridEl.innerHTML = '<div class="adm-mp-subpicker__empty">No images yet. Upload one to get started.</div>';
                  }
                  entries.forEach(function (entry) {
                      var t = document.createElement('button');
                      t.type = 'button';
                      t.className = 'adm-mp-subpicker__thumb';
                      t.setAttribute('aria-label', entry.filename || 'Image ' + entry.id);
                      t.innerHTML = '<img src="' + escAttr(entry.thumb_url || entry.url) + '" alt="" loading="lazy">';
                      t.addEventListener('click', function () {
                          onPick(entry);
                          closeSub();
                      });
                      gridEl.appendChild(t);
                  });
                  moreEl.hidden = !subHasMore;
                  moreEl.disabled = false;
                  moreEl.textContent = 'Load more';
              })
              .catch(function () {
                  subLoading = false;
                  gridEl.innerHTML = '<div class="adm-mp-subpicker__empty is-error">Could not load your images.</div>';
                  moreEl.disabled = false;
              });
        }

        searchEl.addEventListener('input', function () {
            clearTimeout(subDebounce);
            subDebounce = setTimeout(function () {
                subQ = searchEl.value.trim();
                loadSubLibrary(1, true);
            }, 250);
        });
        moreEl.addEventListener('click', function () { loadSubLibrary(subPage + 1, false); });

        uploadBtn.addEventListener('click', function () { fileInput.click(); });
        fileInput.addEventListener('change', function () {
            var file = fileInput.files && fileInput.files[0];
            if (!file) return;
            var ALLOWED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!ALLOWED.includes(file.type)) { setSubStatus('Only JPEG/PNG/GIF/WebP.', true); return; }
            if (file.size > 50 * 1024 * 1024) { setSubStatus('File too large (max 50 MB).', true); return; }
            uploadBtn.disabled = true;
            uploadBtn.textContent = 'Uploading…';
            setSubStatus(null);
            var fd = new FormData();
            fd.append('file', file);
            var xhr = new XMLHttpRequest();
            xhr.open('POST', cfg.restUrl + 'upload');
            admSetRestHeaders(xhr);
            xhr.onload = function () {
                uploadBtn.disabled = false;
                uploadBtn.textContent = 'Upload new';
                var data = null;
                try { data = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ }
                if (xhr.status >= 200 && xhr.status < 300 && data && data.url) {
                    onPick(data);
                    closeSub();
                } else {
                    setSubStatus((data && (data.message || data.error)) || 'Upload failed.', true);
                }
            };
            xhr.onerror = function () {
                uploadBtn.disabled = false;
                uploadBtn.textContent = 'Upload new';
                setSubStatus('Network error.', true);
            };
            xhr.send(fd);
        });

        loadSubLibrary(1, true);
        setTimeout(function () { searchEl.focus(); }, 0);
    }

    // ── Templates flow (Phase 2e) ────────────────────────────────────────
    if (canGenerate) {
        var tplBase = (cfg.reachRestUrl || '') + 'templates';
        var tplPane = overlay.querySelector('[data-pane="templates"]');
        var tplListEl = tplPane && tplPane.querySelector('.adm-mp-car__tpl-grid');
        var tplHintEl = tplPane && tplPane.querySelector('.adm-mp-tpl__hint--tmpl');
        var tplSlotsWrap = tplPane && tplPane.querySelector('.adm-mp-tpl__slots-wrap');
        var tplSlotsEl = tplPane && tplPane.querySelector('.adm-mp-tpl__slots');
        var tplStatusEl = tplPane && tplPane.querySelector('.adm-mp-tpl__status');
        var tplProgress = tplPane && tplPane.querySelector('.adm-mp-tpl__progress');
        var tplPreview = tplPane && tplPane.querySelector('.adm-mp-tpl__preview');
        var tplPreviewImg = tplPane && tplPane.querySelector('.adm-mp-tpl__preview-img');
        var tplCloneBtn = tplPane && tplPane.querySelector('[data-act="tpl-clone"]');
        var tplDeleteBtn = tplPane && tplPane.querySelector('[data-act="tpl-delete"]');
        var tplRenderBtn = tplPane && tplPane.querySelector('[data-act="tpl-render"]');
        var tplState = {
            loaded: false,
            originals: [],
            clones: [],
            planCap: 0,
            current: null,
            selectedId: null,
            busy: false,
            page: 0,
        };
        // 10 — same page size, same fixed 5-column grid, same card component
        // as the Infographic/Carousel layout grids (INF_TPL_PAGE_SIZE /
        // CAR_TPL_PAGE_SIZE). All three tabs share identical paging UI/UX.
        var TPL_PAGE_SIZE = 10;

        // Batch-2 (2026-07-10) — null/'' optimal_output_kind = generic,
        // always shown. 'instagram_*' only shows once the creator has
        // selected Instagram (Reach-post platform tab, or the freeform
        // selector above). Mirrors carTemplatesForPlatform()'s convention
        // but isn't symmetric with it: Templates has a generic tier that
        // carousel doesn't, so LinkedIn/unset never hides the original 6.
        function tplTemplatesForPlatform(list) {
            var plat = (platformSlug || '').toLowerCase();
            return (list || []).filter(function (t) {
                var kind = String(t.optimal_output_kind || '');
                if (!kind) return true;
                return plat === 'instagram' && kind.indexOf('instagram') === 0;
            });
        }

        function setTplStatus(msg, isError) {
            if (!tplStatusEl) return;
            if (!msg) { tplStatusEl.hidden = true; tplStatusEl.textContent = ''; return; }
            tplStatusEl.hidden = false;
            tplStatusEl.textContent = msg;
            tplStatusEl.classList.toggle('is-error', !!isError);
        }
        function setTplBusy(b) {
            tplState.busy = !!b;
            if (tplRenderBtn) tplRenderBtn.disabled = !!b;
            if (tplCloneBtn) tplCloneBtn.disabled = !!b;
            if (tplDeleteBtn) tplDeleteBtn.disabled = !!b;
        }

        function loadTemplates() {
            tplListEl.innerHTML = '<div class="adm-mp-tpl__empty"><span class="adm-media-picker__spinner"></span>Loading templates…</div>';
            fetch(tplBase, { credentials: 'same-origin', headers: admRestHeaders() })
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    if (!data || !data.success) {
                        tplListEl.innerHTML = '<div class="adm-mp-tpl__empty is-error">Could not load templates.</div>';
                        return;
                    }
                    tplState.originals = data.originals || [];
                    tplState.clones = data.clones || [];
                    tplState.planCap = data.plan_cap || 0;
                    tplState.loaded = true;
                    renderTemplateGrid();
                })
                .catch(function () {
                    tplListEl.innerHTML = '<div class="adm-mp-tpl__empty is-error">Network error.</div>';
                });
        }

        function renderTemplateGrid() {
            var all = tplTemplatesForPlatform(
                (tplState.clones || []).map(function (c) { return Object.assign({}, c, { kind: 'clone' }); })
                    .concat((tplState.originals || []).map(function (o) { return Object.assign({}, o, { kind: 'original' }); }))
            );
            var tplPager = tplListEl.parentElement && tplListEl.parentElement.querySelector('.adm-mp-tpl__pager');
            if (!all.length) {
                tplListEl.innerHTML = '<div class="adm-mp-tpl__empty">No templates available.</div>';
                if (tplPager) tplPager.hidden = true;
                return;
            }
            var tplPages = Math.max(1, Math.ceil(all.length / TPL_PAGE_SIZE));
            tplState.page = Math.min(Math.max(0, tplState.page || 0), tplPages - 1);
            // Keep the selected template visible: jump to its page (same
            // pattern as the Infographic tab's renderInfTplGrid).
            if (tplState.selectedId) {
                all.some(function (t, i) {
                    if (String(t.id) === String(tplState.selectedId)) {
                        tplState.page = Math.floor(i / TPL_PAGE_SIZE);
                        return true;
                    }
                    return false;
                });
            }
            tplListEl.innerHTML = '';
            all.slice(tplState.page * TPL_PAGE_SIZE, (tplState.page + 1) * TPL_PAGE_SIZE).forEach(function (tpl) {
                var card = document.createElement('button');
                card.type = 'button';
                card.className = 'adm-mp-car__tpl-card' + (tpl.kind === 'clone' ? ' is-clone' : '');
                card.setAttribute('data-id', String(tpl.id));
                var thumbHtml = tpl.preview_url
                    ? '<div class="adm-mp-car__tpl-thumb"><img src="' + escAttr(tpl.preview_url) + '" alt="' + escAttr(tpl.name) + '" loading="lazy"></div>'
                    : '<div class="adm-mp-car__tpl-thumb adm-mp-car__tpl-thumb--placeholder">'
                        + '<svg viewBox="0 0 32 20" width="44" aria-hidden="true"><rect x="1" y="1" width="30" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.2" opacity=".5"/><path d="M5 14l5-5 4 4 5-6 8 7" fill="none" stroke="currentColor" stroke-width="1.2" opacity=".5"/></svg>'
                        + '</div>';
                card.innerHTML =
                    thumbHtml +
                    '<div class="adm-mp-car__tpl-body">' +
                        '<div class="adm-mp-car__tpl-name">' + escHtml(tpl.name) + '</div>' +
                        '<div class="adm-mp-car__tpl-range">' + (tpl.kind === 'clone' ? 'Your copy' : 'Adamiro original') + '</div>' +
                    '</div>';
                card.classList.toggle('is-selected', String(tpl.id) === String(tplState.selectedId));
                card.addEventListener('click', function () { selectTemplate(tpl); });
                tplListEl.appendChild(card);
            });
            // Pager row lives right after the grid, created once.
            if (!tplPager && tplListEl.parentElement) {
                tplPager = document.createElement('div');
                tplPager.className = 'adm-mp-tpl__pager';
                tplListEl.insertAdjacentElement('afterend', tplPager);
            }
            if (tplPager) {
                if (tplPages <= 1) {
                    tplPager.hidden = true;
                } else {
                    tplPager.hidden = false;
                    tplPager.innerHTML =
                        '<button type="button" class="adm-mp-gen__suggest" data-tpl-page="prev"' + (tplState.page === 0 ? ' disabled' : '') + '>&#8592; Prev</button>' +
                        '<span class="adm-mp-tpl__pager-count">' + (tplState.page + 1) + ' / ' + tplPages + '</span>' +
                        '<button type="button" class="adm-mp-gen__suggest" data-tpl-page="next"' + (tplState.page >= tplPages - 1 ? ' disabled' : '') + '>Next &#8594;</button>';
                    tplPager.querySelectorAll('[data-tpl-page]').forEach(function (btn) {
                        btn.addEventListener('click', function () {
                            tplState.page += (btn.getAttribute('data-tpl-page') === 'next') ? 1 : -1;
                            // Paging must not re-trigger the "snap to selected
                            // page" pull — render from the new page directly.
                            var keep = tplState.selectedId;
                            tplState.selectedId = null;
                            renderTemplateGrid();
                            tplState.selectedId = keep;
                            tplListEl.querySelectorAll('.adm-mp-car__tpl-card').forEach(function (c) {
                                c.classList.toggle('is-selected', c.getAttribute('data-id') === String(keep));
                            });
                        });
                    });
                }
            }
        }

        function selectTemplate(tpl) {
            tplState.current = tpl;
            tplState.selectedId = tpl.id;
            renderTemplateGrid();
            if (tplHintEl) tplHintEl.textContent = tpl.description || '';
            tplPreview.hidden = true;
            tplPreviewImg.removeAttribute('src');
            setTplStatus(null);
            // Build slot inputs.
            tplSlotsEl.innerHTML = '';
            (tpl.slots || []).forEach(function (slot) {
                var wrap = document.createElement('div');
                wrap.className = 'adm-mp-tpl__slot';
                var lbl = document.createElement('label');
                lbl.className = 'adm-mp-tpl__slot-label';
                lbl.textContent = slot.label || slot.key;
                wrap.appendChild(lbl);
                if (slot.kind === 'image') {
                    // Image slots use a library/upload chooser instead of a
                    // bare URL field — typing a long media URL by hand is
                    // friction we don't need.
                    var chooser = document.createElement('div');
                    chooser.className = 'adm-mp-tpl__image-chooser';
                    var thumbBox = document.createElement('div');
                    thumbBox.className = 'adm-mp-tpl__image-thumb is-empty';
                    thumbBox.innerHTML = '<span class="adm-mp-tpl__image-thumb-empty">No image selected</span>';
                    var hidden = document.createElement('input');
                    hidden.type = 'hidden';
                    hidden.className = 'adm-mp-tpl__slot-input';
                    hidden.setAttribute('data-slot-key', slot.key);
                    hidden.value = '';
                    var actions = document.createElement('div');
                    actions.className = 'adm-mp-tpl__image-actions';
                    var chooseBtn = document.createElement('button');
                    chooseBtn.type = 'button';
                    chooseBtn.className = 'adm-mp-tpl__image-choose';
                    chooseBtn.textContent = 'Choose image';
                    var clearBtn = document.createElement('button');
                    clearBtn.type = 'button';
                    clearBtn.className = 'adm-mp-tpl__image-clear';
                    clearBtn.textContent = 'Clear';
                    clearBtn.hidden = true;
                    chooseBtn.addEventListener('click', function () {
                        openImageSubPicker(function (att) {
                            hidden.value = att.url;
                            thumbBox.classList.remove('is-empty');
                            thumbBox.innerHTML = '<img src="' + escAttr(att.url) + '" alt="">';
                            chooseBtn.textContent = 'Change image';
                            clearBtn.hidden = false;
                        });
                    });
                    clearBtn.addEventListener('click', function () {
                        hidden.value = '';
                        thumbBox.classList.add('is-empty');
                        thumbBox.innerHTML = '<span class="adm-mp-tpl__image-thumb-empty">No image selected</span>';
                        chooseBtn.textContent = 'Choose image';
                        clearBtn.hidden = true;
                    });
                    actions.appendChild(chooseBtn);
                    actions.appendChild(clearBtn);
                    chooser.appendChild(thumbBox);
                    chooser.appendChild(actions);
                    chooser.appendChild(hidden);
                    wrap.appendChild(chooser);
                } else {
                    var input;
                    if (slot.multiline) {
                        input = document.createElement('textarea');
                        input.rows = 3;
                    } else {
                        input = document.createElement('input');
                        input.type = 'text';
                    }
                    input.className = 'adm-mp-tpl__slot-input';
                    input.setAttribute('data-slot-key', slot.key);
                    if (slot.max_len) input.setAttribute('maxlength', String(slot.max_len));
                    wrap.appendChild(input);
                }
                tplSlotsEl.appendChild(wrap);
            });
            tplCloneBtn.hidden = tpl.kind !== 'original' || tplState.planCap <= 0;
            tplDeleteBtn.hidden = tpl.kind !== 'clone';
            tplSlotsWrap.hidden = false;
        }

        function deselectTemplate() {
            tplState.current = null;
            tplState.selectedId = null;
            tplSlotsWrap.hidden = true;
            if (tplHintEl) tplHintEl.textContent = '';
            setTplStatus(null);
            renderTemplateGrid();
        }

        function gatherSlotValues() {
            var out = {};
            tplSlotsEl.querySelectorAll('[data-slot-key]').forEach(function (el) {
                out[el.getAttribute('data-slot-key')] = el.value || '';
            });
            return out;
        }

        function renderTemplate() {
            if (!tplState.current || tplState.busy) return;
            var tpl = tplState.current;
            setTplBusy(true);
            setTplStatus(null);
            tplProgress.hidden = false;
            var slots = gatherSlotValues();
            // Require at least one non-empty required slot before charging.
            var missing = (tpl.slots || []).filter(function (s) { return s.required && !String(slots[s.key] || '').trim(); });
            if (missing.length) {
                tplProgress.hidden = true;
                setTplBusy(false);
                setTplStatus('Fill in: ' + missing.map(function (s) { return s.label || s.key; }).join(', '), true);
                return;
            }
            var payload = {
                slots: slots,
                post_id: postId || 0,
                attach_as_hero: postId ? true : false,
            };
            fetch(tplBase + '/' + tpl.id + '/render', {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload),
            }).then(function (r) { return r.json().then(function (d) { return { status: r.status, data: d }; }); })
              .then(function (resp) {
                  tplProgress.hidden = true;
                  setTplBusy(false);
                  if (!resp.data || !resp.data.success) {
                      setTplStatus((resp.data && resp.data.message) || ('Render failed (HTTP ' + resp.status + ').'), true);
                      return;
                  }
                  tplPreviewImg.src = resp.data.url;
                  tplPreview.hidden = false;
                  // Select the rendered image so Insert button activates.
                  // Keep whatever alt is already in the field — the user's text
                  // if they typed one, otherwise the post-title default. The
                  // template name ("CTA card") is a UI label, not an image
                  // description, so it's only a last-resort fallback when the
                  // field is genuinely empty. Never let it override existing alt.
                  selectAttachment({
                      id: resp.data.attachment_id,
                      url: resp.data.url,
                      thumb_url: resp.data.url,
                      alt: (altInput.value.trim() || contextAlt() || tpl.name),
                      width: resp.data.width,
                      height: resp.data.height,
                      filename: 'template-' + tpl.id + '.png',
                  });
                  setTplStatus(resp.data.charged_task ? 'Rendered. Used 1 task.' : 'Rendered.', false);
              })
              .catch(function () {
                  tplProgress.hidden = true;
                  setTplBusy(false);
                  setTplStatus('Network error rendering template.', true);
              });
        }

        function cloneTemplate() {
            if (!tplState.current || tplState.current.kind !== 'original') return;
            setTplBusy(true);
            fetch(tplBase + '/' + tplState.current.id + '/clone', {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
            }).then(function (r) { return r.json(); })
              .then(function (data) {
                  setTplBusy(false);
                  if (!data || !data.success) {
                      setTplStatus((data && data.message) || 'Clone failed.', true);
                      return;
                  }
                  setTplStatus('Cloned — find it under "Your copy" in the list.', false);
                  loadTemplates();
                  deselectTemplate();
              })
              .catch(function () { setTplBusy(false); setTplStatus('Network error cloning.', true); });
        }

        function deleteTemplate() {
            if (!tplState.current || tplState.current.kind !== 'clone') return;
            if (window.adamiroConfirm) {
                window.adamiroConfirm({
                    title: 'Delete this template?',
                    body: 'Your custom template will be removed. This cannot be undone.',
                    confirmLabel: 'Delete',
                    cancelLabel: 'Keep',
                }).then(function (ok) { if (ok) doDeleteTemplate(); });
            } else {
                doDeleteTemplate();
            }
        }
        function doDeleteTemplate() {
            setTplBusy(true);
            fetch(tplBase + '/' + tplState.current.id, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: admRestHeaders(),
            }).then(function (r) { return r.json(); })
              .then(function (data) {
                  setTplBusy(false);
                  if (!data || !data.success) {
                      setTplStatus((data && data.message) || 'Delete failed.', true);
                      return;
                  }
                  loadTemplates();
                  deselectTemplate();
              })
              .catch(function () { setTplBusy(false); setTplStatus('Network error deleting.', true); });
        }


        if (tplRenderBtn) tplRenderBtn.addEventListener('click', renderTemplate);
        if (tplCloneBtn) tplCloneBtn.addEventListener('click', cloneTemplate);
        if (tplDeleteBtn) tplDeleteBtn.addEventListener('click', deleteTemplate);

        // Lazy-load when the user opens the tab.
        tabs.forEach(function (tab) {
            tab.addEventListener('click', function () {
                if (tab.getAttribute('data-tab') === 'templates' && !tplState.loaded) {
                    loadTemplates();
                }
            });
        });

        // Freeform platform switch (2026-07-10) — re-render with the new
        // Instagram-filtered set. Registered here, not in the outer click
        // handler above, because renderTemplateGrid/tplState only exist in
        // this block's scope.
        if (platformSelectEl && platformBtns) {
            platformBtns.forEach(function (btn) {
                btn.addEventListener('click', function () {
                    if (tplState.loaded) {
                        tplState.page = 0;
                        renderTemplateGrid();
                    }
                });
            });
        }
    }

    // ── Carousel flow (Phase 3a — Carousel composer in the Hero modal) ──
    if (canGenerate) {
        function carEscHtml(s) {
            return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
                return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
            });
        }
        function carEscAttr(s) { return carEscHtml(s); }

        var carPane       = overlay.querySelector('[data-pane="carousel"]');
        var carTplGrid    = carPane && carPane.querySelector('.adm-mp-car__tpl-grid');
        var carCountInput = carPane && carPane.querySelector('#adm-mp-car-count');
        var carTopicInput = carPane && carPane.querySelector('#adm-mp-car-topic');
        var carToneInput  = carPane && carPane.querySelector('#adm-mp-car-tone');
        var carLangInput  = carPane && carPane.querySelector('#adm-mp-car-lang');
        var carTplHint    = carPane && carPane.querySelector('.adm-mp-car__hint--tmpl');
        var carForm       = carPane && carPane.querySelector('.adm-mp-car__form');
        var carProgress   = carPane && carPane.querySelector('.adm-mp-car__progress');
        var carProgressFill  = carProgress && carProgress.querySelector('.acp-progress-fill');
        var carProgressLabel = carProgress && carProgress.querySelector('.acp-progress-label');
        var carProgressTime  = carProgress && carProgress.querySelector('.acp-progress-elapsed');
        var carPreviewNote   = carPane && carPane.querySelector('.adm-mp-car__status--note');
        var carDraftProgress = carPane && carPane.querySelector('.adm-mp-car__draft-progress');
        var carReview     = carPane && carPane.querySelector('.adm-mp-car__review');
        var carSlidesEl   = carPane && carPane.querySelector('.adm-mp-car__slides');
        var carCaptionEl  = carPane && carPane.querySelector('#adm-mp-car-caption');
        var carRedraftBtn = carPane && carPane.querySelector('[data-act="car-redraft"]');
        var carGenerateBtn = carPane && carPane.querySelector('[data-act="car-generate"]');
        var carReviewBack = carPane && carPane.querySelector('[data-act="car-review-back"]');
        var carReviewStatus = carPane && carPane.querySelector('.adm-mp-car__status--review');
        var carPreview    = carPane && carPane.querySelector('.adm-mp-car__preview');
        var carPreviewImg = carPane && carPane.querySelector('.adm-mp-car__preview-img');
        var carPreviewTitle = carPane && carPane.querySelector('.adm-mp-car__preview-title');
        var carPreviewCaption = carPane && carPane.querySelector('.adm-mp-car__preview-caption');
        var carPreviewPdfLink = carPane && carPane.querySelector('.adm-mp-car__preview-pdflink');
        var carPreviewRedo = carPane && carPane.querySelector('[data-act="car-redo"]');
        var carDraftBtn   = carPane && carPane.querySelector('[data-act="car-draft"]');
        var carStatus     = carPane && carPane.querySelector('.adm-mp-car__status');

        var carState = { templates: [], loaded: false, busy: false, selectedSlug: '', draft: null, tplPage: 0 };
        // 8 = 2 full rows at the paged grid's fixed 4-column layout (2 cols
        // on mobile) — never an orphaned partial row.
        // 10 — same page size, same fixed 5-column grid as the Infographic/
        // Templates layout grids. All three tabs share identical paging UI/UX.
        var CAR_TPL_PAGE_SIZE = 10;

        function setCarStatus(msg, isError, target) {
            var el = target === 'review' ? carReviewStatus : carStatus;
            if (!el) return;
            if (!msg) { el.hidden = true; el.textContent = ''; return; }
            el.hidden = false;
            el.textContent = msg;
            el.classList.toggle('is-error', !!isError);
        }
        function setCarBusy(b) {
            carState.busy = !!b;
            if (carDraftBtn) carDraftBtn.disabled = !!b || !carState.selectedSlug;
            if (carGenerateBtn) carGenerateBtn.disabled = !!b;
            if (carRedraftBtn) carRedraftBtn.disabled = !!b;
        }
        function hideAllCarPanels() {
            if (carForm) carForm.hidden = true;
            if (carDraftProgress) carDraftProgress.hidden = true;
            if (carReview) carReview.hidden = true;
            if (carProgress) carProgress.hidden = true;
            if (carPreview) carPreview.hidden = true;
        }
        function showCarForm() {
            hideAllCarPanels();
            if (carForm) carForm.hidden = false;
        }
        function showCarDraftProgress() {
            hideAllCarPanels();
            if (carDraftProgress) carDraftProgress.hidden = false;
        }
        function showCarReview() {
            hideAllCarPanels();
            if (carReview) carReview.hidden = false;
        }
        function showCarProgress() {
            hideAllCarPanels();
            if (carProgress) carProgress.hidden = false;
        }
        function showCarPreview() {
            hideAllCarPanels();
            if (carPreview) carPreview.hidden = false;
        }

        function loadCarTemplates() {
            if (carState.loaded || !carTplGrid) return;
            carTplGrid.innerHTML = '<div class="adm-mp-car__tpl-loading"><span class="adm-media-picker__spinner"></span> Loading templates…</div>';
            var url = (cfg.reachRestUrl || '') + 'carousel/templates';
            fetch(url, {
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Accept': 'application/json' }),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    console.error('[Adamiro Carousel] templates fetch failed', resp.status, data || resp.raw.slice(0, 400));
                    carTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty is-error">Could not load templates: ' + carEscHtml(msg) + '</div>';
                    setCarStatus('Carousel templates failed (' + resp.status + '): ' + msg, true);
                    return;
                }
                if (!data) {
                    console.error('[Adamiro Carousel] templates response not JSON', resp.raw.slice(0, 400));
                    carTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty is-error">Server response was not JSON.</div>';
                    setCarStatus('Server returned non-JSON. Open DevTools console for the raw response.', true);
                    return;
                }
                var list = (data && data.templates) || [];
                carState.templates = list;
                carState.loaded = true;
                if (!list.length) {
                    carTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty">No templates available.</div>';
                    return;
                }
                renderCarTplGrid();
            }).catch(function (err) {
                console.error('[Adamiro Carousel] templates fetch threw', err);
                carTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty is-error">Network error loading templates.</div>';
                setCarStatus('Network error loading templates: ' + (err && err.message), true);
            });
        }
        // The post type decides which carousel formats are publishable:
        // Instagram posts a square/portrait PNG set, LinkedIn/Substack a PDF.
        // Only surface templates whose optimal output matches this post's
        // platform, so the picker can't offer a format this post can't publish.
        function carTemplatesForPlatform() {
            var plat = (platformSlug || '').toLowerCase();
            var list = carState.templates || [];
            if (plat === 'instagram') {
                return list.filter(function (t) { return String(t.optimal_output_kind || '').indexOf('instagram') === 0; });
            }
            // linkedin/substack — and Voice (no platform), whose carousel composer
            // is LinkedIn-PDF mode — only show linkedin_pdf templates so the
            // Instagram square/portrait ones stay hidden (they can't be output here).
            if (plat === 'linkedin' || plat === 'substack' || !plat) {
                return list.filter(function (t) { return String(t.optimal_output_kind || '') === 'linkedin_pdf'; });
            }
            return list; // other platforms: don't restrict
        }
        function renderCarTplGrid() {
            carTplGrid.innerHTML = '';
            var tpls = carTemplatesForPlatform();
            var carPager = carTplGrid.parentElement && carTplGrid.parentElement.querySelector('.adm-mp-car__pager');
            if (!tpls.length) {
                carTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty">No carousel templates for this post type.</div>';
                if (carPager) carPager.hidden = true;
                return;
            }
            var carPages = Math.max(1, Math.ceil(tpls.length / CAR_TPL_PAGE_SIZE));
            carState.tplPage = Math.min(Math.max(0, carState.tplPage || 0), carPages - 1);
            // Keep the selected template's page visible when it's still valid.
            var selectionStillValid = tpls.some(function (t) { return t.slug === carState.selectedSlug; });
            if (selectionStillValid) {
                tpls.some(function (t, i) {
                    if (t.slug === carState.selectedSlug) {
                        carState.tplPage = Math.floor(i / CAR_TPL_PAGE_SIZE);
                        return true;
                    }
                    return false;
                });
            }
            tpls.slice(carState.tplPage * CAR_TPL_PAGE_SIZE, (carState.tplPage + 1) * CAR_TPL_PAGE_SIZE).forEach(function (t) {
                var range = t.slide_count_range || [3, 10];
                var card = document.createElement('button');
                card.type = 'button';
                card.className = 'adm-mp-car__tpl-card';
                card.setAttribute('data-slug', t.slug);
                card.setAttribute('data-min', String(range[0]));
                card.setAttribute('data-max', String(range[1]));
                // `<img src>` doesn't send the X-WP-Nonce header, so authenticate
                // via the _wpnonce query param (also accepted by rest_cookie_*
                // checks).
                var thumbSrc = t.preview_svg_url
                    ? t.preview_svg_url + (t.preview_svg_url.indexOf('?') >= 0 ? '&' : '?') + '_wpnonce=' + encodeURIComponent(cfg.nonce)
                        // An <img> cannot carry X-Adamiro-Account, so the account rides the query (adm_acct).
                        + (window.ADAMIRO_ACCT ? '&adm_acct=' + encodeURIComponent(window.ADAMIRO_ACCT) : '')
                    : '';
                var thumbHtml = thumbSrc
                    ? '<div class="adm-mp-car__tpl-thumb"><img src="' + carEscAttr(thumbSrc) + '" alt="' + carEscAttr(t.name) + '" loading="lazy"></div>'
                    : '<div class="adm-mp-car__tpl-thumb adm-mp-car__tpl-thumb--placeholder"></div>';
                card.innerHTML =
                    thumbHtml +
                    '<div class="adm-mp-car__tpl-body">' +
                        '<div class="adm-mp-car__tpl-name">' + carEscHtml(t.name) + '</div>' +
                        '<div class="adm-mp-car__tpl-range">' + range[0] + '–' + range[1] + ' slides</div>' +
                    '</div>';
                card.addEventListener('click', function () { selectCarTemplate(t.slug); });
                carTplGrid.appendChild(card);
            });
            // Auto-select the first template so the form is usable immediately
            // — but only when there's no valid existing selection (first load,
            // or the platform switched and the old slug no longer applies).
            // Paging alone must not steal the selection back to page 1.
            if (!selectionStillValid) {
                if (tpls[0]) selectCarTemplate(tpls[0].slug);
            } else {
                carTplGrid.querySelectorAll('.adm-mp-car__tpl-card').forEach(function (c) {
                    c.classList.toggle('is-selected', c.getAttribute('data-slug') === carState.selectedSlug);
                });
            }
            // Pager row lives right after the grid, created once.
            if (!carPager && carTplGrid.parentElement) {
                carPager = document.createElement('div');
                carPager.className = 'adm-mp-car__pager';
                carTplGrid.insertAdjacentElement('afterend', carPager);
            }
            if (carPager) {
                if (carPages <= 1) {
                    carPager.hidden = true;
                } else {
                    carPager.hidden = false;
                    carPager.innerHTML =
                        '<button type="button" class="adm-mp-gen__suggest" data-car-page="prev"' + (carState.tplPage === 0 ? ' disabled' : '') + '>&#8592; Prev</button>' +
                        '<span class="adm-mp-car__pager-count">' + (carState.tplPage + 1) + ' / ' + carPages + '</span>' +
                        '<button type="button" class="adm-mp-gen__suggest" data-car-page="next"' + (carState.tplPage >= carPages - 1 ? ' disabled' : '') + '>Next &#8594;</button>';
                    carPager.querySelectorAll('[data-car-page]').forEach(function (btn) {
                        btn.addEventListener('click', function () {
                            carState.tplPage += (btn.getAttribute('data-car-page') === 'next') ? 1 : -1;
                            renderCarTplGrid();
                        });
                    });
                }
            }
        }
        function selectCarTemplate(slug) {
            carState.selectedSlug = slug;
            // Visual selection.
            var cards = carTplGrid.querySelectorAll('.adm-mp-car__tpl-card');
            cards.forEach(function (c) {
                c.classList.toggle('is-selected', c.getAttribute('data-slug') === slug);
            });
            var tmpl = (carState.templates || []).filter(function (t) { return t.slug === slug; })[0];
            if (!tmpl) return;
            var range = tmpl.slide_count_range || [3, 10];
            if (carCountInput) {
                carCountInput.min = String(range[0]);
                carCountInput.max = String(range[1]);
                var n = parseInt(carCountInput.value, 10) || range[0];
                if (n < range[0]) carCountInput.value = String(range[0]);
                if (n > range[1]) carCountInput.value = String(range[1]);
            }
            if (carTplHint) carTplHint.textContent = tmpl.description || '';
            if (carDraftBtn) carDraftBtn.disabled = !!carState.busy;
        }

        function humanizeSlotKey(key) {
            // Convert snake_case keys to readable labels for the edit form.
            return String(key).replace(/_/g, ' ').replace(/^\w|\s\w/g, function (c) { return c.toUpperCase(); });
        }

        function renderCarReview() {
            if (!carState.draft || !carSlidesEl) return;
            var slug = carState.draft.template_slug;
            var tmpl = (carState.templates || []).filter(function (t) { return t.slug === slug; })[0];
            if (!tmpl) { setCarStatus('Template definition lost — please re-draft.', true, 'review'); return; }

            // Editable slots = text-kind, non-auto, non-image. Auto-filled
            // values (slide_number, item_number, slide_total) show as
            // read-only badges so the user understands the numbering.
            var editableSlots = (tmpl.slots || []).filter(function (s) { return s.kind === 'text' && !s.auto; });
            var autoSlots = (tmpl.slots || []).filter(function (s) { return s.auto; });

            carSlidesEl.innerHTML = '';
            (carState.draft.slides || []).forEach(function (slide, idx) {
                var card = document.createElement('div');
                card.className = 'adm-mp-car__slide-card';
                card.setAttribute('data-slide-index', String(slide.index));

                var head = '<div class="adm-mp-car__slide-head">' +
                    '<span class="adm-mp-car__slide-num">Slide ' + slide.index + ' / ' + carState.draft.slide_count + '</span>' +
                    (autoSlots.length ? '<span class="adm-mp-car__slide-auto" title="Auto-filled per slide">' +
                        autoSlots.map(function (s) {
                            var val = (slide.slot_values && slide.slot_values[s.key]) || '';
                            return carEscHtml(humanizeSlotKey(s.key)) + ': ' + carEscHtml(val);
                        }).join(' · ') +
                    '</span>' : '') +
                    '</div>';

                var fields = editableSlots.map(function (s) {
                    var val = (slide.slot_values && slide.slot_values[s.key]) || '';
                    var max = s.max_chars > 0 ? s.max_chars : 0;
                    var rows = (max > 80) ? 3 : 2;
                    var inputId = 'adm-mp-car-slot-' + slide.index + '-' + s.key;
                    var counter = max > 0
                        ? '<span class="adm-mp-car__slot-count" data-max="' + max + '">' + val.length + ' / ' + max + '</span>'
                        : '';
                    return '<div class="adm-mp-car__slot-field">' +
                        '<div class="adm-mp-car__slot-head">' +
                            '<label class="adm-mp-car__slot-label" for="' + inputId + '">' + carEscHtml(humanizeSlotKey(s.key)) + (s.required ? ' *' : '') + '</label>' +
                            counter +
                        '</div>' +
                        '<textarea id="' + inputId + '" class="adm-mp-car__slot-input" data-slot-key="' + carEscAttr(s.key) + '" rows="' + rows + '"' + (max > 0 ? ' maxlength="' + max + '"' : '') + '>' + carEscHtml(val) + '</textarea>' +
                        '</div>';
                }).join('');

                card.innerHTML = head + '<div class="adm-mp-car__slot-grid">' + fields + '</div>';
                carSlidesEl.appendChild(card);
            });

            // Live counter updates.
            carSlidesEl.querySelectorAll('.adm-mp-car__slot-input').forEach(function (ta) {
                ta.addEventListener('input', function () {
                    var headEl = ta.parentElement && ta.parentElement.querySelector('.adm-mp-car__slot-count');
                    if (headEl) {
                        var max = parseInt(headEl.getAttribute('data-max'), 10) || 0;
                        var len = ta.value.length;
                        headEl.textContent = len + ' / ' + max;
                        headEl.classList.toggle('is-over', max > 0 && len > max);
                    }
                });
            });

            if (carCaptionEl) carCaptionEl.value = carState.draft.caption || '';

            setCarStatus(null, false, 'review');
            showCarReview();
        }

        function readReviewedSlides() {
            // Walk the review DOM and build the slides_text payload back out
            // of the edited inputs + auto slot values (which weren't editable).
            var slides = [];
            if (!carSlidesEl) return slides;
            var cards = carSlidesEl.querySelectorAll('.adm-mp-car__slide-card');
            cards.forEach(function (card, idx) {
                var index = parseInt(card.getAttribute('data-slide-index'), 10) || (idx + 1);
                var draftSlide = (carState.draft.slides || []).filter(function (s) { return s.index === index; })[0] || { slot_values: {} };
                var slot_values = Object.assign({}, draftSlide.slot_values || {});
                card.querySelectorAll('.adm-mp-car__slot-input').forEach(function (ta) {
                    var key = ta.getAttribute('data-slot-key');
                    if (key) slot_values[key] = ta.value;
                });
                slides.push({ index: index, slot_values: slot_values });
            });
            return slides;
        }

        // Instagram carousels publish as N PNG slides (not a PDF). Aspect is
        // derived from the template slug per product decision: a slug containing
        // 'portrait' → 1080×1350, otherwise square 1080×1080.
        function carIsInstagram() {
            return (platformSlug || '') === 'instagram';
        }
        function carInstagramOutputKind() {
            var slug = String(carState.selectedSlug || '').toLowerCase();
            return slug.indexOf('portrait') !== -1 ? 'instagram_portrait' : 'instagram_square';
        }
        function buildComposeBody() {
            var body = {
                template_slug: carState.selectedSlug,
                slide_count: parseInt(carCountInput && carCountInput.value, 10),
                topic: (carTopicInput && carTopicInput.value || '').trim(),
                tone: (carToneInput && carToneInput.value) || 'direct',
                language: (carLangInput && carLangInput.value) || 'en',
                platform: platformSlug || 'linkedin',
            };
            if (carIsInstagram()) {
                body.output_kind = carInstagramOutputKind();
            }
            return body;
        }

        function draftCarousel() {
            if (!carDraftBtn || carState.busy) return;
            var body = buildComposeBody();
            if (!body.template_slug || !body.slide_count) {
                setCarStatus('Pick a template and a slide count first.', true);
                return;
            }
            setCarStatus(null, false);
            setCarBusy(true);
            showCarDraftProgress();
            fetch((cfg.reachRestUrl || '') + 'carousel/draft-text', {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                setCarBusy(false);
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok || !data) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    console.error('[Adamiro Carousel] draft failed', resp.status, data || resp.raw.slice(0, 400));
                    setCarStatus('Draft failed: ' + msg, true);
                    showCarForm();
                    return;
                }
                carState.draft = {
                    template_slug: data.template_slug,
                    slide_count: data.slide_count,
                    aspect: data.aspect,
                    slides: data.slides || [],
                    caption: data.caption || '',
                };
                renderCarReview();
            }).catch(function (err) {
                setCarBusy(false);
                console.error('[Adamiro Carousel] draft threw', err);
                setCarStatus('Network error drafting slides: ' + (err && err.message), true);
                showCarForm();
            });
        }

        // Compose progress bar. The server writes {stage, done, total} to a
        // transient keyed by a token we send with the compose POST; we poll it
        // every 2s while the POST is pending. Phase weights: backgrounds 5-70%,
        // render 70-92%, pdf 92-97%, 100% on success. Never moves backwards.
        function carNewProgressToken() {
            try {
                if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
            } catch (e) { /* fall through */ }
            var t = '';
            for (var i = 0; i < 32; i++) t += Math.floor(Math.random() * 16).toString(16);
            return t;
        }
        function carProgressView(p) {
            var stage = p && p.stage, done = (p && +p.done) || 0, total = (p && +p.total) || 0;
            var frac = total > 0 ? Math.min(1, done / total) : 0;
            if (stage === 'backgrounds') return { pct: 5 + 65 * frac, label: 'Backgrounds ' + done + ' of ' + total };
            if (stage === 'render')      return { pct: 70 + 22 * frac, label: 'Rendering slide ' + Math.min(total, done + 1) + ' of ' + total };
            if (stage === 'pdf')         return { pct: 94, label: 'Building PDF' };
            if (stage === 'done')        return { pct: 100, label: 'Done' };
            return null;
        }
        function carProgressStart(token) {
            var startedAt = Date.now(), pct = 3, timer = null, stopped = false;
            function paint(label) {
                if (carProgressFill) { carProgressFill.classList.remove('is-failed'); carProgressFill.style.width = pct + '%'; }
                if (label && carProgressLabel) carProgressLabel.textContent = label;
                if (carProgressTime) carProgressTime.textContent = Math.round((Date.now() - startedAt) / 1000) + 's';
            }
            paint('Starting…');
            function poll() {
                if (stopped) return;
                fetch((cfg.reachRestUrl || '') + 'carousel/compose-progress?token=' + encodeURIComponent(token), {
                    credentials: 'same-origin',
                    cache: 'no-store',
                    headers: admRestHeaders({ 'Accept': 'application/json' }),
                }).then(function (r) { return r.ok ? r.json() : null; }).then(function (p) {
                    if (stopped) return;
                    var v = carProgressView(p);
                    if (v) { pct = Math.max(pct, v.pct); paint(v.label); } else { paint(); }
                }).catch(function () { if (!stopped) paint(); });
            }
            timer = setInterval(poll, 2000);
            var ticker = setInterval(function () { if (!stopped) paint(); }, 1000);
            return function stop(outcome) {
                stopped = true;
                clearInterval(timer); clearInterval(ticker);
                if (outcome === 'ok') { pct = 100; paint('Done'); }
                else if (outcome === 'fail' && carProgressFill) { carProgressFill.classList.add('is-failed'); }
            };
        }
        function carReusedNote(slides) {
            if (!slides || !slides.length) return '';
            if (slides.length === 1) return 'Slide ' + slides[0] + " reuses another slide's background (the image service was busy).";
            var last = slides[slides.length - 1];
            return 'Slides ' + slides.slice(0, -1).join(', ') + ' and ' + last + " reuse other slides' backgrounds (the image service was busy).";
        }

        function generateCarousel() {
            if (!carGenerateBtn || carState.busy || !carState.draft) return;
            var slides = readReviewedSlides();
            var caption = carCaptionEl ? carCaptionEl.value : '';
            var body = buildComposeBody();
            body.slides_text = slides;
            body.caption = caption;
            var progressToken = carNewProgressToken();
            body.progress_token = progressToken;
            setCarStatus(null, false, 'review');
            setCarBusy(true);
            showCarProgress();
            var stopProgress = carProgressStart(progressToken);
            // Same endpoint for both — carousel/compose maps to the freeform
            // composer, which branches on output_kind: instagram_* → N PNG
            // slides; linkedin → PDF. (There is no /compose-freeform route.)
            var isIG = carIsInstagram();
            var composeUrl = (cfg.reachRestUrl || '') + 'carousel/compose';
            fetch(composeUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                setCarBusy(false);
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok || !data) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    stopProgress('fail');
                    console.error('[Adamiro Carousel] compose failed', resp.status, data || resp.raw.slice(0, 400));
                    setCarStatus('Compose failed: ' + msg, true, 'review');
                    showCarReview();
                    return;
                }
                stopProgress('ok');
                if (isIG) {
                    // IG: per-slide PNGs, no PDF. Publishes via attach-ig-set on insert.
                    carouselSelection = {
                        isInstagram: true,
                        outputKind: body.output_kind || 'instagram_square',
                        aspect: (body.output_kind === 'instagram_portrait') ? 'portrait' : 'square',
                        pngAttachmentIds: data.png_attachment_ids || [],
                        pngUrls: data.png_urls || [],
                        thumbnailId: data.thumbnail_attachment_id,
                        thumbnailUrl: data.thumbnail_url || (data.png_urls && data.png_urls[0]) || '',
                        // The IG composer also builds a downloadable PDF of the
                        // full set (best-effort). Use the real PDF for "Open PDF";
                        // never fall back to the thumbnail (that's the bug where
                        // "Open PDF" opened the first slide image).
                        pdfUrl: data.pdf_url || '',
                        caption: data.caption || '',
                        templateSlug: data.template_slug || body.template_slug,
                        slideCount: data.slide_count || (data.png_urls && data.png_urls.length) || body.slide_count,
                    };
                } else {
                    carouselSelection = {
                        pdfId: data.pdf_attachment_id,
                        pdfUrl: data.pdf_url,
                        thumbnailId: data.thumbnail_attachment_id,
                        thumbnailUrl: data.thumbnail_url,
                        caption: data.caption || '',
                        templateSlug: data.template_slug || body.template_slug,
                        slideCount: data.slide_count || body.slide_count,
                    };
                }
                if (carPreviewImg) carPreviewImg.src = carouselSelection.thumbnailUrl;
                if (carPreviewTitle) carPreviewTitle.textContent = carouselSelection.templateSlug + ' · ' + carouselSelection.slideCount + ' slides';
                if (carPreviewCaption) carPreviewCaption.textContent = carouselSelection.caption || '';
                if (carPreviewPdfLink) {
                    if (carouselSelection.pdfUrl) {
                        carPreviewPdfLink.href = carouselSelection.pdfUrl;
                        carPreviewPdfLink.hidden = false;
                    } else {
                        carPreviewPdfLink.removeAttribute('href');
                        carPreviewPdfLink.hidden = true;
                    }
                }
                if (carPreviewNote) {
                    var reusedNote = carReusedNote(data.reused_background_slides);
                    carPreviewNote.textContent = reusedNote;
                    carPreviewNote.classList.remove('is-error');
                    carPreviewNote.hidden = !reusedNote;
                }
                showCarPreview();
                refreshInsertBtn();
            }).catch(function (err) {
                stopProgress('fail');
                setCarBusy(false);
                console.error('[Adamiro Carousel] compose threw', err);
                setCarStatus('Network error generating carousel: ' + (err && err.message), true, 'review');
                showCarReview();
            });
        }

        if (carDraftBtn) carDraftBtn.addEventListener('click', draftCarousel);
        if (carRedraftBtn) carRedraftBtn.addEventListener('click', draftCarousel);
        if (carGenerateBtn) carGenerateBtn.addEventListener('click', generateCarousel);
        if (carReviewBack) carReviewBack.addEventListener('click', function () {
            carState.draft = null;
            showCarForm();
        });
        if (carPreviewRedo) carPreviewRedo.addEventListener('click', function () {
            carouselSelection = null;
            carState.draft = null;
            refreshInsertBtn();
            showCarForm();
        });

        // Seed Topic from the editor body so users don't have to retype it.
        function seedCarTopic() {
            if (!carTopicInput || carTopicInput.value) return;
            try {
                var text = (editor.getText && editor.getText()) || '';
                text = String(text || '').trim();
                if (text.length > 0) {
                    carTopicInput.value = text.length > 400 ? text.substring(0, 400) : text;
                }
            } catch (_) { /* ignore */ }
        }

        // Lazy-load templates + seed topic when the tab is opened.
        tabs.forEach(function (tab) {
            tab.addEventListener('click', function () {
                if (tab.getAttribute('data-tab') === 'carousel') {
                    loadCarTemplates();
                    seedCarTopic();
                }
            });
        });

        // Freeform platform switch (2026-07-10) — see the matching listener
        // in the Templates block above for why this lives here rather than
        // in the outer click handler (renderCarTplGrid/carState scoping).
        if (platformSelectEl && platformBtns) {
            platformBtns.forEach(function (btn) {
                btn.addEventListener('click', function () {
                    if (carState.loaded) {
                        carState.tplPage = 0;
                        renderCarTplGrid();
                    }
                });
            });
        }
    }

    // ── Infographic flow (2026-06-12 — fact-sheet driven, Growth+) ─────────
    if (capInfographic && voiceRestUrl) {
        var infEsc = function (s) {
            return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
                return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
            });
        };
        var infPane     = overlay.querySelector('[data-pane="infographic"]');
        var infTplGrid  = infPane && infPane.querySelector('.adm-mp-inf__tpl-grid');
        var infTplHint  = infPane && infPane.querySelector('.adm-mp-inf__hint--tmpl');
        var infForm     = infPane && infPane.querySelector('.adm-mp-inf__form');
        var infSlotsWrap = infPane && infPane.querySelector('.adm-mp-inf__slots-wrap');
        var infSlotsEl  = infPane && infPane.querySelector('.adm-mp-inf__slots');
        var infStatus   = infPane && infPane.querySelector('.adm-mp-inf__status');
        var infSuggestProgress = infPane && infPane.querySelector('.adm-mp-inf__suggest-progress');
        var infProgress = infPane && infPane.querySelector('.adm-mp-inf__progress');
        var infResult   = infPane && infPane.querySelector('.adm-mp-inf__result');
        var infResultGrid = infPane && infPane.querySelector('.adm-mp-inf__result-grid');
        var infSuggestTplBtn = infPane && infPane.querySelector('[data-act="inf-suggest-template"]');
        var infSuggestBtn = infPane && infPane.querySelector('[data-act="inf-suggest"]');
        var infGenBtn   = infPane && infPane.querySelector('[data-act="inf-generate"]');
        var infPreviewBtn = infPane && infPane.querySelector('[data-act="inf-preview"]');
        var infReportEl   = infPane && infPane.querySelector('[data-inf-report]');
        var infRedoBtn  = infPane && infPane.querySelector('[data-act="inf-redo"]');
        // "Suggest texts" fills the CURRENTLY selected layout — disabled until
        // one is picked (manually, or via "Suggest a template").
        if (infSuggestBtn) infSuggestBtn.disabled = true;

        // prefills: slug → slot_values from the last draft round. photos: slot
        // key → chosen URL for the currently selected template (reset on
        // re-select). icons: [{name, svg}] choices for icon-kind slots (from
        // the templates endpoint); iconMap: name → inner svg for previews.
        var infState = { templates: [], loaded: false, busy: false, selectedSlug: '', prefills: {}, offered: {}, drafted: false, photos: {}, photoNames: {}, icons: [], iconMap: {}, tplPage: 0 };

        function infIconSvg(name, size) {
            var inner = infState.iconMap[name] || '';
            if (!inner) return '';
            var s = size || 22;
            return '<svg viewBox="0 0 24 24" width="' + s + '" height="' + s + '" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + inner + '</svg>';
        }

        function setInfStatus(msg, isError) {
            if (!infStatus) return;
            if (!msg) { infStatus.hidden = true; infStatus.textContent = ''; return; }
            infStatus.hidden = false;
            infStatus.textContent = msg;
            infStatus.classList.toggle('is-error', !!isError);
        }
        function setInfBusy(b) {
            infState.busy = !!b;
            if (infSuggestTplBtn) infSuggestTplBtn.disabled = !!b;
            if (infSuggestBtn) infSuggestBtn.disabled = !!b || !infState.selectedSlug;
            if (infGenBtn) infGenBtn.disabled = !!b || !infState.selectedSlug;
            if (infPreviewBtn) infPreviewBtn.disabled = !!b || !infState.selectedSlug;
        }
        function showInfForm() {
            if (infForm) infForm.hidden = false;
            if (infSuggestProgress) infSuggestProgress.hidden = true;
            if (infProgress) infProgress.hidden = true;
            if (infResult) infResult.hidden = true;
        }
        function showInfPanel(which) {
            if (infForm) infForm.hidden = which !== 'form';
            if (infSuggestProgress) infSuggestProgress.hidden = which !== 'suggest';
            if (infProgress) infProgress.hidden = which !== 'progress';
            if (infResult) infResult.hidden = which !== 'result';
        }

        function infSelectedTemplate() {
            return (infState.templates || []).filter(function (t) { return t.slug === infState.selectedSlug; })[0] || null;
        }

        function loadInfTemplates() {
            if (infState.loaded || !infTplGrid) return;
            infTplGrid.innerHTML = '<div class="adm-mp-car__tpl-loading"><span class="adm-media-picker__spinner"></span> Loading layouts…</div>';
            fetch(voiceRestUrl + 'graphics/infographic/templates', {
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Accept': 'application/json' }),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok || !data) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    infTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty is-error">Could not load layouts: ' + infEsc(msg) + '</div>';
                    return;
                }
                infState.templates = data.templates || [];
                infState.icons = data.icons || [];
                infState.iconMap = {};
                infState.icons.forEach(function (ic) { if (ic && ic.name) infState.iconMap[ic.name] = ic.svg || ''; });
                infState.loaded = true;
                renderInfTplGrid();
            }).catch(function (err) {
                infTplGrid.innerHTML = '<div class="adm-mp-car__tpl-empty is-error">Network error loading layouts.</div>';
            });
        }

        // 10 = 2 full rows at the grid's fixed 5-column layout (or 5 full
        // rows at the 2-column mobile layout) — never an orphaned partial row.
        var INF_TPL_PAGE_SIZE = 10;

        function infSortedTemplates() {
            // After a draft round, layouts the draft can feed come first.
            var list = (infState.templates || []).slice();
            if (infState.drafted) {
                list.sort(function (a, b) {
                    var ao = infState.offered[a.slug] ? 0 : 1;
                    var bo = infState.offered[b.slug] ? 0 : 1;
                    return ao - bo;
                });
            }
            return list;
        }

        function renderInfTplGrid() {
            if (!infTplGrid) return;
            var list = infSortedTemplates();
            var pages = Math.max(1, Math.ceil(list.length / INF_TPL_PAGE_SIZE));
            infState.tplPage = Math.min(Math.max(0, infState.tplPage || 0), pages - 1);
            // Keep the selected layout visible: jump to its page.
            if (infState.selectedSlug) {
                list.some(function (t, i) {
                    if (t.slug === infState.selectedSlug) {
                        infState.tplPage = Math.floor(i / INF_TPL_PAGE_SIZE);
                        return true;
                    }
                    return false;
                });
            }
            infTplGrid.innerHTML = '';
            list.slice(infState.tplPage * INF_TPL_PAGE_SIZE, (infState.tplPage + 1) * INF_TPL_PAGE_SIZE).forEach(function (t) {
                var card = document.createElement('button');
                card.type = 'button';
                card.className = 'adm-mp-car__tpl-card';
                card.setAttribute('data-slug', t.slug);
                var suggested = infState.drafted && infState.offered[t.slug];
                var thumbHtml = t.preview_png_url
                    ? '<div class="adm-mp-car__tpl-thumb"><img src="' + infEsc(t.preview_png_url) + '" alt="' + infEsc(t.name) + '" loading="lazy"></div>'
                    : '<div class="adm-mp-car__tpl-thumb adm-mp-car__tpl-thumb--placeholder"></div>';
                card.innerHTML =
                    thumbHtml +
                    '<div class="adm-mp-car__tpl-body">' +
                        '<div class="adm-mp-car__tpl-name">' + infEsc(t.name) + (suggested ? ' <span class="adm-mp-inf__badge">Suggested</span>' : '') + '</div>' +
                        '<div class="adm-mp-car__tpl-range">' + (t.aspects || []).length + ' sizes</div>' +
                    '</div>';
                card.classList.toggle('is-selected', t.slug === infState.selectedSlug);
                card.addEventListener('click', function () { selectInfTemplate(t.slug); });
                infTplGrid.appendChild(card);
            });
            // Pager row lives right after the grid, created once.
            var pager = infTplGrid.parentElement && infTplGrid.parentElement.querySelector('.adm-mp-inf__pager');
            if (!pager && infTplGrid.parentElement) {
                pager = document.createElement('div');
                pager.className = 'adm-mp-inf__pager';
                infTplGrid.insertAdjacentElement('afterend', pager);
            }
            if (pager) {
                if (pages <= 1) {
                    pager.hidden = true;
                } else {
                    pager.hidden = false;
                    pager.innerHTML =
                        '<button type="button" class="adm-mp-gen__suggest" data-inf-page="prev"' + (infState.tplPage === 0 ? ' disabled' : '') + '>&#8592; Prev</button>' +
                        '<span class="adm-mp-inf__pager-count">' + (infState.tplPage + 1) + ' / ' + pages + '</span>' +
                        '<button type="button" class="adm-mp-gen__suggest" data-inf-page="next"' + (infState.tplPage >= pages - 1 ? ' disabled' : '') + '>Next &#8594;</button>';
                    pager.querySelectorAll('[data-inf-page]').forEach(function (btn) {
                        btn.addEventListener('click', function () {
                            infState.tplPage += (btn.getAttribute('data-inf-page') === 'next') ? 1 : -1;
                            // Paging must not re-trigger selection (which re-renders
                            // the form) — clear the "snap to selected page" pull by
                            // rendering from the new page directly.
                            var keep = infState.selectedSlug;
                            infState.selectedSlug = '';
                            renderInfTplGrid();
                            infState.selectedSlug = keep;
                            infTplGrid.querySelectorAll('.adm-mp-car__tpl-card').forEach(function (c) {
                                c.classList.toggle('is-selected', c.getAttribute('data-slug') === keep);
                            });
                        });
                    });
                }
            }
        }

        function selectInfTemplate(slug) {
            // Picked photos only reset when the LAYOUT actually changes —
            // re-selecting the same layout (e.g. after a suggest round
            // re-renders the grid) must keep them, or compose silently loses
            // every image the user already chose.
            if (slug !== infState.selectedSlug) {
                infState.photos = {};
                infState.photoNames = {};
                clearInfPreview();
            }
            infState.selectedSlug = slug;
            renderInfTplGrid();
            var cards = infTplGrid.querySelectorAll('.adm-mp-car__tpl-card');
            cards.forEach(function (c) {
                c.classList.toggle('is-selected', c.getAttribute('data-slug') === slug);
            });
            var tmpl = infSelectedTemplate();
            if (infTplHint) infTplHint.textContent = tmpl ? (tmpl.description || '') : '';
            renderInfSlots();
            if (infGenBtn) infGenBtn.disabled = !!infState.busy || !slug;
            if (infPreviewBtn) infPreviewBtn.disabled = !!infState.busy || !slug;
            if (infSuggestBtn) infSuggestBtn.disabled = !!infState.busy || !slug;
        }

        function renderInfSlots() {
            var tmpl = infSelectedTemplate();
            if (!tmpl || !infSlotsEl || !infSlotsWrap) return;
            var values = infState.prefills[tmpl.slug] || {};
            infSlotsEl.innerHTML = '';
            (tmpl.slots || []).forEach(function (s) {
                if (s.kind === 'image' && s.image_sources && s.image_sources.length) {
                    // User-image slot — upload or pick from the per-user library.
                    var wrap = document.createElement('div');
                    wrap.className = 'adm-mp-car__slot-field adm-mp-inf__photo-field';
                    wrap.innerHTML =
                        '<div class="adm-mp-car__slot-head">' +
                            '<label class="adm-mp-car__slot-label">' + infEsc(humanizeInfSlotKey(s.key)) + (s.required ? ' *' : '') + '</label>' +
                        '</div>' +
                        '<div class="adm-mp-inf__photo-row">' +
                            '<img class="adm-mp-inf__photo-thumb" data-slot-key="' + infEsc(s.key) + '" alt="" hidden>' +
                            '<button type="button" class="adm-mp-gen__suggest" data-inf-upload="' + infEsc(s.key) + '">Upload image…</button>' +
                            '<button type="button" class="adm-mp-gen__suggest" data-inf-library="' + infEsc(s.key) + '">From library…</button>' +
                            '<input type="file" accept="image/jpeg,image/png,image/webp" data-inf-file="' + infEsc(s.key) + '" hidden>' +
                            '<span class="adm-mp-inf__photo-name" data-slot-key="' + infEsc(s.key) + '"></span>' +
                        '</div>';
                    infSlotsEl.appendChild(wrap);
                    return;
                }
                if (s.kind === 'percent' || s.kind === 'number') {
                    // Numeric slot — drives a live bar/gauge/chart in the layout.
                    var nval = values[s.key] || '';
                    var nid = 'adm-mp-inf-slot-' + s.key;
                    var nfield = document.createElement('div');
                    nfield.className = 'adm-mp-car__slot-field adm-mp-inf__num-field';
                    nfield.innerHTML =
                        '<div class="adm-mp-car__slot-head">' +
                            '<label class="adm-mp-car__slot-label" for="' + nid + '">' + infEsc(humanizeInfSlotKey(s.key)) + (s.required ? ' *' : '') + '</label>' +
                        '</div>' +
                        '<input type="number" id="' + nid + '" class="adm-mp-car__slot-input adm-mp-inf__num" data-slot-key="' + infEsc(s.key) + '"' +
                            (s.kind === 'percent' ? ' min="0" max="100"' : ' min="0"') + ' step="any" inputmode="decimal" value="' + infEsc(nval) + '">' +
                        (s.kind === 'percent' ? '<span class="adm-mp-inf__num-unit">%</span>' : '');
                    infSlotsEl.appendChild(nfield);
                    return;
                }
                if (s.kind === 'icon') {
                    // Icon slot — suggested by the draft round, swappable via a
                    // searchable picker fed from the templates endpoint.
                    var ival = values[s.key] || '';
                    var ifield = document.createElement('div');
                    ifield.className = 'adm-mp-car__slot-field adm-mp-inf__icon-field';
                    ifield.innerHTML =
                        '<div class="adm-mp-car__slot-head">' +
                            '<label class="adm-mp-car__slot-label">' + infEsc(humanizeInfSlotKey(s.key)) + (s.required ? ' *' : '') + '</label>' +
                        '</div>' +
                        '<div class="adm-mp-inf__icon-row">' +
                            '<span class="adm-mp-inf__icon-preview" data-slot-key="' + infEsc(s.key) + '">' + infIconSvg(ival) + '</span>' +
                            '<input type="hidden" class="adm-mp-car__slot-input" data-slot-key="' + infEsc(s.key) + '" value="' + infEsc(ival) + '">' +
                            '<button type="button" class="adm-mp-gen__suggest" data-inf-iconpick="' + infEsc(s.key) + '">Choose icon…</button>' +
                            '<span class="adm-mp-inf__icon-name" data-slot-key="' + infEsc(s.key) + '">' + infEsc(ival) + '</span>' +
                        '</div>' +
                        '<div class="adm-mp-inf__icon-strip" data-slot-key="' + infEsc(s.key) + '" hidden></div>';
                    infSlotsEl.appendChild(ifield);
                    return;
                }
                if (s.kind !== 'text') return;
                var val = values[s.key] || '';
                var max = s.max_chars > 0 ? s.max_chars : 0;
                var rows = (max > 80) ? 3 : 2;
                var inputId = 'adm-mp-inf-slot-' + s.key;
                var counter = max > 0
                    ? '<span class="adm-mp-car__slot-count" data-max="' + max + '">' + val.length + ' / ' + max + '</span>'
                    : '';
                var field = document.createElement('div');
                field.className = 'adm-mp-car__slot-field';
                field.innerHTML =
                    '<div class="adm-mp-car__slot-head">' +
                        '<label class="adm-mp-car__slot-label" for="' + inputId + '">' + infEsc(humanizeInfSlotKey(s.key)) + (s.required ? ' *' : '') + '</label>' +
                        counter +
                    '</div>' +
                    '<textarea id="' + inputId + '" class="adm-mp-car__slot-input" data-slot-key="' + infEsc(s.key) + '" rows="' + rows + '"' + (max > 0 ? ' maxlength="' + max + '"' : '') + '>' + infEsc(val) + '</textarea>';
                infSlotsEl.appendChild(field);
            });

            // Live counters.
            infSlotsEl.querySelectorAll('.adm-mp-car__slot-input').forEach(function (ta) {
                ta.addEventListener('input', function () {
                    var headEl = ta.parentElement && ta.parentElement.querySelector('.adm-mp-car__slot-count');
                    if (headEl) {
                        var max = parseInt(headEl.getAttribute('data-max'), 10) || 0;
                        headEl.textContent = ta.value.length + ' / ' + max;
                        headEl.classList.toggle('is-over', max > 0 && ta.value.length > max);
                    }
                });
            });

            // Photo upload + library wiring.
            infSlotsEl.querySelectorAll('[data-inf-upload]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    var key = btn.getAttribute('data-inf-upload');
                    var input = infSlotsEl.querySelector('[data-inf-file="' + key + '"]');
                    if (input) input.click();
                });
            });
            infSlotsEl.querySelectorAll('[data-inf-file]').forEach(function (input) {
                input.addEventListener('change', function () {
                    var key = input.getAttribute('data-inf-file');
                    if (input.files && input.files[0]) uploadInfPhoto(key, input.files[0]);
                });
            });
            // Reuses the canonical library sub-picker (search + upload + Load
            // more pagination) instead of the old inline strip, which only
            // ever fetched page 1 (18 items) and had no way to see the rest —
            // reported 2026-07-09.
            infSlotsEl.querySelectorAll('[data-inf-library]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    var key = btn.getAttribute('data-inf-library');
                    openImageSubPicker(function (entry) {
                        setInfPhoto(key, entry.url, entry.filename || '');
                    });
                });
            });

            // Icon picker wiring.
            infSlotsEl.querySelectorAll('[data-inf-iconpick]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    toggleInfIconStrip(btn.getAttribute('data-inf-iconpick'));
                });
            });

            // Restore photos already picked for this layout — re-renders
            // (suggest rounds, re-selects) must not drop the visible thumbs.
            Object.keys(infState.photos || {}).forEach(function (key) {
                if (infState.photos[key]) setInfPhoto(key, infState.photos[key], infState.photoNames[key] || '');
            });

            // The generate button names the layout it will render, so a wrong
            // selection is visible before it costs a task.
            if (infGenBtn) {
                infGenBtn.textContent = 'Generate ' + (tmpl.name || 'infographic') + ' — 1 task';
            }

            infSlotsWrap.hidden = false;
        }

        function setInfIcon(key, name) {
            var input = infSlotsEl.querySelector('input[type="hidden"].adm-mp-car__slot-input[data-slot-key="' + key + '"]');
            var preview = infSlotsEl.querySelector('.adm-mp-inf__icon-preview[data-slot-key="' + key + '"]');
            var label = infSlotsEl.querySelector('.adm-mp-inf__icon-name[data-slot-key="' + key + '"]');
            if (input) input.value = name;
            if (preview) preview.innerHTML = infIconSvg(name);
            if (label) label.textContent = name;
        }

        function toggleInfIconStrip(key) {
            var strip = infSlotsEl.querySelector('.adm-mp-inf__icon-strip[data-slot-key="' + key + '"]');
            if (!strip) return;
            if (!strip.hidden) { strip.hidden = true; return; }
            // One strip open at a time keeps the form scannable.
            infSlotsEl.querySelectorAll('.adm-mp-inf__icon-strip').forEach(function (s) { s.hidden = true; });
            strip.hidden = false;
            if (strip.getAttribute('data-built')) return;
            strip.setAttribute('data-built', '1');
            var search = document.createElement('input');
            search.type = 'search';
            search.className = 'adm-mp-inf__icon-search';
            search.placeholder = 'Search icons…';
            var grid = document.createElement('div');
            grid.className = 'adm-mp-inf__icon-grid';
            function renderGrid(filter) {
                grid.innerHTML = '';
                var q = String(filter || '').toLowerCase().trim();
                (infState.icons || []).forEach(function (ic) {
                    if (!ic || !ic.name) return;
                    if (q && ic.name.indexOf(q) === -1) return;
                    var b = document.createElement('button');
                    b.type = 'button';
                    b.className = 'adm-mp-inf__icon-item';
                    b.title = ic.name;
                    b.innerHTML = infIconSvg(ic.name, 24);
                    b.addEventListener('click', function () {
                        setInfIcon(key, ic.name);
                        strip.hidden = true;
                    });
                    grid.appendChild(b);
                });
                if (!grid.childNodes.length) {
                    grid.innerHTML = '<div class="adm-mp-car__tpl-empty">No icons match.</div>';
                }
            }
            search.addEventListener('input', function () { renderGrid(search.value); });
            strip.appendChild(search);
            strip.appendChild(grid);
            renderGrid('');
        }

        function humanizeInfSlotKey(key) {
            return String(key).replace(/_/g, ' ').replace(/^\w|\s\w/g, function (c) { return c.toUpperCase(); });
        }

        function setInfPhoto(key, url, label) {
            infState.photos[key] = url;
            infState.photoNames[key] = label || '';
            var thumb = infSlotsEl.querySelector('.adm-mp-inf__photo-thumb[data-slot-key="' + key + '"]');
            var name  = infSlotsEl.querySelector('.adm-mp-inf__photo-name[data-slot-key="' + key + '"]');
            if (thumb) { thumb.src = url; thumb.hidden = !url; }
            if (name) name.textContent = label || '';
        }

        function uploadInfPhoto(key, file) {
            var ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
            if (ALLOWED.indexOf(file.type) === -1) {
                setInfStatus('Only JPEG, PNG, or WebP images for infographics.', true);
                return;
            }
            setInfStatus('Uploading ' + file.name + '…', false);
            var fd = new FormData();
            fd.append('file', file);
            var xhr = new XMLHttpRequest();
            xhr.open('POST', cfg.restUrl + 'upload');
            admSetRestHeaders(xhr);
            xhr.onload = function () {
                var data = null;
                try { data = JSON.parse(xhr.responseText); } catch (e) { /* fall-through */ }
                if (xhr.status >= 200 && xhr.status < 300 && data && data.url) {
                    setInfPhoto(key, data.url, file.name);
                    setInfStatus(null, false);
                    libraryLoaded = false; // the library tab should see the new upload
                } else {
                    setInfStatus((data && (data.message || data.error)) || ('Upload failed (HTTP ' + xhr.status + ').'), true);
                }
            };
            xhr.onerror = function () { setInfStatus('Network error uploading the image.', true); };
            xhr.send(fd);
        }

        // Shared draft-extraction call — both "Suggest a template" and
        // "Suggest texts" read the same fact sheet from the draft; they only
        // differ in what they do with the result (onSuccess below).
        function runInfDraft(onSuccess) {
            if (infState.busy) return;
            var text = '';
            try { text = String((editor.getText && editor.getText()) || '').trim(); } catch (e) { text = ''; }
            if (text.length < 40) {
                setInfStatus('Write a bit more first — the draft is too short to extract facts from.', true);
                return;
            }
            setInfStatus(null, false);
            setInfBusy(true);
            showInfPanel('suggest');
            var lang = (document.documentElement.lang || 'en').slice(0, 2) || 'en';
            fetch(voiceRestUrl + 'graphics/infographic/draft', {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ post_text: text.slice(0, 12000), language: lang }),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                setInfBusy(false);
                showInfPanel('form');
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok || !data) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    setInfStatus('Suggest failed: ' + msg, true);
                    return;
                }
                infState.drafted = true;
                infState.prefills = {};
                infState.offered = {};
                infState.tplPage = 0;
                var firstOffered = '';
                (data.templates || []).forEach(function (t) {
                    infState.offered[t.slug] = !!t.offered;
                    if (t.slot_values && typeof t.slot_values === 'object') {
                        infState.prefills[t.slug] = t.slot_values;
                    }
                    if (t.offered && !firstOffered) firstOffered = t.slug;
                });
                onSuccess(firstOffered);
            }).catch(function (err) {
                setInfBusy(false);
                showInfPanel('form');
                setInfStatus('Network error: ' + (err && err.message), true);
            });
        }

        // "Suggest a template" (above the grid) — picks AND selects the
        // best-fit layout for the draft. The only action that changes the
        // selected layout on the user's behalf.
        function suggestInfTemplate() {
            runInfDraft(function (firstOffered) {
                renderInfTplGrid();
                if (firstOffered) {
                    selectInfTemplate(firstOffered);
                    var tmpl = infSelectedTemplate();
                    setInfStatus('Suggested "' + (tmpl ? tmpl.name : 'a layout') + '" — review the fields, then generate.', false);
                } else {
                    setInfStatus('No layout fits this draft automatically — pick one yourself, then use Suggest texts to fill it in.', false);
                }
            });
        }

        // "Suggest texts" (below the grid) — fills every field of whichever
        // layout is ALREADY selected. Never changes the selection itself
        // (that used to be bundled together, which silently swapped the
        // user's chosen layout — reported 2026-07-08).
        function suggestInfTexts() {
            if (!infState.selectedSlug) {
                setInfStatus('Pick a layout first — or use "Suggest a template" above.', true);
                return;
            }
            runInfDraft(function () {
                renderInfTplGrid();
                renderInfSlots();
                setInfStatus('Filled in from your draft — review the text, add a photo where asked, then generate.', false);
            });
        }

        function readInfSlotValues() {
            var values = {};
            infSlotsEl.querySelectorAll('.adm-mp-car__slot-input').forEach(function (ta) {
                var key = ta.getAttribute('data-slot-key');
                if (key) values[key] = ta.value;
            });
            Object.keys(infState.photos || {}).forEach(function (key) {
                values[key] = infState.photos[key];
            });
            return values;
        }

        function renderInfReport(report) {
            if (!infReportEl) return;
            var label = function (keys) {
                return keys.map(humanizeInfSlotKey).join(', ');
            };
            var notes = [];
            if (report && report.clipped && report.clipped.length) {
                notes.push('Trimmed to fit: ' + label(report.clipped));
            }
            if (report && report.shortened && report.shortened.length) {
                notes.push('Shrunk to fit: ' + label(report.shortened));
            }
            if (report && report.hidden && report.hidden.length) {
                notes.push('Left out, nothing to show: ' + label(report.hidden));
            }
            if (!notes.length) {
                infReportEl.innerHTML = '<p class="adm-mp-inf__report-ok">Everything fits.</p>';
                infReportEl.hidden = false;
                return;
            }
            var html = '<ul class="adm-mp-inf__report-list">';
            notes.forEach(function (n) { html += '<li>' + infEsc(n) + '</li>'; });
            html += '</ul>';
            infReportEl.innerHTML = html;
            infReportEl.hidden = false;
        }

        function hideInfReport() {
            // A report describes ONE specific rendered preview. Leaving it up
            // once that preview is no longer trustworthy — a new request just
            // started, or the last one failed — would keep asserting a fit
            // outcome ("Everything fits") that nothing on screen backs up
            // anymore.
            if (infReportEl) { infReportEl.hidden = true; infReportEl.innerHTML = ''; }
        }

        function clearInfPreview() {
            // A layout switch retires the whole preview, image included — it
            // was rendered from a different template's slots and has nothing
            // to say about the one now selected.
            var img = infPane && infPane.querySelector('[data-inf-preview-img]');
            if (img && img.parentNode) img.parentNode.removeChild(img);
            hideInfReport();
        }

        function previewInfographic() {
            if (infState.busy || !infState.selectedSlug) return;
            if (!infSelectedTemplate()) return;
            var values = readInfSlotValues();
            // Cleared up front, before the request even starts: the report
            // must not sit under "Rendering a preview…" implying it still
            // applies, and since nothing between here and either failure
            // branch below re-populates it, this single call also keeps a
            // failed refresh from leaving the previous report on screen. The
            // image is left alone — it's still a valid preview of the
            // template until a new one replaces it, and only the report
            // makes a claim that can go stale.
            hideInfReport();
            setInfStatus('Rendering a preview…', false);
            setInfBusy(true);
            fetch(voiceRestUrl + 'graphics/infographic/preview', {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    template_slug: infState.selectedSlug,
                    slot_values: values,
                    aspect: '',
                }),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                setInfBusy(false);
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok || !data || !data.preview_url) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    setInfStatus('Preview failed: ' + msg, true);
                    return;
                }
                var img = infPane.querySelector('[data-inf-preview-img]');
                if (!img) {
                    img = document.createElement('img');
                    img.setAttribute('data-inf-preview-img', '1');
                    img.className = 'adm-mp-inf__preview-img';
                    infReportEl.parentNode.insertBefore(img, infReportEl);
                }
                // Each preview gets a fresh filename, so no cache-buster is
                // needed — but the element is reused, so set src last.
                img.width = data.width;
                img.height = data.height;
                img.src = data.preview_url;
                renderInfReport(data.report);
                setInfStatus(null, false);
            }).catch(function (err) {
                setInfBusy(false);
                setInfStatus('Network error rendering the preview: ' + (err && err.message), true);
            });
        }

        function composeInfographic() {
            if (infState.busy || !infState.selectedSlug) return;
            var tmpl = infSelectedTemplate();
            if (!tmpl) return;
            var values = readInfSlotValues();
            // Client-side required check so a missing photo doesn't round-trip.
            var missing = (tmpl.slots || []).filter(function (s) {
                return s.required && !(String(values[s.key] || '').trim());
            })[0];
            if (missing) {
                setInfStatus(humanizeInfSlotKey(missing.key) + ' is required.', true);
                return;
            }
            setInfStatus(null, false);
            setInfBusy(true);
            showInfPanel('progress');
            fetch(voiceRestUrl + 'graphics/infographic/compose', {
                method: 'POST',
                credentials: 'same-origin',
                headers: admRestHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    template_slug: infState.selectedSlug,
                    slot_values: values,
                    alt: (altInput && altInput.value.trim()) || (values.title || ''),
                }),
            }).then(function (r) {
                return r.text().then(function (raw) { return { ok: r.ok, status: r.status, raw: raw }; });
            }).then(function (resp) {
                setInfBusy(false);
                var data = null;
                try { data = JSON.parse(resp.raw); } catch (e) { data = null; }
                if (!resp.ok || !data || !data.renditions || !data.renditions.length) {
                    var msg = (data && (data.message || data.code)) || ('HTTP ' + resp.status);
                    showInfPanel('form');
                    setInfStatus('Generate failed: ' + msg, true);
                    return;
                }
                renderInfResult(data.renditions, values);
            }).catch(function (err) {
                setInfBusy(false);
                showInfPanel('form');
                setInfStatus('Network error generating: ' + (err && err.message), true);
            });
        }

        function renderInfResult(renditions, values) {
            if (!infResultGrid) return;
            infResultGrid.innerHTML = '';
            var altText = (altInput && altInput.value.trim()) || (values.title || 'Infographic');
            renditions.forEach(function (r, i) {
                var card = document.createElement('button');
                card.type = 'button';
                card.className = 'adm-mp-inf__rendition';
                card.innerHTML =
                    '<img src="' + infEsc(r.url) + '" alt="" loading="lazy">' +
                    '<span class="adm-mp-inf__rendition-label">' + infEsc(r.aspect) + '</span>';
                card.addEventListener('click', function () {
                    infResultGrid.querySelectorAll('.adm-mp-inf__rendition').forEach(function (c) {
                        c.classList.toggle('is-selected', c === card);
                    });
                    selectAttachment({ id: r.attachment_id, url: r.url, alt: altText, filename: '' });
                });
                infResultGrid.appendChild(card);
                if (i === 0) card.click(); // sensible default: first (default) aspect
            });
            showInfPanel('result');
        }

        if (infSuggestTplBtn) infSuggestTplBtn.addEventListener('click', suggestInfTemplate);
        if (infSuggestBtn) infSuggestBtn.addEventListener('click', suggestInfTexts);
        if (infGenBtn) infGenBtn.addEventListener('click', composeInfographic);
        if (infPreviewBtn) infPreviewBtn.addEventListener('click', previewInfographic);
        if (infRedoBtn) infRedoBtn.addEventListener('click', function () {
            selected = null;
            refreshInsertBtn();
            showInfPanel('form');
        });

        tabs.forEach(function (tab) {
            tab.addEventListener('click', function () {
                if (tab.getAttribute('data-tab') === 'infographic') {
                    loadInfTemplates();
                }
            });
        });
    }


    // Focus the close button initially for a11y / Esc-friendliness.
    setTimeout(() => overlay.querySelector('.adm-media-picker__close').focus(), 0);
}

/**
 * Cover-image-variety Task 7 — standalone entry point, exposed as
 * window.AdamiroEditor.openImagePicker(opts). No editor/mount() call is
 * required first; this is how the media library's "Generate an image"
 * toolbar button and empty-state CTA open the (generation-only) picker.
 *
 * @param {object} [opts] mountOpts minus `standalone`, which is forced true.
 * @param {number} [opts.campaignId] Campaign-cover Task 3.1 — a campaign
 *     picker call (no postId) with this set gets a "Draft a prompt from the
 *     campaign" suggest button, grounded on the campaign's own title/topics
 *     server-side instead of an editor's text.
 */
function openImagePickerStandalone(opts) {
    openImagePicker(null, Object.assign({ standalone: true }, opts || {}));
}

/**
 * Open the link prompt and apply / remove the link mark. URL allowlist:
 * http, https, mailto. Empty string removes the link.
 */
async function promptLink(editor) {
    const previous = editor.getAttributes('link').href || '';
    const url = await admPrompt({
        title: 'Insert link',
        label: 'URL',
        placeholder: 'https://example.com',
        value: previous,
        validate: (v) => /^(https?:|mailto:)/i.test(v.trim()),
        validateError: 'Link must start with http://, https://, or mailto:',
    });
    if (url === null) return; // cancelled
    if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
}

/**
 * Mount a TipTap editor on the given container element.
 *
 * @param {HTMLElement} el          container that will host the editor
 * @param {object}      opts
 * @param {string}      opts.context      'voice' | 'reach' (informational; full constraints land 1c.2)
 * @param {string}      [opts.platform]   'linkedin' | 'substack' | 'instagram' | null
 * @param {string}      [opts.initialHtml] starting HTML
 * @param {string}      [opts.placeholder] placeholder text shown when empty
 * @param {number}      [opts.maxLength]  soft character cap shown in counter; null = unbounded
 * @param {function}    [opts.onChange]   (html, plainText) called on every edit (debounced caller-side)
 *
 * @returns {object} api — { destroy, getHtml, setHtml, getText, getCharCount, focus, editor }
 */
function mount(el, opts) {
    opts = opts || {};
    if (!el) throw new Error('AdamiroEditor.mount: container element required');

    // Build the shell — toolbar + content + footer (counter).
    el.classList.add('adm-editor');
    el.innerHTML = '';

    const toolbarHost = document.createElement('div');
    el.appendChild(toolbarHost);

    const contentHost = document.createElement('div');
    contentHost.className = 'adm-editor__content';
    el.appendChild(contentHost);

    const footer = document.createElement('div');
    footer.className = 'adm-editor__footer';
    el.appendChild(footer);

    const counter = document.createElement('span');
    counter.className = 'adm-editor__counter';
    footer.appendChild(counter);

    const extensions = [
        StarterKit.configure({
            // h1 reserved for post title row per spec.
            heading: { levels: [2, 3] },
        }),
        DirAttribute,
        Link.configure({
            openOnClick: false,
            HTMLAttributes: {
                rel: 'noopener noreferrer',
                target: '_blank',
            },
            // Validate hrefs on paste — strip anything that isn't http(s) or mailto.
            validate: (href) => /^(https?:|mailto:)/i.test(href || ''),
        }),
        Image.configure({
            // Inline=false because we want images on their own line (block
            // node), matching the LinkedIn / Substack rendering models.
            inline: false,
            allowBase64: false, // never inline data URIs — must go through media library
            HTMLAttributes: {
                class: 'adm-editor__img',
            },
        }),
        ReachSubscribe,
        ReachPaywall,
        Placeholder.configure({
            placeholder: opts.placeholder || 'Write your post…',
        }),
        CharacterCount.configure({
            // Character count is the default. Word count is also exposed via storage.
            limit: null, // soft limit handled by UI, not enforced by editor
        }),
    ];

    const editor = new Editor({
        element: contentHost,
        extensions,
        content: plainTextToHtml(opts.initialHtml || ''),
        autofocus: false,
        editable: true,
        editorProps: {
            attributes: {
                class: 'adm-editor__prose',
                spellcheck: 'true',
            },
            // Paste cleanup — runs before TipTap's schema parse. Strips
            // styling artefacts most clipboard sources insert (Word, Google
            // Docs, Notion, browsers' execCommand-bold style spans). The
            // schema rejects unsupported nodes regardless; this just keeps
            // the inserted content from looking ridiculous in the editor
            // until the schema does its filtering.
            transformPastedHTML: (html) => sanitisePastedHtml(html),
        },
    });

    const toolbar = buildToolbar(editor, opts);
    toolbarHost.appendChild(toolbar.el);

    // Counter strategy: when the editor is bound to a platform that has
    // a registered serializer (Reach LinkedIn / Substack / etc.), count
    // characters on the SERIALIZED output — that's what the platform
    // actually receives, and it's the count creators care about. Voice
    // context (and platforms without a serializer) fall back to the
    // built-in CharacterCount, which counts visible HTML text.
    const platform = (opts.platform && SERIALIZERS[opts.platform]) ? opts.platform : null;
    const manualMax = (opts.maxLength == null) ? null : Number(opts.maxLength);
    let lastSerialized = null; // cached so preview()/save() don't re-serialize

    function computeCounter() {
        if (platform) {
            const result = serializeFor(platform, editor.getHTML());
            lastSerialized = result;
            const chars = result.char_count;
            const cap = result.char_cap || manualMax || 0;
            const words = (result.text.match(/\S+/g) || []).length;
            return { chars, cap, words, result };
        }
        lastSerialized = null;
        const chars = editor.storage.characterCount.characters();
        const words = editor.storage.characterCount.words();
        return { chars, cap: manualMax || 0, words, result: null };
    }

    function updateCounter() {
        const c = computeCounter();
        if (c.cap > 0) {
            counter.textContent = `${c.chars} / ${c.cap} chars · ${c.words} words`;
            counter.classList.toggle('is-warning', c.chars > c.cap * 0.9 && c.chars <= c.cap);
            counter.classList.toggle('is-over', c.chars > c.cap);
        } else {
            counter.textContent = `${c.chars} chars · ${c.words} words`;
            counter.classList.remove('is-warning', 'is-over');
        }
    }

    function onUpdate() {
        toolbar.refresh();
        updateCounter();
        if (typeof opts.onChange === 'function') {
            opts.onChange(editor.getHTML(), editor.getText());
        }
    }

    editor.on('selectionUpdate', toolbar.refresh);
    editor.on('update', onUpdate);
    updateCounter();
    toolbar.refresh();

    return {
        destroy: () => editor.destroy(),
        getHtml: () => editor.getHTML(),
        setHtml: (html) => editor.commands.setContent(plainTextToHtml(html || ''), false),
        getText: () => editor.getText(),
        getCharCount: () => editor.storage.characterCount.characters(),
        getWordCount: () => editor.storage.characterCount.words(),
        focus: () => editor.commands.focus(),
        /**
         * Serialize the current body for a given platform. Defaults to the
         * editor's mount-time platform. Returns { text, char_count,
         * char_cap, warnings, errors } — same shape as the JS serializer.
         */
        preview: (forPlatform) => serializeFor(forPlatform || platform || '', editor.getHTML()),
        /**
         * Last cached serialized output from the counter pass. Saves
         * a re-serialize when callers just need the most-recent result.
         */
        getLastSerialized: () => lastSerialized,
        editor, // escape hatch for advanced callers
        /**
         * Open the cover-image picker attached to this mounted editor —
         * full Templates/Carousel/Infographic/Upload/Library tab set,
         * inserting into this doc on Insert. Reuses this mount's own opts
         * (context/platform/postId/voiceDraftId) exactly like the toolbar's
         * image button above, so a no-argument call from the cover strip
         * behaves identically to clicking that toolbar button. See
         * openImagePickerStandalone for the no-editor library entry point.
         */
        openImagePicker: () => openImagePicker(editor, opts),
    };
}

function destroy(api) {
    if (api && typeof api.destroy === 'function') {
        api.destroy();
    }
}

// IIFE export — esbuild sets window.AdamiroEditor to this module's exports
// when format=iife + globalName. openImagePickerStandalone is exported as
// `openImagePicker` — window.AdamiroEditor.openImagePicker(opts) — the
// no-editor entry the media library's "Generate an image" affordances call.
export { mount, destroy, openImagePickerStandalone as openImagePicker };
